import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './App.css';
import Settings from './Settings';
import { useAuth } from './AuthProvider';
import { API_BASE } from './apiBase';

const AssetMeshLogo = ({ size = 40, className = '' }) => (
  <svg width={size} height={size * 1.12} viewBox="0 0 200 224" className={className} xmlns="http://www.w3.org/2000/svg" fill="none">
    <path d="M100 8 L186 44 V128 C186 176 150 204 100 216 C50 204 14 176 14 128 V44 Z" fill="#152d4c"/>
    <path d="M100 180 C76 144 42 100 26 44 C36 68 52 92 74 108 C58 90 46 66 42 40 C52 68 70 100 100 148 Z" fill="#43c6cf"/>
    <path d="M100 180 C124 144 158 100 174 44 C164 68 148 92 126 108 C142 90 154 66 158 40 C148 68 130 100 100 148 Z" fill="#43c6cf"/>
    <path d="M74 108 C56 132 38 158 26 178 C36 162 52 136 68 114 Z" fill="#1b5a9e"/>
    <path d="M126 108 C144 132 162 158 174 178 C164 162 148 136 132 114 Z" fill="#1b5a9e"/>
    <circle cx="100" cy="52" r="18" fill="white"/>
  </svg>
);

function App() {
  const { user, logout, authFetch } = useAuth();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true); // Only true on initial load
  const hasLoadedOnce = useRef(false);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'global_health_status', direction: 'asc' });
  const [, setLastSync] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  // Integration action modal state
  const [integrationModal, setIntegrationModal] = useState(null); // { device, source, deleteOptions }
  // Bulk Microsoft action modal state
  const [bulkMicrosoftModal, setBulkMicrosoftModal] = useState(null); // { devices, action: 'archive'|'delete', reason, ticket, createTicket }
  // Bulk delete modal state (for ImmyBot, CloudRadial, N-Central bundled delete)
  const [bulkDeleteModal, setBulkDeleteModal] = useState(null); // { devices, blockCloudRadial, ticket, createTicket }
  // Changelog modal state
  const [showChangelogModal, setShowChangelogModal] = useState(false);
  const APP_VERSION = '7.1.0';
  const [syncingSource, setSyncingSource] = useState(null);
  const [showSyncMenu, setShowSyncMenu] = useState(false);
  // showSettings removed — v6.0 uses currentView === 'settings' via navigateTo()
  const [, setSyncInterval] = useState(4);
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');
  const [branding, setBranding] = useState({ app_name: '', logo_dark: '', logo_light: '', favicon: '', primary_color: '', accent_color: '' });
  const [deviceHistory, setDeviceHistory] = useState([]);
  const [, setTimezone] = useState('UTC');
  const [currentView, setCurrentView] = useState('fleet'); // 'fleet' | 'integration' | 'org' | 'settings'
  const [drillDownContext, setDrillDownContext] = useState(null); // { type: 'integration'|'org', name: string, filter: string }
  // showLandingDashboard removed — v6.0 always shows fleet command landing
  const [settingsInitialTab, setSettingsInitialTab] = useState(null); // Tab to open when navigating to settings
  const [showOverflowMenu, setShowOverflowMenu] = useState(false); // Overflow menu (Settings, Export, etc.)
  const [drillDownTab, setDrillDownTab] = useState('issues'); // 'issues' | 'stale' | 'all' | 'offboarded' | 'archived' | 'duplicates'
  const [selectedSources] = useState(() => {
    const saved = localStorage.getItem('selectedSources');
    return saved ? JSON.parse(saved) : ['N-Central', 'ConnectWise', 'Huntress', 'ImmyBot', 'CloudRadial', 'Datto RMM', 'Microsoft'];
  });
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [allOrganizations, setAllOrganizations] = useState([]);
  const [orgMappings, setOrgMappings] = useState([]); // Store full mapping data
  const [orgSearchTerm, setOrgSearchTerm] = useState('');
  const [sourceSyncTimes, setSourceSyncTimes] = useState({
    'N-Central': null,
    'ConnectWise': null,
    'Huntress': null,
    'ImmyBot': null,
    'CloudRadial': null,
    'Datto RMM': null,
    'Microsoft': null
  });
  const [syncStatus, setSyncStatus] = useState({
    'full': 'idle',
    'N-Central': 'idle',
    'ConnectWise': 'idle',
    'Huntress': 'idle',
    'ImmyBot': 'idle',
    'CloudRadial': 'idle',
    'Datto RMM': 'idle',
    'Microsoft': 'idle'
  });
  const [selectedDevices, setSelectedDevices] = useState(new Set());
  // checkingDuplicates state removed — managed in Settings.js
  const [duplicateGroupData, setDuplicateGroupData] = useState(null);
  const [duplicateGroupLoading, setDuplicateGroupLoading] = useState(false);
  const [selectedDuplicateIds, setSelectedDuplicateIds] = useState(new Set());
  const [taskQueue, setTaskQueue] = useState({ in_progress: [], completed: [] });
  const [showTaskDrawer, setShowTaskDrawer] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [, setVerificationStatus] = useState({ active: 0, scheduled: 0, completed: 0, devices: [] });
  const [scheduledTasks, setScheduledTasks] = useState({ jobs: [], last_sync: null, sync_interval_hours: 4, pending_verifications: 0 });
  const prevCompletedIdsRef = useRef(new Set());
  const [thresholds, setThresholds] = useState({
    stale_threshold_days: 30,
    sync_drift_threshold_days: 3,
    warning_threshold_days: 7
  });
  const [orgSettings, setOrgSettings] = useState({});
  const [, setCredentialsStatus] = useState({ cloudradial: false, immybot: false, ncentral: false });

  // ImmyBot real-time online status from ephemeral agent API
  const [immybotOnlineStatus, setImmybotOnlineStatus] = useState({});
  const [immybotStatusLoading, setImmybotStatusLoading] = useState(false);
  const immybotOnlineStatusRef = useRef(null); // debounce timer

  // PSA ticketing config
  const [ticketingConfig, setTicketingConfig] = useState({ enabled: false, psaName: null });

  // Quick org settings modal (from device row issues badge)
  const [quickOrgSettingsModal, setQuickOrgSettingsModal] = useState(null); // { orgName, settings }
  const [quickOrgSettingsForm, setQuickOrgSettingsForm] = useState({
    is_hidden: false,
    ncentral_enabled: true,
    connectwise_enabled: true,
    huntress_enabled: true,
    immybot_enabled: true,
    cloudradial_enabled: true,
    datto_rmm_enabled: true,
    microsoft_enabled: true,
    primary_rmm: 'global',
    // MacOS settings
    mac_health_checks_enabled: false,
    mac_ncentral_required: false,
    mac_datto_rmm_required: false,
    mac_cloudradial_required: false,
    mac_huntress_required: false,
    mac_connectwise_required: false,
    mac_microsoft_required: false
  });

  // Confirmation drawer (replaces window.confirm)
  const [confirmDrawer, setConfirmDrawer] = useState(null); // { title, message, confirmLabel, onConfirm, variant: 'danger'|'deploy'|'info', showTicketOption }
  const confirmDrawerResolveRef = useRef(null);
  // Command palette
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [commandQuery, setCommandQuery] = useState('');
  const [commandSelectedIdx, setCommandSelectedIdx] = useState(0);
  const commandInputRef = useRef(null);
  const deviceTableRef = useRef(null);
  // Keyboard shortcut help
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);
  // Page transition direction
  const [transitionDir, setTransitionDir] = useState('none'); // 'forward' | 'back' | 'none'

  // Deployment approval modal state
  const [deploymentApprovalModal, setDeploymentApprovalModal] = useState(null);
  // Structure: { status, software_name, org_name, device, deployment_id, deployment_name, immy_url, deployments (for selection), software_url (for manual), pendingDeploys }

  // Pending deployments tracking - shows "Deploying..." in UI
  // Structure: { "hostname-serial": { "Huntress": timestamp, "Datto RMM": timestamp } }
  const [pendingDeployments, setPendingDeployments] = useState(() => {
    try {
      const saved = localStorage.getItem('pendingDeployments');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Clean up deployments older than 30 minutes
        const thirtyMinutesAgo = Date.now() - 30 * 60 * 1000;
        const cleaned = {};
        for (const [key, deploys] of Object.entries(parsed)) {
          const validDeploys = {};
          for (const [software, timestamp] of Object.entries(deploys)) {
            if (timestamp > thirtyMinutesAgo) {
              validDeploys[software] = timestamp;
            }
          }
          if (Object.keys(validDeploys).length > 0) {
            cleaned[key] = validDeploys;
          }
        }
        return cleaned;
      }
    } catch (e) {}
    return {};
  });

  // Save pending deployments to localStorage when they change
  useEffect(() => {
    localStorage.setItem('pendingDeployments', JSON.stringify(pendingDeployments));
  }, [pendingDeployments]);

  const API_URL = API_BASE;

  // Authenticated API fetch wrapper
  const apiFetch = useCallback(async (url, options = {}) => {
    return authFetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
  }, [authFetch]);

  // Fetch task queue status
  const fetchTaskQueue = async () => {
    try {
      const response = await apiFetch(`${API_URL}/task-queue`);
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success') {
          setTaskQueue({
            in_progress: data.in_progress || [],
            completed: data.completed || []
          });
        }
      }
    } catch (error) {
      console.error('Error fetching task queue:', error);
    }
  };

  // Fetch scheduled verification status for the task drawer (independent of Settings page)
  // Fetch scheduled tasks info for the task drawer (sync schedule, verifications, etc.)
  const fetchScheduledTasks = async () => {
    try {
      const response = await apiFetch(`${API_URL}/scheduled-tasks`);
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success') {
          setScheduledTasks(data);
          setVerificationStatus(prev => ({
            ...prev,
            scheduled: data.pending_verifications || 0,
          }));
        }
      }
    } catch (error) {
      // Silently fail
    }
  };

  // Detect newly completed tasks and show toast notifications
  useEffect(() => {
    const currentIds = new Set(taskQueue.completed.map(t => t.id));
    const prevIds = prevCompletedIdsRef.current;

    // On first load, just seed the ref without toasting
    if (prevIds.size === 0 && currentIds.size > 0) {
      prevCompletedIdsRef.current = currentIds;
      return;
    }

    taskQueue.completed.forEach(task => {
      if (!prevIds.has(task.id)) {
        const toastId = `${task.id}-${Date.now()}`;
        setToasts(prev => [...prev, { ...task, toastId, exiting: false }]);
        // Auto-dismiss after 5 seconds
        setTimeout(() => {
          setToasts(prev => prev.map(t => t.toastId === toastId ? { ...t, exiting: true } : t));
          setTimeout(() => {
            setToasts(prev => prev.filter(t => t.toastId !== toastId));
          }, 300); // match exit animation duration
        }, 5000);
      }
    });

    prevCompletedIdsRef.current = currentIds;
  }, [taskQueue.completed]);

  // Dismiss a toast immediately on click
  const dismissToast = useCallback((toastId) => {
    setToasts(prev => prev.map(t => t.toastId === toastId ? { ...t, exiting: true } : t));
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.toastId !== toastId));
    }, 300);
  }, []);

  // Show a toast notification from anywhere in the app
  // type: 'success' | 'error' | 'info' | 'warning' | 'progress'
  // Returns toast ID for updating later
  const showToast = useCallback((message, { type = 'info', subtitle = '', duration, progressCurrent, progressTotal } = {}) => {
    const toastId = `manual-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const statusMap = { success: 'completed', error: 'failed', info: 'info', warning: 'warning', progress: 'progress' };
    // Error toasts are sticky by default (no auto-dismiss), progress toasts never auto-dismiss
    const effectiveDuration = duration !== undefined ? duration : (type === 'error' || type === 'progress') ? 0 : 5000;
    setToasts(prev => {
      const next = [...prev, {
        toastId,
        description: message,
        status: statusMap[type] || 'info',
        hostname: subtitle || null,
        exiting: false,
        isProgress: type === 'progress',
        progressCurrent: progressCurrent ?? null,
        progressTotal: progressTotal ?? null,
      }];
      // Limit visible toasts to 5, remove oldest non-progress ones
      if (next.length > 5) {
        const toRemove = next.find(t => !t.isProgress && !t.exiting);
        if (toRemove) return next.filter(t => t !== toRemove);
      }
      return next;
    });
    if (effectiveDuration > 0) {
      setTimeout(() => {
        setToasts(prev => prev.map(t => t.toastId === toastId ? { ...t, exiting: true } : t));
        setTimeout(() => {
          setToasts(prev => prev.filter(t => t.toastId !== toastId));
        }, 300);
      }, effectiveDuration);
    }
    return toastId;
  }, []);

  // Update an existing toast (e.g., progress → completed) — public API for operations
  const updateToast = useCallback((toastId, { message, type, subtitle, duration = 4000, progressCurrent, progressTotal } = {}) => { // eslint-disable-line no-unused-vars
    const statusMap = { success: 'completed', error: 'failed', info: 'info', warning: 'warning', progress: 'progress' };
    setToasts(prev => prev.map(t => {
      if (t.toastId !== toastId) return t;
      return {
        ...t,
        description: message || t.description,
        status: type ? (statusMap[type] || t.status) : t.status,
        hostname: subtitle !== undefined ? subtitle : t.hostname,
        isProgress: type === 'progress',
        progressCurrent: progressCurrent !== undefined ? progressCurrent : t.progressCurrent,
        progressTotal: progressTotal !== undefined ? progressTotal : t.progressTotal,
      };
    }));
    // Auto-dismiss after update if not progress/error
    if (type && type !== 'progress' && type !== 'error' && duration > 0) {
      setTimeout(() => {
        setToasts(prev => prev.map(t => t.toastId === toastId ? { ...t, exiting: true } : t));
        setTimeout(() => {
          setToasts(prev => prev.filter(t => t.toastId !== toastId));
        }, 300);
      }, duration);
    }
  }, []);

  // Show confirmation drawer (replaces window.confirm)
  // Returns a Promise<boolean> — true if confirmed, false if cancelled
  const showConfirmation = useCallback(({ title, message, confirmLabel = 'Confirm', variant = 'danger', showTicketOption = false }) => {
    return new Promise((resolve) => {
      confirmDrawerResolveRef.current = resolve;
      setConfirmDrawer({ title, message, confirmLabel, variant, showTicketOption, createTicket: false });
    });
  }, []);

  const handleConfirmDrawerConfirm = useCallback(() => {
    if (confirmDrawerResolveRef.current) {
      confirmDrawerResolveRef.current(true);
      confirmDrawerResolveRef.current = null;
    }
    setConfirmDrawer(null);
  }, []);

  const handleConfirmDrawerCancel = useCallback(() => {
    if (confirmDrawerResolveRef.current) {
      confirmDrawerResolveRef.current(false);
      confirmDrawerResolveRef.current = null;
    }
    setConfirmDrawer(null);
  }, []);

  // Fetch credentials status to check which integrations are enabled
  const fetchCredentialsStatus = async () => {
    try {
      const response = await apiFetch(`${API_URL}/credentials/status`);
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success' && data.credentials) {
          setCredentialsStatus(data.credentials);
        }
      }
    } catch (error) {
      console.error('Error fetching credentials status:', error);
    }
  };

  // Fetch general settings (thresholds)
  const fetchGeneralSettings = async () => {
    try {
      const response = await apiFetch(`${API_URL}/settings/general`);
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success' && data.settings) {
          setThresholds({
            stale_threshold_days: data.settings.stale_threshold_days || 30,
            sync_drift_threshold_days: data.settings.sync_drift_threshold_days || 3,
            warning_threshold_days: data.settings.warning_threshold_days || 7
          });
        }
      }
    } catch (error) {
      console.error('Error fetching general settings:', error);
    }
  };

  // Fetch organization settings (for health checks and visibility)
  const fetchOrgSettings = async () => {
    try {
      const response = await apiFetch(`${API_URL}/organizations/settings`);
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success' && data.settings) {
          setOrgSettings(data.settings);
        }
      }
    } catch (error) {
      console.error('Error fetching org settings:', error);
    }
  };

  const fetchDevices = async () => {
    // Only show loading spinner on initial load, not background refreshes
    if (!hasLoadedOnce.current) setLoading(true);
    try {
      const response = await apiFetch(`${API_URL}/devices`);
      if (!response.ok) throw new Error("Failed");
      const data = await response.json();
      setDevices(data);

      // Note: Source sync times are now fetched from sync-status API (completedTimes)
      // which tracks actual sync completion rather than device check-in times

      // Fetch last sync timestamp
      const syncResponse = await apiFetch(`${API_URL}/last-sync`);
      if (syncResponse.ok) {
        const syncData = await syncResponse.json();
        setLastSync(syncData.last_sync);
      }
    } catch (error) { console.error(error); }
    hasLoadedOnce.current = true;
    setLoading(false);
  };

  const fetchOrganizations = async () => {
    try {
      const response = await apiFetch(`${API_URL}/organizations/mappings`);
      if (response.ok) {
        const data = await response.json();
        setOrgMappings(data); // Store full mapping data
        const orgs = [...new Set(data.map(o => o.master_org_name))].sort();
        setAllOrganizations(orgs);
      }
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    }
  };

  const fetchSyncStatus = async () => {
    try {
      const response = await apiFetch(`${API_URL}/sync-status`);
      if (response.ok) {
        const data = await response.json();

        // Detect if any source just finished syncing (was syncing, now idle/error)
        const justFinished = [];
        ['full', 'N-Central', 'ConnectWise', 'Huntress', 'ImmyBot', 'CloudRadial', 'Datto RMM', 'Microsoft'].forEach(source => {
          if (syncStatus[source] === 'syncing' && data[source] !== 'syncing') {
            justFinished.push(source);
          }
        });

        setSyncStatus(data);

        // Update source sync times from completed times (actual sync completion, not device check-in)
        if (data.completedTimes) {
          setSourceSyncTimes(prev => ({
            ...prev,
            'N-Central': data.completedTimes['N-Central'] || prev['N-Central'],
            'ConnectWise': data.completedTimes['ConnectWise'] || prev['ConnectWise'],
            'Huntress': data.completedTimes['Huntress'] || prev['Huntress'],
            'ImmyBot': data.completedTimes['ImmyBot'] || prev['ImmyBot'],
            'CloudRadial': data.completedTimes['CloudRadial'] || prev['CloudRadial'],
            'Datto RMM': data.completedTimes['Datto RMM'] || prev['Datto RMM'],
            'Microsoft': data.completedTimes['Microsoft'] || prev['Microsoft']
          }));
        }

        // If any sync just finished, refresh device data and organizations immediately
        if (justFinished.length > 0) {
          fetchDevices();
          fetchOrganizations(); // Refresh organizations after sync
          // Show completion toast
          const finished = justFinished.filter(s => s !== 'full');
          if (finished.length > 0) {
            showToast(`Sync complete: ${finished.join(', ')}`, { type: 'success', duration: 4000 });
          } else if (justFinished.includes('full')) {
            showToast('Full sync complete — all sources updated', { type: 'success', duration: 4000 });
          }
        }

        // Update syncing state for backward compatibility
        if (data.full === 'syncing') {
          setSyncing(true);
        } else {
          setSyncing(false);
        }

        // Update syncingSource for individual syncs
        const syncingSources = [];
        ['N-Central', 'ConnectWise', 'Huntress', 'ImmyBot', 'CloudRadial', 'Datto RMM', 'Microsoft'].forEach(source => {
          if (data[source] === 'syncing') {
            syncingSources.push(source);
          }
        });
        if (syncingSources.length > 0) {
          setSyncingSource(syncingSources[0]); // Set to first syncing source
        } else {
          setSyncingSource(null);
        }
      }
    } catch (error) {
      console.error('Failed to fetch sync status:', error);
    }
  };

  const handleRefresh = () => {
    fetchDevices();
    fetchGeneralSettings();
    fetchOrgSettings();
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setShowSyncMenu(false);
      const response = await apiFetch(`${API_URL}/sync`, { method: 'POST' });
      if (response.ok) {
        showToast('Full sync started — all sources syncing in background', { type: 'info', duration: 3000 });
      } else {
        showToast('Failed to start sync', { type: 'error' });
        setSyncing(false);
      }
      // Don't set setSyncing(false) here — fetchSyncStatus polling (every 3s)
      // will detect when the background sync completes and update state + refresh data
    } catch (error) {
      console.error('Failed to start sync:', error);
      showToast(`Sync failed: ${error.message}`, { type: 'error' });
      setSyncing(false);
    }
  };

  // Map display names to backend URL keys for /api/sync-source/<key>
  const SOURCE_URL_MAP = {
    'N-Central': 'ncentral',
    'ImmyBot': 'immybot',
    'CloudRadial': 'cloudradial',
    'Huntress': 'huntress',
    'ConnectWise': 'connectwise',
    'Datto RMM': 'datto_rmm',
    'Microsoft': 'microsoft'
  };

  const handleSyncSource = async (source) => {
    try {
      setSyncingSource(source);
      setShowSyncMenu(false);
      const urlKey = SOURCE_URL_MAP[source] || source.toLowerCase().replace(/\s+/g, '_');
      const response = await apiFetch(`${API_URL}/sync-source/${urlKey}`, { method: 'POST' });
      if (response.ok) {
        showToast(`Syncing ${source} in background...`, { type: 'info', duration: 3000 });
      } else {
        showToast(`Failed to start ${source} sync`, { type: 'error' });
        setSyncingSource(null);
      }
      // Don't clear syncingSource — fetchSyncStatus polling will detect completion
      // and auto-refresh devices when done
    } catch (error) {
      console.error(`Failed to sync ${source}:`, error);
      showToast(`Sync failed: ${error.message}`, { type: 'error' });
      setSyncingSource(null);
    }
  };

  // --- DELETE ACTIONS ---
  // Standalone delete handlers removed in v6.0 — all actions now go through handleIntegrationModalAction

  // Integration icon click handler - opens action modal
  const handleIntegrationClick = (e, device, source) => {
    e.stopPropagation(); // Prevent row click

    // Build list of available integrations for this device (for delete/deactivate)
    const availableIntegrations = [];
    if (device.ids_map?.ImmyBot) availableIntegrations.push('ImmyBot');
    if (device.ids_map?.CloudRadial) availableIntegrations.push('CloudRadial');
    if (device.ids_map?.['N-Central']) availableIntegrations.push('N-Central');
    if (device.ids_map?.ConnectWise) availableIntegrations.push('ConnectWise');
    if (device.ids_map?.Microsoft) availableIntegrations.push('Microsoft');

    // Build list of ALL remediation options (deploy via ImmyBot)
    // Labels: "Install" if missing, "Redeploy" if present but stale/error, "Reinstall" if present and healthy
    const remediationOptions = [];
    const hasImmyBot = device.ids_map?.ImmyBot;
    const getDeployLabel = (src, isPresent) => {
      if (!isPresent) return `Install ${src} Agent`;
      const ih = device.healthIntegrations?.[src];
      if (ih && (ih.health === 'stale' || ih.health === 'warning' || ih.health === 'unhealthy')) {
        return `Redeploy ${src} Agent`;
      }
      return `Reinstall ${src} Agent`;
    };
    if (hasImmyBot) {
      // N-Central - can install, reinstall, or redeploy
      const hasNC = !!device.ids_map?.['N-Central'];
      remediationOptions.push({
        target: 'N-Central',
        via: 'ImmyBot',
        label: getDeployLabel('N-Central', hasNC),
        isReinstall: hasNC
      });
      // Datto RMM - can install, reinstall, or redeploy
      const hasDatto = device.sources?.includes('Datto RMM');
      remediationOptions.push({
        target: 'Datto RMM',
        via: 'ImmyBot',
        label: getDeployLabel('Datto RMM', hasDatto),
        isReinstall: !!hasDatto
      });
      // CloudRadial - can install, reinstall, or redeploy
      const hasCR = !!device.ids_map?.CloudRadial;
      remediationOptions.push({
        target: 'CloudRadial',
        via: 'ImmyBot',
        label: getDeployLabel('CloudRadial', hasCR),
        isReinstall: hasCR
      });
      // Huntress - can install, reinstall, or redeploy
      const hasHuntress = device.sources?.includes('Huntress');
      remediationOptions.push({
        target: 'Huntress',
        via: 'ImmyBot',
        label: getDeployLabel('Huntress', hasHuntress),
        isReinstall: !!hasHuntress
      });
    }

    // Check if the clicked source is present (for delete) or has remediation
    const isClickedSourcePresent = availableIntegrations.includes(source);
    // Check if clicked source is in error/stale state (candidate for redeploy)
    const clickedIntHealth = device.healthIntegrations?.[source];
    const isClickedSourceUnhealthy = clickedIntHealth && (clickedIntHealth.health === 'stale' || clickedIntHealth.health === 'warning' || clickedIntHealth.health === 'unhealthy');
    const canRedeploy = isClickedSourcePresent && isClickedSourceUnhealthy && hasImmyBot && remediationOptions.some(opt => opt.target === source);

    // Initialize delete options - pre-select clicked source if present
    const deleteOptions = {};
    availableIntegrations.forEach(int => {
      deleteOptions[int] = isClickedSourcePresent && int === source;
    });

    // Initialize remediation options
    // If clicked source is unhealthy and redeployable, pre-select deploy too (redeploy = remove + deploy)
    const deployOptions = {};
    remediationOptions.forEach(opt => {
      deployOptions[opt.target] = (opt.target === source && !isClickedSourcePresent) || (opt.target === source && canRedeploy);
    });

    // Check if Microsoft is available and device's archive status
    const hasMicrosoft = availableIntegrations.includes('Microsoft');
    const isArchived = device.microsoft_archived || false;

    setIntegrationModal({
      device,
      clickedSource: source,
      availableIntegrations,
      deleteOptions,
      blockCloudRadial: false,
      remediationOptions,
      deployOptions,
      // Archive options
      showArchive: hasMicrosoft && !isArchived,
      showUnarchive: hasMicrosoft && isArchived,
      archiveSelected: false,
      unarchiveSelected: false,
      archiveReason: 'Leave of Absence',
      archiveTicket: '',
      createTicket: true  // Default to creating ticket if PSA is configured
    });
  };

  // Handle integration modal action execution
  const handleIntegrationModalAction = async () => {
    if (!integrationModal) return;

    const { device, deleteOptions, blockCloudRadial, deployOptions, archiveSelected, unarchiveSelected, archiveReason, archiveTicket, createTicket } = integrationModal;
    const selectedDeletes = Object.entries(deleteOptions).filter(([_, checked]) => checked).map(([int]) => int);
    const selectedDeploys = Object.entries(deployOptions || {}).filter(([_, checked]) => checked).map(([int]) => int);

    if (selectedDeletes.length === 0 && selectedDeploys.length === 0 && !archiveSelected && !unarchiveSelected) {
      showToast("Please select at least one action (delete, deploy, archive, or unarchive).", { type: 'warning' });
      return;
    }

    // Build confirmation message
    let confirmMsg = `Actions for ${device.hostname}:\n`;
    if (selectedDeletes.length > 0) {
      // Separate ConnectWise (deactivate), Microsoft (with reason), and others (delete)
      const toDeactivate = selectedDeletes.filter(int => int === 'ConnectWise');
      const toDeleteMicrosoft = selectedDeletes.filter(int => int === 'Microsoft');
      const toDelete = selectedDeletes.filter(int => !['ConnectWise', 'Microsoft'].includes(int));

      if (toDelete.length > 0) {
        confirmMsg += `\n⚠️ DELETE from:`;
        toDelete.forEach(int => {
          confirmMsg += `\n  • ${int}`;
          if (int === 'CloudRadial' && blockCloudRadial) {
            confirmMsg += ' (+ BLOCK)';
          }
        });
      }
      if (toDeleteMicrosoft.length > 0) {
        confirmMsg += `\n\n⚠️ DELETE from Microsoft:`;
        confirmMsg += `\n  • Reason: ${archiveReason}`;
        if (ticketingConfig.enabled && createTicket) {
          confirmMsg += `\n  • Ticket: Auto-create in ${ticketingConfig.psaName}`;
        } else if (archiveTicket) {
          confirmMsg += `\n  • Ticket ref: ${archiveTicket}`;
        }
      }
      if (toDeactivate.length > 0) {
        confirmMsg += `\n\n⚠️ DEACTIVATE in:`;
        toDeactivate.forEach(int => {
          confirmMsg += `\n  • ${int}`;
        });
      }
    }
    if (selectedDeploys.length > 0) {
      confirmMsg += `\n\n🚀 DEPLOY:`;
      selectedDeploys.forEach(int => {
        confirmMsg += `\n  • ${int} Agent (via ImmyBot)`;
      });
    }
    if (archiveSelected) {
      confirmMsg += `\n\n📦 ARCHIVE in Microsoft Entra:`;
      confirmMsg += `\n  • Reason: ${archiveReason}`;
      if (ticketingConfig.enabled && createTicket) {
        confirmMsg += `\n  • Ticket: Auto-create in ${ticketingConfig.psaName}`;
      } else if (archiveTicket) {
        confirmMsg += `\n  • Ticket ref: ${archiveTicket}`;
      }
    }
    if (unarchiveSelected) {
      confirmMsg += `\n\n📦 UNARCHIVE - Re-enable in Microsoft Entra`;
    }
    if (!await showConfirmation({ title: `Confirm Actions for ${device.hostname}`, message: confirmMsg, confirmLabel: 'Proceed', variant: selectedDeletes.length > 0 ? 'danger' : 'deploy' })) return;

    setIntegrationModal(null); // Close modal
    let results = [];

    try {
      // Handle DELETES first — must remove before redeploying (redeploy = delete + deploy)
      if (selectedDeletes.length > 0) {
        // If only CloudRadial and block is selected, use the block endpoint
        if (selectedDeletes.length === 1 && selectedDeletes[0] === 'CloudRadial' && blockCloudRadial) {
          const response = await apiFetch(`${API_URL}/delete-block-cloudradial`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: device.ids_map.CloudRadial })
          });
          const result = await response.json();
          if (result.status === 'success' || result.status === 'partial') {
            results.push(`CloudRadial blocked and deleted`);
          } else {
            results.push(`CloudRadial delete failed: ${result.message}`);
          }
        }
        // If only one non-CloudRadial integration, use individual delete
        else if (selectedDeletes.length === 1 && selectedDeletes[0] !== 'CloudRadial') {
          const source = selectedDeletes[0];
          if (source === 'ImmyBot') {
            await apiFetch(`${API_URL}/delete-immy`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: device.ids_map.ImmyBot }) });
            results.push(`Deleted from ImmyBot`);
          } else if (source === 'N-Central') {
            const response = await apiFetch(`${API_URL}/delete-ncentral`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: device.ids_map['N-Central'] }) });
            const result = await response.json();
            if (result.status === 'success') {
              results.push(`Deleted from N-Central`);
            } else {
              results.push(`N-Central delete failed: ${result.message}`);
            }
          } else if (source === 'ConnectWise') {
            const response = await apiFetch(`${API_URL}/deactivate-connectwise`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: device.ids_map.ConnectWise }) });
            const result = await response.json();
            if (result.status === 'success') {
              results.push(`Deactivated in ConnectWise`);
            } else {
              results.push(`ConnectWise deactivate failed: ${result.message}`);
            }
          } else if (source === 'Microsoft') {
            const response = await apiFetch(`${API_URL}/delete-microsoft`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: device.ids_map.Microsoft }) });
            const result = await response.json();
            if (result.status === 'success') {
              results.push(`Deleted from Microsoft`);
            } else {
              results.push(`Microsoft delete failed: ${result.message}`);
            }
          }
        }
        // If only CloudRadial without block
        else if (selectedDeletes.length === 1 && selectedDeletes[0] === 'CloudRadial' && !blockCloudRadial) {
          await apiFetch(`${API_URL}/delete-cloudradial`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: device.ids_map.CloudRadial }) });
          results.push(`Deleted from CloudRadial`);
        }
        // Multiple integrations - handle ConnectWise and Microsoft separately, then bundle the rest
        else if (selectedDeletes.length > 1) {
          // Handle ConnectWise deactivation separately (it's a deactivate, not delete)
          if (selectedDeletes.includes('ConnectWise')) {
            const response = await apiFetch(`${API_URL}/deactivate-connectwise`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: device.ids_map.ConnectWise }) });
            const result = await response.json();
            if (result.status === 'success') {
              results.push(`Deactivated in ConnectWise`);
            } else {
              results.push(`ConnectWise deactivate failed: ${result.message}`);
            }
          }

          // Handle Microsoft deletion separately (not part of bundled delete)
          if (selectedDeletes.includes('Microsoft')) {
            const response = await apiFetch(`${API_URL}/delete-microsoft`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: device.ids_map.Microsoft,
                reason: archiveReason,
                ticket: archiveTicket || null,
                create_ticket: ticketingConfig.enabled && createTicket
              })
            });
            const result = await response.json();
            if (result.status === 'success') {
              results.push(`Deleted from Microsoft (${archiveReason})`);
            } else {
              results.push(`Microsoft delete failed: ${result.message}`);
            }
          }

          // Handle remaining integrations with bundled delete (ImmyBot, CloudRadial, N-Central)
          const bundledDeletes = selectedDeletes.filter(s => !['ConnectWise', 'Microsoft'].includes(s));
          if (bundledDeletes.length > 0) {
            const payload = {
              immy_id: bundledDeletes.includes('ImmyBot') ? device.ids_map?.ImmyBot : undefined,
              cr_id: bundledDeletes.includes('CloudRadial') ? device.ids_map?.CloudRadial : undefined,
              nc_id: bundledDeletes.includes('N-Central') ? device.ids_map?.['N-Central'] : undefined,
              block_cloudradial: blockCloudRadial && bundledDeletes.includes('CloudRadial')
            };
            const res = await apiFetch(`${API_URL}/delete-bundled`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            const result = await res.json();
            if (result.status === 'success') {
              results.push(`Deleted from ${bundledDeletes.join(' + ')}${blockCloudRadial ? ' (CloudRadial blocked)' : ''}`);
            } else {
              results.push(`Bundled delete failed: ${result.message || 'Unknown error'}`);
            }
          }
        }
      }

      // Handle DEPLOYMENTS after deletes — use generic trigger-deployment endpoint
      for (const target of selectedDeploys) {
        if (!device.ids_map?.ImmyBot) {
          results.push(`${target} deploy failed: No ImmyBot ID`);
          continue;
        }

        const response = await apiFetch(`${API_URL}/trigger-deployment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            immy_id: device.ids_map.ImmyBot,
            software_name: target,
            org_name: device.organization
          })
        });
        const result = await response.json();

        if (result.status === 'success') {
          results.push(`${target} deployment triggered`);
          const deviceKey = `${device.hostname}-${device.serial}`;
          setPendingDeployments(prev => ({
            ...prev,
            [deviceKey]: { ...(prev[deviceKey] || {}), [target]: Date.now() }
          }));
        } else if (result.status === 'needs_approval' || result.status === 'needs_selection' || result.status === 'needs_manual') {
          setDeploymentApprovalModal({
            ...result,
            device,
            pendingDeploys: selectedDeploys.slice(selectedDeploys.indexOf(target) + 1),
            pendingDeletes: null // Deletes already ran
          });
          if (results.length > 0) {
            showToast(results.join('. '), { type: 'info' });
          }
          return;
        } else {
          results.push(`${target} deploy failed: ${result.message}`);
        }
      }

      // Handle ARCHIVE
      if (archiveSelected && device.ids_map?.Microsoft) {
        try {
          const response = await apiFetch(`${API_URL}/microsoft/archive-device`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              unique_id: device.ids_map.Microsoft,
              reason: archiveReason,
              ticket: archiveTicket || null,
              create_ticket: ticketingConfig.enabled && createTicket
            })
          });
          const result = await response.json();
          if (result.status === 'success') {
            results.push(`Archived in Microsoft Entra (${archiveReason})`);
          } else {
            results.push(`Archive failed: ${result.message || 'Unknown error'}`);
          }
        } catch (err) {
          results.push(`Archive error: ${err.message}`);
        }
      }

      // Handle UNARCHIVE
      if (unarchiveSelected && device.ids_map?.Microsoft) {
        try {
          const response = await apiFetch(`${API_URL}/microsoft/unarchive-device`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              unique_id: device.ids_map.Microsoft
            })
          });
          const result = await response.json();
          if (result.status === 'success') {
            results.push(`Unarchived - Re-enabled in Microsoft Entra`);
          } else {
            results.push(`Unarchive failed: ${result.message || 'Unknown error'}`);
          }
        } catch (err) {
          results.push(`Unarchive error: ${err.message}`);
        }
      }

      // Show results with bullet-point detail
      if (results.length > 0) {
        const hasFailures = results.some(r => r.toLowerCase().includes('failed') || r.toLowerCase().includes('error'));
        const allFailed = results.every(r => r.toLowerCase().includes('failed') || r.toLowerCase().includes('error'));
        const toastType = allFailed ? 'error' : hasFailures ? 'warning' : 'success';
        const bulletResults = results.map(r => `\u2022 ${r}`).join('\n');
        showToast(`${device.hostname}: ${results.length} action(s)`, {
          type: toastType,
          subtitle: bulletResults,
          duration: hasFailures ? 0 : 6000  // Sticky if any failures
        });
      }
      fetchDevices();
    } catch (e) {
      showToast(`Error: ${e.message}`, { type: 'error' });
    }
  };

  // Check if any selected devices have duplicates before allowing bulk delete
  const checkBulkMicrosoftDuplicates = async (devices) => {
    try {
      // Check the scan endpoint to see if any of these devices are in duplicate groups
      const response = await apiFetch(`${API_URL}/microsoft/duplicates/scan`);
      const data = await response.json();

      if (data.status !== 'success') return { hasDuplicates: false };

      // Build a map of hostname|org -> duplicate group devices (excluding the device already in the list)
      const duplicateGroupMap = {};
      for (const org of data.organizations || []) {
        for (const group of org.groups || []) {
          const key = `${group.hostname}|${org.organization_name}`;
          duplicateGroupMap[key] = group.devices || [];
        }
      }

      // Check if any selected devices are in duplicate groups
      const devicesWithDuplicates = devices.filter(d => {
        const key = `${d.hostname}|${d.organization}`;
        return !!duplicateGroupMap[key];
      });

      return {
        hasDuplicates: devicesWithDuplicates.length > 0,
        devicesWithDuplicates,
        duplicateGroupMap,
        totalDuplicateGroups: data.summary?.total_duplicate_groups || 0
      };
    } catch (error) {
      console.error('Error checking duplicates:', error);
      return { hasDuplicates: false };
    }
  };

  // Handle bulk Microsoft action (archive/delete)
  const handleBulkMicrosoftAction = async () => {
    if (!bulkMicrosoftModal) return;

    const { devices, action, reason, ticket, createTicket, selectedDevices, selectedDuplicateIds } = bulkMicrosoftModal;

    // Filter to only selected devices
    const devicesToProcess = devices.filter(d => selectedDevices?.has(d.ids_map?.Microsoft));

    // Collect selected duplicate IDs (these are unique_ids from the duplicate scan)
    const duplicateIdsToProcess = [...(selectedDuplicateIds || [])];

    const totalToDelete = devicesToProcess.length + duplicateIdsToProcess.length;

    if (totalToDelete === 0) {
      showToast('Please select at least one device', { type: 'warning' });
      return;
    }

    if (!reason) {
      showToast('Please select a reason', { type: 'warning' });
      return;
    }

    const actionLabel = action === 'delete' ? 'DELETE' : 'ARCHIVE';
    let confirmMsg = `⚠️ ${actionLabel} ${totalToDelete} Microsoft device(s)?\n\n`;
    confirmMsg += `Reason: ${reason}\n`;
    if (ticketingConfig.enabled && createTicket) {
      confirmMsg += `Ticket: Auto-create in ${ticketingConfig.psaName}\n`;
    } else if (ticket) {
      confirmMsg += `Ticket ref: ${ticket}\n`;
    }
    confirmMsg += `\nDevices:\n`;
    devicesToProcess.slice(0, 10).forEach(d => {
      confirmMsg += `  • ${d.hostname}\n`;
    });
    if (duplicateIdsToProcess.length > 0) {
      confirmMsg += `  + ${duplicateIdsToProcess.length} duplicate record(s)\n`;
    }
    if (devicesToProcess.length > 10) {
      confirmMsg += `  ... and ${devicesToProcess.length - 10} more\n`;
    }

    if (!await showConfirmation({ title: `Bulk ${action === 'delete' ? 'Delete' : 'Archive'} Microsoft Devices`, message: confirmMsg, confirmLabel: `${action === 'delete' ? 'Delete' : 'Archive'} ${totalToDelete} Devices`, variant: 'danger' })) return;

    setBulkMicrosoftModal(null);
    const progressToastId = showToast(
      action === 'delete' ? 'Deleting devices...' : 'Archiving devices...',
      { type: 'progress', progressCurrent: 0, progressTotal: totalToDelete }
    );
    let successCount = 0;
    let errorCount = 0;

    try {
      if (action === 'delete') {
        let progressIndex = 0;
        // Delete endpoint only accepts single device, so loop through each
        for (let i = 0; i < devicesToProcess.length; i++) {
          const device = devicesToProcess[i];
          progressIndex++;
          updateToast(progressToastId, { type: 'progress', progressCurrent: progressIndex, progressTotal: totalToDelete });
          try {
            const response = await apiFetch(`${API_URL}/delete-microsoft`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: device.ids_map.Microsoft,
                reason,
                ticket: ticket || null,
                create_ticket: createTicket || false
              })
            });
            const result = await response.json();
            if (result.status === 'success') {
              successCount++;
            } else {
              errorCount++;
            }
          } catch (err) {
            errorCount++;
          }
        }
        // Also delete selected duplicate records
        for (let i = 0; i < duplicateIdsToProcess.length; i++) {
          const dupId = duplicateIdsToProcess[i];
          progressIndex++;
          updateToast(progressToastId, { type: 'progress', progressCurrent: progressIndex, progressTotal: totalToDelete });
          try {
            const response = await apiFetch(`${API_URL}/delete-microsoft`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: dupId,
                reason,
                ticket: ticket || null,
                create_ticket: createTicket || false
              })
            });
            const result = await response.json();
            if (result.status === 'success') {
              successCount++;
            } else {
              errorCount++;
            }
          } catch (err) {
            errorCount++;
          }
        }
        if (successCount > 0) {
          updateToast(progressToastId, { message: `DELETE complete: ${successCount} device(s) deleted${errorCount > 0 ? `, ${errorCount} failed` : ''}`, type: 'success' });
        } else {
          updateToast(progressToastId, { message: `DELETE failed: ${errorCount} device(s) failed`, type: 'error' });
        }
      } else {
        // Archive endpoint accepts bulk unique_ids
        const uniqueIds = devicesToProcess.map(d => d.ids_map.Microsoft);
        const response = await apiFetch(`${API_URL}/microsoft/archive-device`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            unique_ids: uniqueIds,
            reason,
            ticket: ticket || null,
            create_ticket: createTicket || false
          })
        });
        const result = await response.json();

        if (result.status === 'success') {
          successCount = result.processed || devicesToProcess.length;
          updateToast(progressToastId, { message: `ARCHIVE complete: ${successCount} device(s) processed`, type: 'success' });
        } else {
          updateToast(progressToastId, { message: `ARCHIVE failed: ${result.message || 'Unknown error'}`, type: 'error' });
        }
      }
    } catch (err) {
      updateToast(progressToastId, { message: `Error: ${err.message}`, type: 'error' });
    }

    fetchDevices();
    setSelectedDevices(new Set());
  };

  // Handle deployment approval modal actions
  const handleDeploymentApproval = async (action, selectedDeploymentId = null, manualDeploymentId = null) => {
    if (!deploymentApprovalModal) return;

    const { device, software_name, org_name, pendingDeploys, pendingDeletes } = deploymentApprovalModal; // eslint-disable-line no-unused-vars

    if (action === 'cancel') {
      setDeploymentApprovalModal(null);
      return;
    }

    let deploymentId = null;

    if (action === 'approve') {
      // User approved the discovered deployment
      deploymentId = deploymentApprovalModal.deployment_id;
    } else if (action === 'select') {
      // User selected from multiple deployments
      deploymentId = selectedDeploymentId;
    } else if (action === 'manual') {
      // User entered manual deployment ID
      deploymentId = manualDeploymentId;
    }

    if (!deploymentId && action !== 'skip') {
      showToast('Please select or enter a deployment ID', { type: 'warning' });
      return;
    }

    try {
      // Save the approved deployment ID
      if (deploymentId) {
        const saveResponse = await apiFetch(`${API_URL}/deployment-ids/${encodeURIComponent(org_name)}/${software_name}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            deployment_id: deploymentId,
            approved: true,
            manual_override: action === 'manual'
          })
        });
        const saveResult = await saveResponse.json();
        if (saveResult.status !== 'success') {
          showToast(`Failed to save deployment ID: ${saveResult.message}`, { type: 'error' });
          return;
        }
      }

      // Now trigger the deployment
      const triggerResponse = await apiFetch(`${API_URL}/trigger-deployment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          immy_id: device.ids_map.ImmyBot,
          software_name: software_name,
          org_name: org_name
        })
      });
      const result = await triggerResponse.json();

      let results = [];
      if (result.status === 'success') {
        results.push(`${software_name} deployment triggered`);
        // Track pending deployment for UI feedback
        const deviceKey = `${device.hostname}-${device.serial}`;
        setPendingDeployments(prev => ({
          ...prev,
          [deviceKey]: { ...(prev[deviceKey] || {}), [software_name]: Date.now() }
        }));
      } else {
        results.push(`${software_name} deploy failed: ${result.message}`);
      }

      // Close modal
      setDeploymentApprovalModal(null);

      // Continue with remaining deploys if any
      if (pendingDeploys && pendingDeploys.length > 0) {
        // Re-open integration modal to continue
        // For now, just show results and refresh
        if (results.length > 0) {
          showToast(results.join('. '), { type: 'info' });
        }
      } else if (pendingDeletes) {
        // Continue with deletes - for now just alert and refresh
        showToast(results.join('. ') + '. Please re-open the device to continue with delete actions.', { type: 'info' });
      } else {
        if (results.length > 0) {
          showToast(results.join('. '), { type: 'info' });
        }
      }

      fetchDevices();
    } catch (e) {
      showToast(`Error: ${e.message}`, { type: 'error' });
    }
  };

  // Selection handlers
  const toggleDeviceSelection = (deviceKey) => {
    setSelectedDevices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(deviceKey)) {
        newSet.delete(deviceKey);
      } else {
        newSet.add(deviceKey);
      }
      return newSet;
    });
  };

  const toggleSelectAll = (devices) => {
    // Select devices that have any actionable integration (ImmyBot, CloudRadial, N-Central, or Microsoft)
    const actionableDevices = devices.filter(d => d.ids_map?.ImmyBot || d.ids_map?.CloudRadial || d.ids_map?.['N-Central'] || d.ids_map?.Microsoft);
    const allActionableSelected = actionableDevices.every(d => selectedDevices.has(`${d.hostname}-${d.serial}`));

    if (allActionableSelected && actionableDevices.length > 0) {
      setSelectedDevices(new Set());
    } else {
      setSelectedDevices(new Set(actionableDevices.map(d => `${d.hostname}-${d.serial}`)));
    }
  };

  // Quick org settings modal - open from device row issues badge
  const openQuickOrgSettings = (orgName) => {
    const currentSettings = orgSettings[orgName] || {};
    const hasSettings = !!orgSettings[orgName]; // Whether org has saved settings
    setQuickOrgSettingsForm({
      is_hidden: currentSettings.is_hidden || false,
      // Use actual saved values; for unsaved orgs, default most to true except Datto/Microsoft
      ncentral_enabled: hasSettings ? !!currentSettings.ncentral_enabled : true,
      connectwise_enabled: hasSettings ? !!currentSettings.connectwise_enabled : true,
      huntress_enabled: hasSettings ? !!currentSettings.huntress_enabled : true,
      immybot_enabled: hasSettings ? !!currentSettings.immybot_enabled : true,
      cloudradial_enabled: hasSettings ? !!currentSettings.cloudradial_enabled : true,
      datto_rmm_enabled: hasSettings ? !!currentSettings.datto_rmm_enabled : false, // Default false per Settings.js
      microsoft_enabled: hasSettings ? !!currentSettings.microsoft_enabled : false, // Default false per Settings.js
      primary_rmm: currentSettings.primary_rmm || 'global',
      // MacOS settings (default to false/disabled)
      mac_health_checks_enabled: currentSettings.mac_health_checks_enabled || false,
      mac_ncentral_required: currentSettings.mac_ncentral_required || false,
      mac_datto_rmm_required: currentSettings.mac_datto_rmm_required || false,
      mac_cloudradial_required: currentSettings.mac_cloudradial_required || false,
      mac_huntress_required: currentSettings.mac_huntress_required || false,
      mac_connectwise_required: currentSettings.mac_connectwise_required || false,
      mac_microsoft_required: currentSettings.mac_microsoft_required || false
    });
    setQuickOrgSettingsModal({ orgName });
  };

  // Quick org settings modal - save and refresh
  const saveQuickOrgSettings = async () => {
    if (!quickOrgSettingsModal) return;

    try {
      const response = await apiFetch(`${API_URL}/organizations/settings/${encodeURIComponent(quickOrgSettingsModal.orgName)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(quickOrgSettingsForm)
      });

      if (response.ok) {
        // Update local state
        setOrgSettings(prev => ({
          ...prev,
          [quickOrgSettingsModal.orgName]: quickOrgSettingsForm
        }));
        setQuickOrgSettingsModal(null);
        // Refresh device data to apply new settings
        fetchDevices();
      } else {
        const err = await response.json();
        showToast(`Failed to save settings: ${err.error || 'Unknown error'}`, { type: 'error' });
      }
    } catch (error) {
      showToast(`Error saving settings: ${error.message}`, { type: 'error' });
    }
  };

  // Bulk delete handler - opens modal for confirmation with ticket options
  const handleBulkDelete = (blockCloudRadial = false) => {
    if (selectedDevices.size === 0) {
      showToast("No devices selected", { type: 'warning' });
      return;
    }

    // Filter to only process devices that are actually visible in the current view
    const devicesToDelete = resolveSelectedDevices(selectedDevices);

    if (devicesToDelete.length === 0) {
      showToast("No valid devices to delete", { type: 'warning' });
      return;
    }

    // Open the bulk delete modal
    setBulkDeleteModal({
      devices: devicesToDelete,
      blockCloudRadial,
      ticket: '',
      createTicket: ticketingConfig.enabled // Default to true if PSA configured
    });
  };

  // Execute bulk delete after modal confirmation
  const executeBulkDelete = async () => {
    if (!bulkDeleteModal) return;

    const { devices: devicesToDelete, blockCloudRadial, ticket, createTicket } = bulkDeleteModal;

    setBulkDeleteModal(null);
    const progressToastId = showToast('Deleting devices...', { type: 'progress', progressCurrent: 0, progressTotal: devicesToDelete.length });

    const action = blockCloudRadial ? "block & delete" : "delete";
    let successCount = 0;
    let partialCount = 0;
    let errorCount = 0;
    let skippedCount = 0;
    const skippedDevices = [];

    for (let i = 0; i < devicesToDelete.length; i++) {
      const device = devicesToDelete[i];
      // Check for any of the bundled delete integrations (ImmyBot, CloudRadial, N-Central)
      // Microsoft is handled separately via bulk Microsoft modal
      const hasBundledIntegration = device.ids_map?.ImmyBot || device.ids_map?.CloudRadial || device.ids_map?.['N-Central'];

      if (!hasBundledIntegration) {
        // No bundled integrations - skip but note it (Microsoft is handled separately)
        skippedCount++;
        skippedDevices.push(device.hostname);
        updateToast(progressToastId, { type: 'progress', progressCurrent: i + 1, progressTotal: devicesToDelete.length });
        continue;
      }

      try {
        // Use bundled delete endpoint - creates ONE ticket and handles all sources together
        // Determine which integrations are missing for this device
        const skippedIntegrations = [];
        if (!device.ids_map?.ImmyBot) skippedIntegrations.push('ImmyBot');
        if (!device.ids_map?.CloudRadial) skippedIntegrations.push('CloudRadial');
        if (!device.ids_map?.['N-Central']) skippedIntegrations.push('N-Central');

        const payload = {
          immy_id: device.ids_map?.ImmyBot,
          cr_id: device.ids_map?.CloudRadial,
          nc_id: device.ids_map?.['N-Central'],
          block_cloudradial: blockCloudRadial,
          skipped_integrations: skippedIntegrations,
          ticket: ticket || null,
          create_ticket: createTicket || false
        };
        const response = await apiFetch(`${API_URL}/delete-bundled`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const result = await response.json();
        if (result.status === 'success') {
          // Check if any integrations were skipped
          if (result.skipped && result.skipped.length > 0) {
            partialCount++;
          } else {
            successCount++;
          }
        } else {
          errorCount++;
        }
      } catch (e) {
        console.error(`Failed to ${action} ${device.hostname}:`, e);
        errorCount++;
      }

      updateToast(progressToastId, { type: 'progress', progressCurrent: i + 1, progressTotal: devicesToDelete.length });
    }

    setSelectedDevices(new Set());

    let resultMsg = `Bulk ${action} complete: `;
    resultMsg += `${successCount} fully deleted`;
    if (partialCount > 0) resultMsg += `, ${partialCount} partially deleted (some integrations skipped)`;
    if (skippedCount > 0) resultMsg += `, ${skippedCount} skipped (no integrations)`;
    if (errorCount > 0) resultMsg += `, ${errorCount} failed`;
    if (skippedDevices.length > 0 && skippedDevices.length <= 5) {
      resultMsg += `. Skipped devices: ${skippedDevices.join(', ')}`;
    }
    updateToast(progressToastId, { message: resultMsg, type: 'success' });
    fetchDevices();
  };

  // Bulk deploy CloudRadial handler
  const handleBulkDeployCloudRadial = async () => {
    if (selectedDevices.size === 0) {
      showToast("No devices selected", { type: 'warning' });
      return;
    }

    // Filter to only process devices that have ImmyBot but not CloudRadial
    const devicesToDeploy = resolveSelectedDevices(selectedDevices)
      .filter(d => d.ids_map?.ImmyBot && !d.ids_map?.CloudRadial);

    if (devicesToDeploy.length === 0) {
      showToast("No valid devices to deploy CloudRadial to (must have ImmyBot but not CloudRadial)", { type: 'warning' });
      return;
    }

    if (!await showConfirmation({ title: `Deploy CloudRadial`, message: `Deploy CloudRadial to ${devicesToDeploy.length} selected devices? This will trigger ImmyBot to install CloudRadial Agent on each device.`, confirmLabel: `Deploy ${devicesToDeploy.length} Devices`, variant: 'deploy' })) return;

    const progressToastId = showToast('Deploying CloudRadial...', { type: 'progress', progressCurrent: 0, progressTotal: devicesToDeploy.length });
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < devicesToDeploy.length; i++) {
      const device = devicesToDeploy[i];

      try {
        const response = await apiFetch(`${API_URL}/trigger-cloudradial-deployment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ immy_id: device.ids_map.ImmyBot })
        });
        const result = await response.json();
        if (result.status === 'success') {
          successCount++;
        } else {
          console.error(`Failed to deploy CloudRadial to ${device.hostname}:`, result.message);
          errorCount++;
        }
      } catch (e) {
        console.error(`Failed to deploy CloudRadial to ${device.hostname}:`, e);
        errorCount++;
      }

      updateToast(progressToastId, { type: 'progress', progressCurrent: i + 1, progressTotal: devicesToDeploy.length });
    }

    setSelectedDevices(new Set());
    updateToast(progressToastId, { message: `CloudRadial deployment complete: ${successCount} triggered, ${errorCount} failed`, type: 'success' });
    fetchDevices();
  };

  // Single device CloudRadial deployment (for future use)
  const _handleDeployCloudRadial = async (immyId, hostname) => { // eslint-disable-line no-unused-vars
    if (!await showConfirmation({ title: `Deploy CloudRadial`, message: `Deploy CloudRadial to ${hostname}? This will trigger ImmyBot to install CloudRadial Agent.`, confirmLabel: 'Deploy', variant: 'deploy' })) return;

    try {
      const response = await apiFetch(`${API_URL}/trigger-cloudradial-deployment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ immy_id: immyId })
      });
      const result = await response.json();
      if (result.status === 'success') {
        showToast(`CloudRadial deployment triggered for ${hostname}`, { type: 'success' });
      } else {
        showToast(`Error: ${result.message}`, { type: 'error' });
      }
    } catch (e) {
      showToast(`Error: ${e.message}`, { type: 'error' });
    }
  };

  // Bulk deploy N-Central handler
  // onlineOnly: if true, only deploy to devices online in ImmyBot or Datto RMM (silent push)
  const handleBulkDeployNCentral = async (onlineOnly = false) => {
    if (selectedDevices.size === 0) {
      showToast("No devices selected", { type: 'warning' });
      return;
    }

    // Filter to only process devices that have ImmyBot but not N-Central
    let devicesToDeploy = resolveSelectedDevices(selectedDevices)
      .filter(d => d.ids_map?.ImmyBot && !d.sources.includes('N-Central'));

    // If onlineOnly, further filter to devices online in ImmyBot or Datto RMM
    if (onlineOnly) {
      devicesToDeploy = devicesToDeploy.filter(d => isDeviceOnline(d));
    }

    if (devicesToDeploy.length === 0) {
      showToast(onlineOnly
        ? "No online devices to deploy N-Central to (must be online in ImmyBot or Datto RMM within 24h)"
        : "No valid devices to deploy N-Central to (must have ImmyBot but not N-Central)", { type: 'warning' });
      return;
    }

    const modeText = onlineOnly ? "ONLINE" : "ALL SELECTED";
    const deployMsg = onlineOnly
      ? `Deploy N-Central to ${devicesToDeploy.length} ONLINE devices? These devices checked in within 24h via ImmyBot or Datto RMM. Silent push — higher success rate.`
      : `Deploy N-Central to ${devicesToDeploy.length} devices (including offline)? Offline devices will queue the deployment for later.`;

    if (!await showConfirmation({ title: `Deploy N-Central (${modeText})`, message: deployMsg, confirmLabel: `Deploy ${devicesToDeploy.length} Devices`, variant: 'deploy' })) return;

    const progressToastId = showToast('Deploying N-Central...', { type: 'progress', progressCurrent: 0, progressTotal: devicesToDeploy.length });
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < devicesToDeploy.length; i++) {
      const device = devicesToDeploy[i];

      try {
        const response = await apiFetch(`${API_URL}/trigger-ncentral-deployment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ immy_id: device.ids_map.ImmyBot })
        });
        const result = await response.json();
        if (result.status === 'success') {
          successCount++;
        } else {
          console.error(`Failed to deploy N-Central to ${device.hostname}:`, result.message);
          errorCount++;
        }
      } catch (e) {
        console.error(`Failed to deploy N-Central to ${device.hostname}:`, e);
        errorCount++;
      }

      updateToast(progressToastId, { type: 'progress', progressCurrent: i + 1, progressTotal: devicesToDeploy.length });
    }

    setSelectedDevices(new Set());
    updateToast(progressToastId, { message: `N-Central deployment complete (${modeText}): ${successCount} triggered, ${errorCount} failed`, type: 'success' });
    fetchDevices();
  };

  // Single device N-Central deployment (for future use)
  const _handleDeployNCentral = async (immyId, hostname) => { // eslint-disable-line no-unused-vars
    if (!await showConfirmation({ title: `Deploy N-Central`, message: `Deploy N-Central to ${hostname}? This will trigger ImmyBot to install N-Central Agent.`, confirmLabel: 'Deploy', variant: 'deploy' })) return;

    try {
      const response = await apiFetch(`${API_URL}/trigger-ncentral-deployment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ immy_id: immyId })
      });
      const result = await response.json();
      if (result.status === 'success') {
        showToast(`N-Central deployment triggered for ${hostname}`, { type: 'success' });
      } else {
        showToast(`Error: ${result.message}`, { type: 'error' });
      }
    } catch (e) {
      showToast(`Error: ${e.message}`, { type: 'error' });
    }
  };

  // --- NEW: MANUAL MERGE ACTION ---
  const handleTrustMerge = async (hostname, candidates) => {
    // Show details for confirmation
    const details = candidates.map(c => `• ${c.sources.join('+')} (Serial: ${c.serial})`).join('\n');

    if (!await showConfirmation({ title: `Merge Records for "${hostname}"`, message: `We detected multiple rows for this device:\n${details}\n\nForce merge? This will save "${hostname}" to the Trusted Merge list.`, confirmLabel: 'Merge Records', variant: 'info' })) return;

    try {
        await apiFetch(`${API_URL}/trust-merge`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ hostname })
        });
        // Force refresh to see the merge happen
        setLoading(true);
        setTimeout(() => fetchDevices(), 1000);
    } catch (e) { showToast("Merge failed: " + e.message, { type: 'error' }); }
  };

  // --- CSV EXPORT ---
  const handleExportCSV = () => {
    // Create CSV content from currently displayed data
    const headers = ['Hostname', 'Serial', 'Status', 'N-Central Last Sync', 'ConnectWise Status', 'Huntress Last Sync', 'ImmyBot Last Sync', 'CloudRadial Last Sync', 'Sources'];
    const csvRows = [headers.join(',')];

    finalDisplayData.forEach(dev => {
      const row = [
        `"${dev.hostname || ''}"`,
        `"${dev.serial || ''}"`,
        `"${dev.computedStatus || dev.global_health_status || ''}"`,
        `"${dev.last_sync_nable || 'N/A'}"`,
        `"${dev.cw_status || 'N/A'}"`,
        `"${dev.last_sync_huntress || 'N/A'}"`,
        `"${dev.last_sync_immybot || 'N/A'}"`,
        `"${dev.last_sync_cloudradial || 'N/A'}"`,
        `"${dev.sources ? dev.sources.join('+') : ''}"`
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `asset-mesh-export-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Fetch ticketing/PSA configuration
  const fetchTicketingConfig = async () => {
    try {
      const response = await apiFetch(`${API_URL}/system-info`);
      if (response.ok) {
        const data = await response.json();
        setTicketingConfig({
          enabled: data.ticketing_enabled || false,
          psaName: data.ticketing_psa_name || null
        });
      }
    } catch (error) {
      console.error('Failed to fetch ticketing config:', error);
    }
  };

  // Fetch branding/whitelabel settings
  const fetchBranding = async () => {
    try {
      const response = await apiFetch(`${API_URL}/settings/branding`);
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success' && data.settings) {
          setBranding(data.settings);
        }
      }
    } catch (error) {
      // Branding is non-critical, silently fail
    }
  };

  useEffect(() => {
    fetchDevices();
    fetchSyncConfig();
    fetchSyncStatus();
    fetchOrganizations();
    fetchTaskQueue();
    fetchCredentialsStatus();
    fetchGeneralSettings();
    fetchOrgSettings();
    fetchTicketingConfig();
    fetchBranding();
    fetchScheduledTasks();

    const deviceInterval = setInterval(fetchDevices, 30000);
    const statusInterval = setInterval(fetchSyncStatus, 5000);
    const taskQueueInterval = setInterval(fetchTaskQueue, 5000);
    const scheduledTasksInterval = setInterval(fetchScheduledTasks, 30000);

    return () => {
      clearInterval(deviceInterval);
      clearInterval(taskQueueInterval);
      clearInterval(statusInterval);
      clearInterval(scheduledTasksInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.body.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Apply branding colors as CSS variables
  useEffect(() => {
    const root = document.documentElement;
    if (branding.primary_color) {
      root.style.setProperty('--blue', branding.primary_color);
    } else {
      root.style.removeProperty('--blue');
    }
    if (branding.accent_color) {
      root.style.setProperty('--purple', branding.accent_color);
    } else {
      root.style.removeProperty('--purple');
    }
    // Apply custom favicon
    if (branding.favicon) {
      const link = document.querySelector("link[rel='icon']") || document.createElement('link');
      link.rel = 'icon';
      link.href = branding.favicon;
      document.head.appendChild(link);
    }
    // Apply custom page title
    if (branding.app_name) {
      document.title = branding.app_name;
    }
  }, [branding]);

  useEffect(() => {
    localStorage.setItem('selectedSources', JSON.stringify(selectedSources));
  }, [selectedSources]);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'dark' ? 'light' : 'dark');
  };

  const fetchSyncConfig = async () => {
    try {
      const response = await apiFetch(`${API_URL}/sync-config`);
      if (response.ok) {
        const data = await response.json();
        setSyncInterval(data.interval_hours);
        if (data.timezone) {
          setTimezone(data.timezone);
        }
      }
    } catch (error) {
      console.error('Failed to fetch sync config:', error);
    }
  };

  // handleSaveSyncInterval removed — sync config managed via Settings.js component

  const getDaysAgo = (dateStr) => {
    if (!dateStr || dateStr === 'N/A' || dateStr === '0000') return 999;
    return (new Date() - new Date(dateStr)) / (1000 * 60 * 60 * 24);
  };
  const getTimeDiffText = (days) => (days === 999 ? 'N/A' : `${Math.floor(days)}d ago`);

  // 3-tier online status: 'connected' (green), 'recent' (yellow), 'offline' (grey)
  // - connected: ImmyBot ephemeral agent confirms device is online right now
  // - recent: checked in within 24h via ImmyBot or Datto RMM (probably online)
  // - offline: no recent activity
  const getDeviceOnlineStatus = (dev) => {
    // Tier 1: Real-time ImmyBot ephemeral agent status
    const immyId = dev.ids_map?.ImmyBot;
    if (immyId && immybotOnlineStatus[immyId] === true) return 'connected';

    // Tier 2: Checked in within 24h
    const ONLINE_THRESHOLD_HOURS = 24;
    const immyDays = getDaysAgo(dev.last_sync_immybot);
    const dattoDays = getDaysAgo(dev.last_sync_datto);
    if (immyDays < (ONLINE_THRESHOLD_HOURS / 24) || dattoDays < (ONLINE_THRESHOLD_HOURS / 24)) return 'recent';

    // Tier 3: Offline
    return 'offline';
  };

  // Backward-compatible wrapper: returns true if device is connected or recently active
  const isDeviceOnline = (dev) => getDeviceOnlineStatus(dev) !== 'offline';

  // Calculate device health with support for multiple primary RMMs and combined status
  const calculateDeviceHealth = (dev) => {
    // Helper to detect macOS from OS string (fallback when os_type not set)
    const detectMacOSFromString = (osString) => {
      if (!osString) return false;
      const lower = osString.toLowerCase();
      // Same patterns as backend detect_os_type for macOS
      const macTerms = ['macos', 'mac os', 'darwin', 'osx', 'os x', 'macintosh',
                        'imac', 'macbook', 'mac mini', 'mac pro', 'mac studio',
                        'macmdm'];
      if (macTerms.some(term => lower.includes(term))) return true;
      // Check for 'mac' at start (e.g., "Mac" standalone) but not 'machine', etc.
      if (lower.startsWith('mac') && (lower.length === 3 || !/[a-z]/.test(lower[3]))) return true;
      return false;
    };

    // Determine OS type with fallback detection from os_name/os_version
    const getDeviceOsType = () => {
      // First check backend-provided os_type
      if (dev.os_type && dev.os_type !== 'unknown') return dev.os_type;
      // Fallback: check os_name and os_version strings for macOS patterns
      if (detectMacOSFromString(dev.os_name)) return 'macos';
      if (detectMacOSFromString(dev.os_version)) return 'macos';
      // Default to windows
      return 'windows';
    };

    // Integration OS Support - which integrations support which OS
    const INTEGRATION_OS_SUPPORT = {
      'N-Central': { windows: true, macos: true, linux: false },
      'Datto RMM': { windows: true, macos: true, linux: true },
      'ImmyBot': { windows: true, macos: false, linux: false },
      'CloudRadial': { windows: true, macos: true, linux: true },
      'Huntress': { windows: true, macos: true, linux: false },
      'ConnectWise': { windows: true, macos: true, linux: true },
      'Microsoft': { windows: true, macos: true, linux: false },
    };

    // Helper to check if integration supports device OS
    const supportsDeviceOS = (integrationName, deviceOsType) => {
      const support = INTEGRATION_OS_SUPPORT[integrationName];
      if (!support) return true; // Unknown integration, assume supported
      if (deviceOsType === 'macos') return support.macos;
      if (deviceOsType === 'linux') return support.linux;
      return support.windows; // Default to windows
    };

    // Return structure for health details with priority
    // Priority: 'critical' (security), 'high' (PSA orphan, stale costs money), 'medium' (drift/missing), 'low' (healthy)
    const createHealthResult = (status, severity, issues, integrations, priority = 'low', issueTypes = []) => ({
      status,
      severity,
      issues: issues || [],
      integrations: integrations || {},
      priority,
      issueTypes: issueTypes || [] // Categorized issue types for smart filtering
    });

    if (dev.is_duplicate) {
      return createHealthResult("Duplicate", "duplicate", [], {});
    }

    // Archived devices (Microsoft) are considered "healthy" - intentionally disabled
    if (dev.microsoft_archived) {
      return createHealthResult("Archived", "healthy", [], {}, 'low', ['archived']);
    }

    // Find the master org for this device to get org-specific settings
    let deviceOrgSettings = null;
    if (dev.organization && orgMappings && orgMappings.length > 0) {
      const mapping = orgMappings.find(m =>
        m.sources && m.sources.some(s => s.source_org_name === dev.organization)
      );
      if (mapping && mapping.master_org_name) {
        deviceOrgSettings = orgSettings[mapping.master_org_name];
      }
    }

    // Default to all enabled if no org settings found
    const orgEnabled = {
      'N-Central': deviceOrgSettings?.ncentral_enabled !== false,
      'ConnectWise': deviceOrgSettings?.connectwise_enabled !== false,
      'Huntress': deviceOrgSettings?.huntress_enabled !== false,
      'ImmyBot': deviceOrgSettings?.immybot_enabled !== false,
      'CloudRadial': deviceOrgSettings?.cloudradial_enabled !== false,
      'Datto RMM': deviceOrgSettings?.datto_rmm_enabled !== false,
      'Microsoft': deviceOrgSettings?.microsoft_enabled !== false
    };

    // Check which sources the device is present in
    const sourceChecks = {
      'N-Central': dev.sources.includes("N-Central"),
      'ConnectWise': dev.sources.includes("ConnectWise"),
      'Huntress': dev.sources.includes("Huntress"),
      'ImmyBot': dev.sources.includes("ImmyBot"),
      'CloudRadial': dev.sources.includes("CloudRadial"),
      'Datto RMM': dev.sources.includes("Datto RMM"),
      'Microsoft': dev.sources.includes("Microsoft")
    };

    const sourceDates = {
      'N-Central': dev.last_sync_nable,
      'ConnectWise': dev.last_sync_connectwise,
      'Huntress': dev.last_sync_huntress,
      'ImmyBot': dev.last_sync_immybot,
      'CloudRadial': dev.last_sync_cloudradial,
      'Datto RMM': dev.last_sync_datto,
      'Microsoft': dev.last_sync_microsoft
    };

    // Build integration health details for icon display
    const integrations = {};
    const issues = [];

    // PRIMARY RMMs: Determined by per-org setting or global default
    // Per-org setting: 'global' (use both), 'ncentral' (N-Central only), 'datto' (Datto RMM only), 'microsoft' (Microsoft Intune only)
    const orgPrimaryRmm = deviceOrgSettings?.primary_rmm || 'global';
    let primaryRMMs;
    if (orgPrimaryRmm === 'ncentral') {
      primaryRMMs = ['N-Central'];
    } else if (orgPrimaryRmm === 'datto') {
      primaryRMMs = ['Datto RMM'];
    } else if (orgPrimaryRmm === 'microsoft') {
      primaryRMMs = ['Microsoft'];
    } else {
      // Global default: either N-Central OR Datto RMM is sufficient
      primaryRMMs = ['N-Central', 'Datto RMM'];
    }
    const enabledPrimaryRMMs = primaryRMMs.filter(rmm =>
      selectedSources.includes(rmm) && orgEnabled[rmm]
    );
    const hasPrimaryRMM = enabledPrimaryRMMs.some(rmm => sourceChecks[rmm]);

    // Calculate days for each source
    const getSourceDays = (source) => {
      const date = sourceDates[source];
      return date && date !== 'N/A' ? getDaysAgo(date) : 999;
    };

    // Build integration status for each source
    for (const source of ['N-Central', 'Datto RMM', 'ConnectWise', 'Huntress', 'ImmyBot', 'CloudRadial', 'Microsoft']) {
      const isSelected = selectedSources.includes(source);
      const isEnabled = orgEnabled[source];
      const isPresent = sourceChecks[source];
      const days = getSourceDays(source);
      const isPrimaryRMM = primaryRMMs.includes(source);

      let health = 'disabled'; // grey - not selected or not enabled for org
      if (isSelected && isEnabled) {
        if (!isPresent) {
          health = 'missing'; // grey with X
        } else if (source === 'ConnectWise') {
          // ConnectWise PSA health depends on primary RMM relationship:
          // - Active + has primary RMM = healthy (green dot) — expected state
          // - Active + NO primary RMM = unhealthy (red X) — PSA orphan, shouldn't be active
          // - Inactive + has primary RMM = unhealthy (drift — RMM active but PSA inactive)
          // - Inactive + NO primary RMM = inactive (grey dot — correctly offboarded)
          if (dev.cw_status === 'Active') {
            health = hasPrimaryRMM ? 'healthy' : 'unhealthy';
          } else {
            health = hasPrimaryRMM ? 'unhealthy' : 'inactive';
          }
        } else if (days > thresholds.stale_threshold_days) {
          health = 'stale'; // red
        } else if (days > thresholds.warning_threshold_days) {
          health = 'warning'; // yellow
        } else {
          health = 'healthy'; // green
        }
      }

      integrations[source] = {
        present: isPresent,
        enabled: isEnabled,
        selected: isSelected,
        days: days < 999 ? Math.floor(days) : null,
        health,
        isPrimaryRMM,
        cwStatus: source === 'ConnectWise' ? dev.cw_status : null
      };
    }

    // HEALTH CHECK HIERARCHY (collect all issues, determine highest severity)
    let highestSeverity = 'healthy';
    let priority = 'low';
    const issueTypes = []; // Categorized for smart filtering: 'psa_orphan', 'missing_security', 'stale', 'needs_attention', 'drift'

    // 1. Check for Offboarded status FIRST
    // If PSA is Inactive and no other integrations are present, device is successfully offboarded
    const osType = getDeviceOsType();
    const isMacOS = osType === 'macos';
    const macHealthEnabled = deviceOrgSettings?.mac_health_checks_enabled || false;

    // For macOS devices with mac_health_checks_enabled, override orgEnabled
    // to use mac_*_required settings instead of Windows-centric global settings
    if (isMacOS && macHealthEnabled) {
      orgEnabled['N-Central'] = deviceOrgSettings?.mac_ncentral_required || false;
      orgEnabled['Datto RMM'] = deviceOrgSettings?.mac_datto_rmm_required || false;
      orgEnabled['CloudRadial'] = deviceOrgSettings?.mac_cloudradial_required || false;
      orgEnabled['Huntress'] = deviceOrgSettings?.mac_huntress_required || false;
      orgEnabled['ConnectWise'] = deviceOrgSettings?.mac_connectwise_required || false;
      orgEnabled['Microsoft'] = deviceOrgSettings?.mac_microsoft_required || false;
      orgEnabled['ImmyBot'] = false; // ImmyBot is Windows-only, never required for macOS
    }

    // For macOS with mac health checks enabled, use macOS-specific requirements
    // Otherwise use standard Windows-centric checks
    const hasOtherIntegrations = enabledPrimaryRMMs.some(rmm => sourceChecks[rmm]) ||
      ['Huntress', 'ImmyBot', 'CloudRadial', 'Microsoft'].some(src =>
        selectedSources.includes(src) && orgEnabled[src] && sourceChecks[src] && supportsDeviceOS(src, osType)
      );

    // ConnectWise status helper: true if device is in CW but NOT Active (Inactive, null, etc.)
    const cwNotActive = sourceChecks['ConnectWise'] && dev.cw_status !== 'Active';

    if (cwNotActive && !hasOtherIntegrations) {
      // PSA not-Active with no other integrations = Successfully Offboarded
      // This catches cw_status of 'Inactive', null, undefined, etc.
      issues.push("Offboarded");
      highestSeverity = 'offboarded';
      issueTypes.push('offboarded');
    }
    // For macOS devices with mac_health_checks_enabled, skip Windows-centric PSA Orphan/No RMM checks
    // macOS devices have their own health check requirements configured in org settings
    else if (isMacOS && macHealthEnabled) {
      // macOS with health checks enabled - skip PSA Orphan / No Primary RMM checks
      // macOS health checks use mac_*_required settings (orgEnabled was overridden above)
      // Secondary integration checks below will use the overridden orgEnabled values
    }
    // 2. PSA ORPHAN: Active in PSA but missing from Primary RMM (high priority - leftover after offboard)
    // Only applies to Windows devices or macOS without mac_health_checks_enabled
    else if (enabledPrimaryRMMs.length > 0 && !hasPrimaryRMM && sourceChecks['ConnectWise'] && dev.cw_status === 'Active') {
      issues.push("PSA Orphan");
      highestSeverity = 'error';
      issueTypes.push('psa_orphan');
      priority = 'high'; // PSA orphans cost money/billing issues
    }
    // 3. ERROR: No Primary RMM at all (when primary RMMs are enabled)
    // Only applies to Windows devices or macOS without mac_health_checks_enabled
    else if (enabledPrimaryRMMs.length > 0 && !hasPrimaryRMM) {
      if (cwNotActive && hasOtherIntegrations) {
        // In PSA as not-Active but has other integrations = Incomplete offboard
        issues.push("Incomplete Offboard");
        highestSeverity = 'warning';
        issueTypes.push('incomplete_offboard');
        priority = 'medium';
      } else if (!isMacOS) {
        // Only flag "No Primary RMM" for Windows devices
        issues.push("No Primary RMM");
        highestSeverity = 'error';
        issueTypes.push('no_rmm');
        priority = 'high';
      }
    }

    // 4. Check Primary RMM staleness (high priority - costs money due to licensing)
    if (hasPrimaryRMM) {
      const primaryRMMDays = enabledPrimaryRMMs
        .filter(rmm => sourceChecks[rmm])
        .map(rmm => getSourceDays(rmm));
      const minPrimaryDays = Math.min(...primaryRMMDays);

      if (minPrimaryDays > thresholds.stale_threshold_days) {
        issues.push("Stale: RMM");
        highestSeverity = 'stale';
        issueTypes.push('stale');
        priority = 'high'; // Stale devices cost money (still licensed)
      }
    }

    // 4b. Check Microsoft staleness independently (when not primary RMM but enabled)
    // Microsoft is treated like an RMM for staleness purposes
    if (!primaryRMMs.includes('Microsoft') && selectedSources.includes('Microsoft') && orgEnabled['Microsoft'] && sourceChecks['Microsoft']) {
      const microsoftDays = getSourceDays('Microsoft');
      if (microsoftDays > thresholds.stale_threshold_days) {
        issues.push("Stale: Microsoft");
        issueTypes.push('stale');
        // If device has no primary RMM (e.g., only in Microsoft), Microsoft staleness should be the primary indicator
        // This allows Microsoft-only devices to appear in the Stale filter for cleanup/archiving
        if (highestSeverity === 'healthy' || (!hasPrimaryRMM && highestSeverity !== 'offboarded')) {
          highestSeverity = 'stale';
        }
        if (priority === 'low') priority = 'high'; // Stale Microsoft devices may still be licensed
      }
    }

    // 5. MISSING SECURITY: Has active RMM but missing Huntress (critical priority - security/compliance)
    if (hasPrimaryRMM && selectedSources.includes('Huntress') && orgEnabled['Huntress'] && !sourceChecks['Huntress']) {
      // Only flag as critical if RMM is not stale (active device without security)
      const primaryRMMDays = enabledPrimaryRMMs
        .filter(rmm => sourceChecks[rmm])
        .map(rmm => getSourceDays(rmm));
      const minPrimaryDays = Math.min(...primaryRMMDays);

      if (minPrimaryDays <= thresholds.stale_threshold_days) {
        issues.push("Missing: Security");
        issueTypes.push('missing_security');
        priority = 'critical'; // Active device without security = critical
        if (highestSeverity === 'healthy') highestSeverity = 'warning';
      }
    }

    // 6. PSA Checks (WARNING level) - only if we have a primary RMM
    if (hasPrimaryRMM && selectedSources.includes('ConnectWise') && orgEnabled['ConnectWise']) {
      if (!sourceChecks['ConnectWise']) {
        issues.push("Missing: PSA");
        issueTypes.push('needs_attention');
        if (highestSeverity === 'healthy') highestSeverity = 'warning';
        if (priority === 'low') priority = 'medium';
      } else if (dev.cw_status === 'Inactive') {
        // PSA Inactive but has active RMM = mismatch/drift
        issues.push("PSA Inactive");
        issueTypes.push('drift');
        issueTypes.push('needs_attention');
        if (highestSeverity === 'healthy') highestSeverity = 'warning';
        if (priority === 'low') priority = 'medium';
      }
    }

    // 7. Secondary integration checks (WARNING level)
    // Only check integrations that support the device's OS
    const secondaryIntegrations = ['Huntress', 'ImmyBot', 'CloudRadial'];
    const missingSecondary = [];
    const driftSecondary = [];
    // Use osType which already has fallback detection from os_name/os_version
    const deviceOsType = osType;

    for (const source of secondaryIntegrations) {
      // Skip integrations that don't support this device's OS
      if (!supportsDeviceOS(source, deviceOsType)) continue;

      if (selectedSources.includes(source) && orgEnabled[source]) {
        if (!sourceChecks[source]) {
          // Skip Huntress here as it's handled above with critical priority
          if (source !== 'Huntress') {
            missingSecondary.push(source);
          }
        } else {
          const days = getSourceDays(source);
          if (days > thresholds.stale_threshold_days) {
            driftSecondary.push(source);
          }
        }
      }
    }

    if (missingSecondary.length > 0) {
      issues.push(`Missing: ${missingSecondary.join(', ')}`);
      issueTypes.push('needs_attention');
      if (highestSeverity === 'healthy') highestSeverity = 'warning';
      if (priority === 'low') priority = 'medium';
    }

    if (driftSecondary.length > 0) {
      issues.push(`Drift: ${driftSecondary.join(', ')}`);
      issueTypes.push('drift');
      issueTypes.push('needs_attention');
      if (highestSeverity === 'healthy') highestSeverity = 'warning';
      if (priority === 'low') priority = 'medium';
    }

    // 8. No active sources at all
    const dateCheckSources = selectedSources.filter(src => src !== 'ConnectWise');
    const activeDates = dateCheckSources
      .filter(src => sourceChecks[src] && orgEnabled[src])
      .map(src => sourceDates[src])
      .filter(d => d && d !== 'N/A');

    if (activeDates.length === 0 && issues.length === 0) {
      issues.push("No Active Sources");
      highestSeverity = 'error';
      issueTypes.push('needs_attention');
      priority = 'high';
    }

    // Determine final status string
    let statusText = "Healthy";
    if (highestSeverity === 'offboarded') {
      statusText = "Offboarded";
    } else if (highestSeverity === 'error') {
      statusText = issues[0] || "Error";
    } else if (highestSeverity === 'stale') {
      statusText = "Stale";
    } else if (highestSeverity === 'warning') {
      statusText = issues.length > 1 ? `${issues.length} Issues` : (issues[0] || "Warning");
    }

    return createHealthResult(statusText, highestSeverity, issues, integrations, priority, [...new Set(issueTypes)]);
  };

  // toggleSource removed — source selection managed via Settings.js

  const processedData = useMemo(() => {
    // Helper to detect mobile OS from device
    const isMobileDevice = (d) => {
      // Check backend-provided os_type first
      if (d.os_type === 'android' || d.os_type === 'ios') return true;
      // Fallback: check os_name and os_version for mobile patterns
      const checkMobile = (str) => {
        if (!str) return false;
        const lower = str.toLowerCase();
        // iOS patterns
        if (['ios', 'iphone', 'ipad', 'ipados', 'ipod'].some(t => lower.includes(t))) return true;
        // Android patterns
        if (lower.includes('android')) return true;
        return false;
      };
      return checkMobile(d.os_name) || checkMobile(d.os_version);
    };

    const enriched = devices.map(d => {
      const healthResult = calculateDeviceHealth(d);
      return {
        ...d,
        computedStatus: healthResult.status,  // String for backward compat
        healthSeverity: healthResult.severity,
        healthIssues: healthResult.issues,
        healthIntegrations: healthResult.integrations,
        healthPriority: healthResult.priority, // 'critical', 'high', 'medium', 'low'
        healthIssueTypes: healthResult.issueTypes, // ['psa_orphan', 'missing_security', 'stale', 'needs_attention', 'drift']
        // Use health calculation result for offboarded status — it's the single source of truth
        // calculateDeviceHealth uses cwNotActive && !hasOtherIntegrations which is correct
        isOffboarded: healthResult.severity === 'offboarded'
      };
    });

    // Build organization filter lookup
    const selectedSourceOrgs = new Set();
    if (selectedOrgs.length > 0 && orgMappings && orgMappings.length > 0) {
      selectedOrgs.forEach(masterOrg => {
        const mappings = orgMappings.filter(m => m.master_org_name === masterOrg);
        if (mappings && mappings.length > 0) {
          mappings.forEach(mapping => {
            if (mapping.sources && Array.isArray(mapping.sources)) {
              mapping.sources.forEach(source => {
                if (source && source.source_org_name) {
                  selectedSourceOrgs.add(source.source_org_name);
                }
              });
            }
          });
        }
      });
    }

    // Build lookup for hidden orgs (map source org name to master org to check if hidden)
    const hiddenSourceOrgs = new Set();
    // Build lookup for orgs that hide mobile devices
    const hideMobileSourceOrgs = new Set();
    if (orgMappings && orgMappings.length > 0) {
      orgMappings.forEach(mapping => {
        const masterSettings = orgSettings[mapping.master_org_name];
        if (masterSettings?.is_hidden) {
          // Mark all source orgs under this master as hidden
          if (mapping.sources && Array.isArray(mapping.sources)) {
            mapping.sources.forEach(source => {
              if (source && source.source_org_name) {
                hiddenSourceOrgs.add(source.source_org_name);
              }
            });
          }
        }
        // Check if mobile devices should be hidden for this org (default to true)
        if (masterSettings?.hide_mobile_devices !== false) {
          if (mapping.sources && Array.isArray(mapping.sources)) {
            mapping.sources.forEach(source => {
              if (source && source.source_org_name) {
                hideMobileSourceOrgs.add(source.source_org_name);
              }
            });
          }
        }
      });
    }

    // Filter to only include devices that appear in at least one selected source
    // AND apply organization filter if active
    // AND exclude hidden organizations
    // AND exclude mobile devices for orgs with hide_mobile_devices enabled
    const filtered = enriched.filter(d => {
      // Exclude devices from hidden organizations
      if (d.organization && hiddenSourceOrgs.has(d.organization)) {
        return false;
      }

      // Exclude mobile devices (Android/iOS) if the org has hide_mobile_devices enabled
      if (isMobileDevice(d) && d.organization && hideMobileSourceOrgs.has(d.organization)) {
        return false;
      }

      // Apply organization filter first if active
      if (selectedOrgs.length > 0) {
        if (!d.organization || !selectedSourceOrgs.has(d.organization)) {
          return false;
        }
        // If org filter passes, include regardless of source
        return true;
      }
      // Otherwise, apply source filter
      return selectedSources.some(src => d.sources.includes(src));
    });

    // --- SPLIT DETECTION ---
    // Count how many times each hostname appears in the FILTERED list
    const hostCounts = {};
    filtered.forEach(d => {
        if (!d.is_duplicate && d.hostname !== 'UNKNOWN') {
            hostCounts[d.hostname] = (hostCounts[d.hostname] || 0) + 1;
        }
    });

    // Tag devices that are part of a split
    const final = filtered.map(d => ({
        ...d,
        isSplit: !d.is_duplicate && hostCounts[d.hostname] > 1
    }));

    const stats = {
        total: 0, healthy: 0, warning: 0, stale: 0, duplicate: 0, noOrganization: 0, incompleteOffboard: 0, offboarded: 0,
        archived: 0, // Microsoft archived devices
        missingCloudRadial: 0, missingNCentral: 0, missingNCentralOnline: 0, remediation: 0, remediationOnline: 0,
        // New smart filter stats (with online counts)
        psaOrphans: 0, psaOrphansOnline: 0,
        missingSecurity: 0, missingSecurityOnline: 0,
        needsAttention: 0, needsAttentionOnline: 0,
        staleOnline: 0,
        allIssues: 0, allIssuesOnline: 0,
        // Category filter stats
        psaIssues: 0,        // PSA/ConnectWise issues (orphans + missing/stale/unhealthy)
        serviceDelivery: 0,  // CloudRadial issues
        rmmIssues: 0,        // RMM issues (stale, missing, incomplete offboard)
        microsoftIssues: 0,  // Microsoft issues (stale, missing)
        // Priority breakdown
        critical: 0, high: 0, medium: 0,
        // Organization breakdown for landing dashboard
        orgBreakdown: {}
    };

    final.forEach(d => {
        // Count offboarded devices separately (they don't count toward other stats)
        if (d.isOffboarded && !d.is_duplicate) {
            stats.offboarded++;
            return; // Skip other stat calculations for offboarded devices
        }

        // Count archived Microsoft devices separately (they don't count toward health issues)
        if (d.microsoft_archived && !d.is_duplicate) {
            stats.archived++;
            stats.total++; // Still count in total but not in issues
            stats.healthy++; // Count archived as "healthy" since they're intentionally disabled
            return; // Skip other stat calculations for archived devices
        }

        if (d.is_duplicate) stats.duplicate++;
        else {
            stats.total++;
            // Use healthSeverity for more accurate categorization
            if (d.healthSeverity === "healthy") stats.healthy++;
            else if (d.healthSeverity === "stale") stats.stale++;
            else if (d.healthSeverity === "error" || d.healthSeverity === "warning") stats.warning++;
            else stats.warning++; // Fallback
        }

        // Count by issue types (for smart filters) - include online counts
        if (!d.is_duplicate && !d.isOffboarded && d.healthIssueTypes) {
            const deviceOnline = isDeviceOnline(d);
            if (d.healthIssueTypes.includes('psa_orphan')) {
                stats.psaOrphans++;
                if (deviceOnline) stats.psaOrphansOnline++;
            }
            if (d.healthIssueTypes.includes('missing_security')) {
                stats.missingSecurity++;
                if (deviceOnline) stats.missingSecurityOnline++;
            }
            if (d.healthIssueTypes.includes('needs_attention')) {
                stats.needsAttention++;
                if (deviceOnline) stats.needsAttentionOnline++;
            }
            if (d.healthIssueTypes.includes('stale') && deviceOnline) {
                stats.staleOnline++;
            }

            // Count by priority
            if (d.healthPriority === 'critical') stats.critical++;
            else if (d.healthPriority === 'high') stats.high++;
            else if (d.healthPriority === 'medium') stats.medium++;

            // All issues = any device with problems
            if (d.healthIssueTypes.length > 0) {
                stats.allIssues++;
                if (deviceOnline) stats.allIssuesOnline++;
            }

            // Category: PSA Issues (ConnectWise orphans + missing/stale/unhealthy)
            const cwHealth = d.healthIntegrations?.ConnectWise;
            const hasCWIssue = cwHealth && cwHealth.selected && cwHealth.enabled &&
                (cwHealth.health === 'missing' || cwHealth.health === 'stale' || cwHealth.health === 'warning' || cwHealth.health === 'unhealthy');
            if (d.healthIssueTypes.includes('psa_orphan') || hasCWIssue) {
                stats.psaIssues++;
            }

            // Category: Service Delivery (CloudRadial issues)
            const crHealth = d.healthIntegrations?.CloudRadial;
            if (crHealth && crHealth.selected && crHealth.enabled &&
                (crHealth.health === 'missing' || crHealth.health === 'stale' || crHealth.health === 'warning')) {
                stats.serviceDelivery++;
            }

            // Category: RMM Issues (N-Central, Datto RMM, ImmyBot — stale/missing/no_rmm/incomplete_offboard)
            const rmmSources = ['N-Central', 'Datto RMM', 'ImmyBot'];
            const hasRMMIssue = rmmSources.some(src => {
                const h = d.healthIntegrations?.[src];
                return h && h.selected && h.enabled && (h.health === 'missing' || h.health === 'stale' || h.health === 'warning');
            }) || d.healthIssueTypes.includes('no_rmm') || d.healthIssueTypes.includes('incomplete_offboard');
            if (hasRMMIssue) stats.rmmIssues++;

            // Category: Microsoft Issues (stale/missing)
            const msHealth = d.healthIntegrations?.Microsoft;
            if (msHealth && msHealth.selected && msHealth.enabled &&
                (msHealth.health === 'missing' || msHealth.health === 'stale' || msHealth.health === 'warning')) {
                stats.microsoftIssues++;
            }

            // Organization breakdown (for landing dashboard)
            if (d.organization && d.healthIssueTypes.length > 0) {
                // Find master org for this device
                let masterOrg = d.organization;
                if (orgMappings && orgMappings.length > 0) {
                    const mapping = orgMappings.find(m =>
                        m.sources && m.sources.some(s => s.source_org_name === d.organization)
                    );
                    if (mapping && mapping.master_org_name) {
                        masterOrg = mapping.master_org_name;
                    }
                }

                if (!stats.orgBreakdown[masterOrg]) {
                    stats.orgBreakdown[masterOrg] = {
                        total: 0, critical: 0, high: 0, medium: 0,
                        psaOrphans: 0, missingSecurity: 0, stale: 0, needsAttention: 0
                    };
                }
                stats.orgBreakdown[masterOrg].total++;
                if (d.healthPriority === 'critical') stats.orgBreakdown[masterOrg].critical++;
                else if (d.healthPriority === 'high') stats.orgBreakdown[masterOrg].high++;
                else if (d.healthPriority === 'medium') stats.orgBreakdown[masterOrg].medium++;

                if (d.healthIssueTypes.includes('psa_orphan')) stats.orgBreakdown[masterOrg].psaOrphans++;
                if (d.healthIssueTypes.includes('missing_security')) stats.orgBreakdown[masterOrg].missingSecurity++;
                if (d.healthIssueTypes.includes('stale')) stats.orgBreakdown[masterOrg].stale++;
                if (d.healthIssueTypes.includes('needs_attention')) stats.orgBreakdown[masterOrg].needsAttention++;
            }
        }

        // Count "No Organization" devices (ImmyBot or CloudRadial with no/invalid organization)
        const orgLower = (d.organization || '').toLowerCase();
        const isNoOrg = (
            (d.sources.includes('ImmyBot') && (!d.organization || d.organization === 'Unknown immy.bot Agents' || orgLower === 'placeholder')) ||
            (d.sources.includes('CloudRadial') && (!d.organization || d.organization === 'N/A' || orgLower === 'placeholder'))
        );
        if (isNoOrg && !d.is_duplicate) {
            stats.noOrganization++;
        }

        // Count "Missing CloudRadial" devices: has ImmyBot but NOT CloudRadial (only if CloudRadial is selected)
        const isMissingCR = selectedSources.includes('CloudRadial') && d.sources.includes('ImmyBot') && !d.sources.includes('CloudRadial');
        if (isMissingCR && !d.is_duplicate && !d.isOffboarded) {
            stats.missingCloudRadial++;
        }

        // Count "Missing N-Central" devices: has ImmyBot but NOT N-Central (only if N-Central is selected)
        const isMissingNC = selectedSources.includes('N-Central') && d.sources.includes('ImmyBot') && !d.sources.includes('N-Central');
        if (isMissingNC && !d.is_duplicate && !d.isOffboarded) {
            stats.missingNCentral++;
            // Also count how many are online (checked in within 24h via ImmyBot or Datto RMM)
            if (isDeviceOnline(d)) {
                stats.missingNCentralOnline++;
            }
        }

        // Count "Remediation" devices: has ImmyBot but missing N-Central OR CloudRadial (or both) - respects selected sources
        if ((isMissingNC || isMissingCR) && !d.is_duplicate && !d.isOffboarded) {
            stats.remediation++;
            if (isDeviceOnline(d)) {
                stats.remediationOnline++;
            }
        }

        // Count "Incomplete Offboard" devices: has valid organization, stale on ImmyBot or CloudRadial, and no N-Central
        const hasValidOrg = d.organization &&
                           d.organization !== 'Unknown immy.bot Agents' &&
                           d.organization !== 'N/A';
        const hasNoNCentral = !d.sources.includes('N-Central');
        const isStaleImmyOrCR = (d.sources.includes('ImmyBot') || d.sources.includes('CloudRadial')) &&
                                d.healthSeverity === 'stale';

        if (hasValidOrg && hasNoNCentral && isStaleImmyOrCR && !d.is_duplicate) {
            stats.incompleteOffboard++;
        }
    });
    return { enriched: final, stats };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices, selectedSources, selectedOrgs, orgMappings, thresholds, orgSettings]);

  const finalDisplayData = useMemo(() => {
    // Build a lookup of all source org names that belong to each selected master org
    const selectedSourceOrgs = new Set();
    if (selectedOrgs.length > 0 && orgMappings && orgMappings.length > 0) {
      selectedOrgs.forEach(masterOrg => {
        // Find all mappings for this master org
        const mappings = orgMappings.filter(m => m.master_org_name === masterOrg);
        if (mappings && mappings.length > 0) {
          mappings.forEach(mapping => {
            // Add all source org names from this mapping's sources
            if (mapping.sources && Array.isArray(mapping.sources)) {
              mapping.sources.forEach(source => {
                if (source && source.source_org_name) {
                  selectedSourceOrgs.add(source.source_org_name);
                }
              });
            }
          });
        }
      });
    }

    let result = processedData.enriched.filter(dev => {
        // Apply filter first
        let passesFilter = false;

        // Special case: "Offboarded" filter shows ONLY offboarded devices
        if (activeFilter === 'Offboarded') {
            passesFilter = dev.isOffboarded && !dev.is_duplicate;
        } else if (activeFilter === 'Archived') {
            // Archived filter shows ONLY Microsoft archived devices
            passesFilter = dev.microsoft_archived && !dev.is_duplicate;
        } else if (activeFilter === 'Duplicate') {
            passesFilter = dev.is_duplicate;
        } else if (activeFilter === 'Stale') {
            // Exclude offboarded and archived from stale view - use healthSeverity for accurate match
            // Also includes No Organization devices (ImmyBot/CloudRadial with missing org)
            const devOrgLower = (dev.organization || '').toLowerCase();
            const isNoOrg = (
                (dev.sources.includes('ImmyBot') && (!dev.organization || dev.organization === 'Unknown immy.bot Agents' || devOrgLower === 'placeholder')) ||
                (dev.sources.includes('CloudRadial') && (!dev.organization || dev.organization === 'N/A' || devOrgLower === 'placeholder'))
            );
            passesFilter = !dev.is_duplicate && !dev.isOffboarded && !dev.microsoft_archived && (dev.healthSeverity === "stale" || isNoOrg);
        } else if (activeFilter === 'Healthy') {
            // Exclude offboarded and archived from healthy view (archived shown in Archived filter)
            passesFilter = !dev.is_duplicate && !dev.isOffboarded && !dev.microsoft_archived && dev.healthSeverity === "healthy";
        } else if (activeFilter === 'Warning') {
            // Exclude offboarded and archived from warning view - includes warning and error severities
            passesFilter = !dev.is_duplicate && !dev.isOffboarded && !dev.microsoft_archived && (dev.healthSeverity === "warning" || dev.healthSeverity === "error");
        } else if (activeFilter === 'No Organization') {
            // No Organization devices: ImmyBot or CloudRadial with no/invalid organization or placeholder
            const devOrgLower = (dev.organization || '').toLowerCase();
            const isNoOrg = (
                (dev.sources.includes('ImmyBot') && (!dev.organization || dev.organization === 'Unknown immy.bot Agents' || devOrgLower === 'placeholder')) ||
                (dev.sources.includes('CloudRadial') && (!dev.organization || dev.organization === 'N/A' || devOrgLower === 'placeholder'))
            );
            passesFilter = !dev.is_duplicate && !dev.isOffboarded && !dev.microsoft_archived && isNoOrg;
        } else if (activeFilter === 'Incomplete Offboard') {
            // Incomplete Offboard: has valid org, stale ImmyBot/CloudRadial, no N-Central
            const hasValidOrg = dev.organization &&
                               dev.organization !== 'Unknown immy.bot Agents' &&
                               dev.organization !== 'N/A';
            const hasNoNCentral = !dev.sources.includes('N-Central');
            const isStaleImmyOrCR = (dev.sources.includes('ImmyBot') || dev.sources.includes('CloudRadial')) &&
                                    dev.healthSeverity === 'stale';
            passesFilter = !dev.is_duplicate && !dev.isOffboarded && !dev.microsoft_archived && hasValidOrg && hasNoNCentral && isStaleImmyOrCR;
        } else if (activeFilter === 'Remediation') {
            // Remediation: has ImmyBot but missing N-Central OR CloudRadial (or both) - respects selected sources
            const isMissingNC = selectedSources.includes('N-Central') && dev.sources.includes('ImmyBot') && !dev.sources.includes('N-Central');
            const isMissingCR = selectedSources.includes('CloudRadial') && dev.sources.includes('ImmyBot') && !dev.sources.includes('CloudRadial');
            passesFilter = !dev.is_duplicate && !dev.isOffboarded && !dev.microsoft_archived && (isMissingNC || isMissingCR);
        } else if (activeFilter === 'PSA Orphans') {
            // PSA Orphans: Active in PSA but missing from Primary RMM
            passesFilter = !dev.is_duplicate && !dev.isOffboarded && !dev.microsoft_archived && dev.healthIssueTypes?.includes('psa_orphan');
        } else if (activeFilter === 'Missing Security') {
            // Missing Security: Active RMM but missing Huntress
            passesFilter = !dev.is_duplicate && !dev.isOffboarded && !dev.microsoft_archived && dev.healthIssueTypes?.includes('missing_security');
        } else if (activeFilter === 'Needs Attention') {
            // Needs Attention: Drift or missing required integrations
            passesFilter = !dev.is_duplicate && !dev.isOffboarded && !dev.microsoft_archived && dev.healthIssueTypes?.includes('needs_attention');
        } else if (activeFilter === 'All Issues') {
            // All Issues: Any device with problems
            passesFilter = !dev.is_duplicate && !dev.isOffboarded && !dev.microsoft_archived && dev.healthIssueTypes?.length > 0;
        } else if (activeFilter === 'Security Issues') {
            // Security Issues: EDR/Huntress issues
            passesFilter = !dev.is_duplicate && !dev.isOffboarded && !dev.microsoft_archived && dev.healthIssueTypes?.includes('missing_security');
        } else if (activeFilter === 'PSA Issues') {
            // PSA Issues: PSA orphans, missing/stale/unhealthy PSA (ConnectWise)
            const cwHealth = dev.healthIntegrations?.ConnectWise;
            const hasCWIssue = cwHealth && cwHealth.selected && cwHealth.enabled &&
                (cwHealth.health === 'missing' || cwHealth.health === 'stale' || cwHealth.health === 'warning' || cwHealth.health === 'unhealthy');
            passesFilter = !dev.is_duplicate && !dev.isOffboarded && !dev.microsoft_archived &&
                (dev.healthIssueTypes?.includes('psa_orphan') || hasCWIssue);
        } else if (activeFilter === 'Service Delivery') {
            // Service Delivery: CloudRadial issues (missing, stale, warning)
            const crHealth = dev.healthIntegrations?.CloudRadial;
            passesFilter = !dev.is_duplicate && !dev.isOffboarded && !dev.microsoft_archived &&
                crHealth && crHealth.selected && crHealth.enabled &&
                (crHealth.health === 'missing' || crHealth.health === 'stale' || crHealth.health === 'warning');
        } else if (activeFilter === 'RMM Issues') {
            // RMM Issues: N-Central, Datto RMM, ImmyBot — stale/missing/no_rmm/incomplete_offboard
            const rmmSources = ['N-Central', 'Datto RMM', 'ImmyBot'];
            const hasRMMIssue = rmmSources.some(src => {
                const h = dev.healthIntegrations?.[src];
                return h && h.selected && h.enabled && (h.health === 'missing' || h.health === 'stale' || h.health === 'warning');
            }) || dev.healthIssueTypes?.includes('no_rmm') || dev.healthIssueTypes?.includes('incomplete_offboard');
            passesFilter = !dev.is_duplicate && !dev.isOffboarded && !dev.microsoft_archived && hasRMMIssue;
        } else if (activeFilter === 'Microsoft Issues') {
            // Microsoft Issues: Microsoft stale/missing
            const msHealth = dev.healthIntegrations?.Microsoft;
            passesFilter = !dev.is_duplicate && !dev.isOffboarded && !dev.microsoft_archived &&
                msHealth && msHealth.selected && msHealth.enabled &&
                (msHealth.health === 'missing' || msHealth.health === 'stale' || msHealth.health === 'warning');
        } else {
            // "All" filter - show all non-duplicates, EXCLUDING offboarded and archived by default
            passesFilter = !dev.is_duplicate && !dev.isOffboarded && !dev.microsoft_archived;
        }

        if (!passesFilter) return false;

        // Apply organization filter - check if device's org is in any of the source orgs for selected masters
        if (selectedOrgs.length > 0) {
            if (!dev.organization || !selectedSourceOrgs.has(dev.organization)) {
                return false;
            }
        }

        // Then apply search term (searches hostname, serial, and organization)
        if (searchTerm) {
            const search = searchTerm.toLowerCase();
            const hostnameMatch = dev.hostname?.toLowerCase().includes(search);
            const serialMatch = dev.serial?.toLowerCase().includes(search);
            const orgMatch = dev.organization?.toLowerCase().includes(search);
            return hostnameMatch || serialMatch || orgMatch;
        }

        return true;
    });
    // Issue filters with different sorting strategies
    const onlineFirstFilters = ['Missing Security', 'Security Issues', 'Needs Attention', 'Remediation', 'All Issues', 'Service Delivery', 'RMM Issues', 'Microsoft Issues']; // Online matters for remediation
    const oldestFirstFilters = ['PSA Orphans', 'PSA Issues', 'Stale', 'Incomplete Offboard']; // Just sort by oldest, online doesn't matter

    // Helper to get primary RMM days for sorting
    const getPrimaryRMMDays = (dev) => {
      const ncDays = getDaysAgo(dev.last_sync_nable);
      const dattoDays = getDaysAgo(dev.last_sync_datto);
      const immyDays = getDaysAgo(dev.last_sync_immybot);
      const days = [ncDays, dattoDays, immyDays].filter(d => d !== Infinity);
      return days.length > 0 ? Math.min(...days) : Infinity;
    };

    if (onlineFirstFilters.includes(activeFilter) && sortConfig.key === 'global_health_status') {
      // Online first, then by oldest check-in (for filters where you can remediate online devices)
      result.sort((a, b) => {
        const aOnline = isDeviceOnline(a) ? 0 : 1;
        const bOnline = isDeviceOnline(b) ? 0 : 1;
        if (aOnline !== bOnline) return aOnline - bOnline;

        const aDays = getPrimaryRMMDays(a);
        const bDays = getPrimaryRMMDays(b);
        if (aDays === Infinity && bDays !== Infinity) return 1;
        if (bDays === Infinity && aDays !== Infinity) return -1;
        return bDays - aDays; // Oldest first
      });
    } else if (oldestFirstFilters.includes(activeFilter) && sortConfig.key === 'global_health_status') {
      // Just sort by oldest check-in (online status visible but doesn't affect sort)
      result.sort((a, b) => {
        const aDays = getPrimaryRMMDays(a);
        const bDays = getPrimaryRMMDays(b);
        if (aDays === Infinity && bDays !== Infinity) return 1;
        if (bDays === Infinity && aDays !== Infinity) return -1;
        return bDays - aDays; // Oldest first
      });
    } else if (sortConfig.key) {
      result.sort((a, b) => {
        let valA, valB;
        if (['ncentral', 'immy', 'cloudradial', 'huntress', 'datto', 'microsoft'].includes(sortConfig.key)) {
            const dateMap = {
                'ncentral': [a.last_sync_nable, b.last_sync_nable],
                'immy': [a.last_sync_immybot, b.last_sync_immybot],
                'huntress': [a.last_sync_huntress, b.last_sync_huntress],
                'cloudradial': [a.last_sync_cloudradial, b.last_sync_cloudradial],
                'datto': [a.last_sync_datto, b.last_sync_datto],
                'microsoft': [a.last_sync_microsoft, b.last_sync_microsoft]
            };
            const [dateA, dateB] = dateMap[sortConfig.key];
            valA = getDaysAgo(dateA); valB = getDaysAgo(dateB);
        } else if (sortConfig.key === 'connectwise') {
            // Sort ConnectWise by status: Active first, then Inactive, then missing
            const statusOrder = { 'Active': 0, 'Inactive': 1 };
            valA = a.cw_status ? (statusOrder[a.cw_status] ?? 2) : 3;
            valB = b.cw_status ? (statusOrder[b.cw_status] ?? 2) : 3;
        } else if (sortConfig.key === 'status') { valA = a.computedStatus; valB = b.computedStatus;
        } else if (sortConfig.key === 'priority') {
            // Sort by priority: critical > high > medium > low
            const priorityOrder = { 'critical': 0, 'high': 1, 'medium': 2, 'low': 3, undefined: 4 };
            valA = priorityOrder[a.healthPriority] ?? 4;
            valB = priorityOrder[b.healthPriority] ?? 4;
        } else { valA = a[sortConfig.key] || ''; valB = b[sortConfig.key] || ''; }
        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processedData, activeFilter, searchTerm, sortConfig, selectedOrgs, orgMappings]);

  // Count archived devices matching search term (for "in archive" indicator)
  const archivedSearchCount = useMemo(() => { // eslint-disable-line no-unused-vars
    if (!searchTerm || activeFilter === 'Archived' || !processedData?.enriched) return 0;
    const search = searchTerm.toLowerCase();
    return processedData.enriched.filter(dev => {
      if (!dev.microsoft_archived || dev.is_duplicate) return false;
      const hostnameMatch = dev.hostname?.toLowerCase().includes(search);
      const serialMatch = dev.serial?.toLowerCase().includes(search);
      const orgMatch = dev.organization?.toLowerCase().includes(search);
      return hostnameMatch || serialMatch || orgMatch;
    }).length;
  }, [searchTerm, activeFilter, processedData]);

  useEffect(() => { setCurrentPage(1); }, [activeFilter, searchTerm, selectedOrgs, sortConfig]);

  // Clear duplicate panel state when device panel closes
  useEffect(() => {
    if (!selectedDevice) {
      setDuplicateGroupData(null);
      setDuplicateGroupLoading(false);
      setSelectedDuplicateIds(new Set());
    }
  }, [selectedDevice]);

  const paginatedDevices = useMemo(() => {
    if (itemsPerPage === 'All') return finalDisplayData;
    const startIndex = (currentPage - 1) * itemsPerPage;
    return finalDisplayData.slice(startIndex, startIndex + itemsPerPage);
  }, [finalDisplayData, currentPage, itemsPerPage]);

  const totalPages = itemsPerPage === 'All' ? 1 : Math.ceil(finalDisplayData.length / itemsPerPage);
  const requestSort = (key) => { let direction = 'asc'; if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc'; setSortConfig({ key, direction }); };
  const getSortIcon = (key) => sortConfig.key !== key ? '⇅' : (sortConfig.direction === 'asc' ? '▲' : '▼');

  const fetchDeviceHistory = async (hostname) => {
    try {
      const response = await apiFetch(`${API_URL}/device-history/${encodeURIComponent(hostname)}`);
      if (response.ok) {
        const data = await response.json();
        setDeviceHistory(data);
      }
    } catch (error) {
      console.error('Failed to fetch device history:', error);
    }
  };

  // Fetch duplicate group data for a device (only calls API if device is a duplicate)
  const fetchDuplicateGroup = async (device) => {
    if (!device.is_duplicate || !device.organization) {
      setDuplicateGroupData(null);
      return;
    }
    setDuplicateGroupLoading(true);
    setDuplicateGroupData(null);
    setSelectedDuplicateIds(new Set());
    try {
      const response = await apiFetch(`${API_URL}/microsoft/duplicates/scan?organization=${encodeURIComponent(device.organization)}`);
      const data = await response.json();
      if (data.status === 'success') {
        // Find the group matching this device's hostname
        for (const org of data.organizations || []) {
          for (const group of org.groups || []) {
            if (group.hostname?.toLowerCase() === device.hostname?.toLowerCase()) {
              setDuplicateGroupData({
                ...group,
                organization_name: org.organization_name,
                stale_threshold_days: data.summary?.stale_threshold_days
              });
              setDuplicateGroupLoading(false);
              return;
            }
          }
        }
        // No matching group found
        setDuplicateGroupData(null);
      }
    } catch (error) {
      console.error('Error fetching duplicate group:', error);
      setDuplicateGroupData(null);
    } finally {
      setDuplicateGroupLoading(false);
    }
  };

  // Handle duplicate cleanup from device panel
  const handleDuplicateCleanup = async () => {
    if (selectedDuplicateIds.size === 0) {
      showToast('Please select at least one duplicate to clean up', { type: 'warning' });
      return;
    }

    const idsToDelete = Array.from(selectedDuplicateIds);
    if (!window.confirm(`Clean up ${idsToDelete.length} duplicate record(s)?\n\nThis will delete stale Intune records and archive Entra device objects. A PSA ticket will be created automatically.`)) return;

    try {
      const response = await apiFetch(`${API_URL}/microsoft/duplicates/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          unique_ids: idsToDelete,
          reason: 'Duplicate device cleanup from device panel'
        })
      });
      const data = await response.json();

      if (data.ticket_error) {
        showToast('PSA Ticket Creation Failed — cleanup aborted', { type: 'error' });
        return;
      }

      if (data.success_count > 0) {
        let message = `Cleaned up ${data.success_count} of ${idsToDelete.length} duplicate(s).`;
        if (data.results) {
          const archived = data.results.filter(r => r.entra_archived).length;
          const skipped = data.results.filter(r => r.entra_skipped).length;
          if (archived > 0) message += ` ${archived} Entra object(s) archived.`;
          if (skipped > 0) message += ` ${skipped} Entra archive(s) skipped (safety check).`;
        }
        const subtitle = data.ticket_id ? `PSA Ticket #${data.ticket_id} created.` : undefined;
        showToast(message, { type: 'success', subtitle });

        // Close panel and refresh
        setSelectedDevice(null);
        fetchDevices();
      } else {
        showToast(`Cleanup failed: ${data.message || 'Unknown error'}`, { type: 'error' });
      }
    } catch (error) {
      console.error('Error cleaning up duplicates:', error);
      showToast('Error cleaning up duplicates: ' + error.message, { type: 'error' });
    }
  };

  // Fetch real-time ImmyBot online status for visible devices via ephemeral agent API
  const fetchImmybotOnlineStatus = useCallback(async (devices) => {
    // Extract ImmyBot computer IDs from visible devices
    const immyIds = devices
      .filter(d => d.ids_map?.ImmyBot)
      .map(d => d.ids_map.ImmyBot)
      .filter((id, idx, arr) => arr.indexOf(id) === idx); // deduplicate

    if (immyIds.length === 0) return;

    setImmybotStatusLoading(true);
    try {
      const response = await apiFetch(
        `${API_URL}/immybot/online-status?computer_ids=${immyIds.join(',')}`
      );
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success') {
          setImmybotOnlineStatus(prev => ({ ...prev, ...data.online_status }));
        }
      }
    } catch (error) {
      console.error('Error fetching ImmyBot online status:', error);
    } finally {
      setImmybotStatusLoading(false);
    }
  }, [apiFetch, API_URL]);

  // renderTimeCell / renderIntegrationCell removed — v6.0 uses inline integration status dots

  // === NAVIGATION HELPERS ===
  // Handle stat card filter clicks — set filter and scroll to device table
  const handleFilterClick = useCallback((filter) => {
    setActiveFilter(filter);
    if (filter !== 'All') {
      // Scroll to device table after React re-renders it
      setTimeout(() => {
        deviceTableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  }, []);

  const navigateTo = useCallback((view, context = null) => {
    // Determine transition direction: forward (deeper) or back (shallower)
    const depths = { fleet: 0, integration: 1, org: 1, settings: 0 };
    setCurrentView(prev => {
      const prevDepth = depths[prev] || 0;
      const nextDepth = depths[view] || 0;
      // Check prefers-reduced-motion
      const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
      if (!prefersReducedMotion) {
        setTransitionDir(nextDepth > prevDepth ? 'forward' : nextDepth < prevDepth ? 'back' : 'none');
        // Clear transition after animation completes
        setTimeout(() => setTransitionDir('none'), 350);
      }
      return view;
    });
    setDrillDownContext(context);
    setSelectedDevices(new Set());
    setSearchTerm('');
    if (view === 'fleet') {
      setActiveFilter('All');
      setSelectedOrgs([]);
      setDrillDownTab('issues');
    }
    if (view === 'integration' && context) {
      setDrillDownTab('issues');
    }
    if (view === 'org' && context) {
      setDrillDownTab('issues');
      setSelectedOrgs([context.name]);
    }
    setCurrentPage(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // === COMPUTED DATA FOR NEW VIEWS ===
  const integrationBreakdown = useMemo(() => {
    if (!processedData?.enriched) return {};
    const sources = ['N-Central', 'ConnectWise', 'Huntress', 'ImmyBot', 'CloudRadial', 'Datto RMM', 'Microsoft'];
    const breakdown = {};
    sources.forEach(src => {
      breakdown[src] = { total: 0, healthy: 0, issues: 0, stale: 0, missing: 0, devices: [] };
    });
    processedData.enriched.forEach(d => {
      if (d.is_duplicate || d.isOffboarded || d.microsoft_archived) return;
      sources.forEach(src => {
        if (!selectedSources.includes(src)) return;
        const intHealth = d.healthIntegrations?.[src];
        if (!intHealth || !intHealth.selected || !intHealth.enabled) return;
        breakdown[src].total++;
        if (intHealth.health === 'healthy') breakdown[src].healthy++;
        else if (intHealth.health === 'missing') { breakdown[src].missing++; breakdown[src].issues++; }
        else if (intHealth.health === 'stale') { breakdown[src].stale++; breakdown[src].issues++; }
        else if (intHealth.health === 'warning' || intHealth.health === 'unhealthy') { breakdown[src].issues++; }
      });
    });
    return breakdown;
  }, [processedData, selectedSources]);

  const orgHealthBreakdown = useMemo(() => {
    if (!processedData?.enriched || !allOrganizations) return [];
    const orgMap = {};
    processedData.enriched.forEach(d => {
      if (d.is_duplicate || d.isOffboarded) return;
      let masterOrg = d.organization;
      if (orgMappings && orgMappings.length > 0) {
        const mapping = orgMappings.find(m =>
          m.sources && m.sources.some(s => s.source_org_name === d.organization)
        );
        if (mapping && mapping.master_org_name) masterOrg = mapping.master_org_name;
      }
      if (!masterOrg) return;
      if (!orgMap[masterOrg]) orgMap[masterOrg] = { name: masterOrg, total: 0, healthy: 0, issues: 0, critical: 0, high: 0, medium: 0, issueTypes: {} };
      orgMap[masterOrg].total++;
      if (d.healthSeverity === 'healthy' || d.microsoft_archived) orgMap[masterOrg].healthy++;
      if (d.healthIssueTypes && d.healthIssueTypes.length > 0 && !d.microsoft_archived) {
        orgMap[masterOrg].issues++;
        if (d.healthPriority === 'critical') orgMap[masterOrg].critical++;
        else if (d.healthPriority === 'high') orgMap[masterOrg].high++;
        else if (d.healthPriority === 'medium') orgMap[masterOrg].medium++;
        d.healthIssueTypes.forEach(t => {
          orgMap[masterOrg].issueTypes[t] = (orgMap[masterOrg].issueTypes[t] || 0) + 1;
        });
      }
    });
    // Compute health percentage and sort by worst health
    return Object.values(orgMap)
      .map(org => ({ ...org, healthPct: org.total > 0 ? Math.round((org.healthy / org.total) * 100) : 100 }))
      .filter(org => org.total > 0)
      .sort((a, b) => a.healthPct - b.healthPct);
  }, [processedData, allOrganizations, orgMappings]);

  // Filtered devices for drill-down views
  const drillDownDevices = useMemo(() => {
    if (!drillDownContext || !processedData?.enriched) return [];
    let filtered = processedData.enriched.filter(d => !d.is_duplicate && !d.isOffboarded && !d.microsoft_archived);

    if (drillDownContext.type === 'integration') {
      const src = drillDownContext.name;
      if (drillDownTab === 'issues') {
        filtered = filtered.filter(d => {
          const intHealth = d.healthIntegrations?.[src];
          return intHealth && intHealth.selected && intHealth.enabled && (intHealth.health === 'missing' || intHealth.health === 'stale' || intHealth.health === 'warning' || intHealth.health === 'unhealthy');
        });
      } else if (drillDownTab === 'stale') {
        filtered = filtered.filter(d => {
          const intHealth = d.healthIntegrations?.[src];
          return intHealth && intHealth.selected && intHealth.enabled && intHealth.health === 'stale';
        });
      } else {
        // 'all' - show all devices that have this integration enabled
        filtered = filtered.filter(d => {
          const intHealth = d.healthIntegrations?.[src];
          return intHealth && intHealth.selected && intHealth.enabled;
        });
      }
    } else if (drillDownContext.type === 'org') {
      const orgName = drillDownContext.name;
      // Find all source org names for this master org
      const sourceOrgNames = new Set();
      if (orgMappings && orgMappings.length > 0) {
        const mappings = orgMappings.filter(m => m.master_org_name === orgName);
        mappings.forEach(mapping => {
          if (mapping.sources && Array.isArray(mapping.sources)) {
            mapping.sources.forEach(source => {
              if (source?.source_org_name) sourceOrgNames.add(source.source_org_name);
            });
          }
        });
      }
      // For offboarded/archived/duplicates tabs, start from ALL devices in this org (not pre-filtered)
      if (drillDownTab === 'offboarded' || drillDownTab === 'archived' || drillDownTab === 'duplicates') {
        filtered = processedData.enriched.filter(d => d.organization && sourceOrgNames.has(d.organization));
        if (drillDownTab === 'offboarded') {
          filtered = filtered.filter(d => d.isOffboarded && !d.is_duplicate);
        } else if (drillDownTab === 'archived') {
          filtered = filtered.filter(d => d.microsoft_archived && !d.is_duplicate);
        } else if (drillDownTab === 'duplicates') {
          filtered = filtered.filter(d => d.is_duplicate);
        }
      } else {
        filtered = filtered.filter(d => d.organization && sourceOrgNames.has(d.organization));
        if (drillDownTab === 'issues') {
          filtered = filtered.filter(d => d.healthIssueTypes && d.healthIssueTypes.length > 0);
        } else if (drillDownTab === 'stale') {
          filtered = filtered.filter(d => d.healthSeverity === 'stale');
        }
      }
    }

    // Apply search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(d =>
        d.hostname?.toLowerCase().includes(search) ||
        d.serial?.toLowerCase().includes(search) ||
        d.organization?.toLowerCase().includes(search)
      );
    }

    // Sort by priority then online status
    filtered.sort((a, b) => {
      const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
      const pA = priorityOrder[a.healthPriority] ?? 3;
      const pB = priorityOrder[b.healthPriority] ?? 3;
      if (pA !== pB) return pA - pB;
      const aOnline = isDeviceOnline(a) ? 0 : 1;
      const bOnline = isDeviceOnline(b) ? 0 : 1;
      return aOnline - bOnline;
    });

    return filtered;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drillDownContext, drillDownTab, processedData, searchTerm, orgMappings]);

  // Resolve selected device keys to device objects across all visible data sources
  // (paginatedDevices only covers the current page; drill-down views use drillDownDevices)
  const resolveSelectedDevices = useCallback((keys) => {
    const pool = (currentView === 'integration' || currentView === 'org')
      ? drillDownDevices
      : finalDisplayData;
    return Array.from(keys)
      .map(key => pool.find(d => `${d.hostname}-${d.serial}` === key))
      .filter(Boolean);
  }, [currentView, drillDownDevices, finalDisplayData]);

  // Fetch ImmyBot online status when visible devices change (debounced 500ms)
  useEffect(() => {
    if (immybotOnlineStatusRef.current) {
      clearTimeout(immybotOnlineStatusRef.current);
    }

    // Determine which device list is currently visible
    const visibleDevices = (currentView === 'integration' || currentView === 'org')
      ? drillDownDevices
      : paginatedDevices;

    if (!visibleDevices || visibleDevices.length === 0) return;

    immybotOnlineStatusRef.current = setTimeout(() => {
      fetchImmybotOnlineStatus(visibleDevices);
    }, 500);

    return () => {
      if (immybotOnlineStatusRef.current) {
        clearTimeout(immybotOnlineStatusRef.current);
      }
    };
  }, [paginatedDevices, drillDownDevices, currentView, fetchImmybotOnlineStatus]);

  // Integration issue breakdown for org drill-down
  const orgIntegrationIssues = useMemo(() => {
    if (!drillDownContext || drillDownContext.type !== 'org' || !processedData?.enriched) return [];
    const orgName = drillDownContext.name;
    const sourceOrgNames = new Set();
    if (orgMappings && orgMappings.length > 0) {
      const mappings = orgMappings.filter(m => m.master_org_name === orgName);
      mappings.forEach(mapping => {
        if (mapping.sources && Array.isArray(mapping.sources)) {
          mapping.sources.forEach(source => {
            if (source?.source_org_name) sourceOrgNames.add(source.source_org_name);
          });
        }
      });
    }
    const orgDevices = processedData.enriched.filter(d =>
      !d.is_duplicate && !d.isOffboarded && !d.microsoft_archived &&
      d.organization && sourceOrgNames.has(d.organization)
    );

    const sources = ['N-Central', 'Huntress', 'ImmyBot', 'CloudRadial', 'Datto RMM', 'ConnectWise', 'Microsoft'];
    const issues = [];
    sources.forEach(src => {
      if (!selectedSources.includes(src)) return;
      const missing = orgDevices.filter(d => {
        const ih = d.healthIntegrations?.[src];
        return ih && ih.selected && ih.enabled && ih.health === 'missing';
      });
      const stale = orgDevices.filter(d => {
        const ih = d.healthIntegrations?.[src];
        return ih && ih.selected && ih.enabled && ih.health === 'stale';
      });
      const total = orgDevices.filter(d => {
        const ih = d.healthIntegrations?.[src];
        return ih && ih.selected && ih.enabled;
      });
      const healthy = total.length - missing.length - stale.length;
      if (missing.length > 0 || stale.length > 0) {
        issues.push({ source: src, missing: missing.length, stale: stale.length, healthy, total: total.length, devices: [...missing, ...stale] });
      }
    });
    return issues.sort((a, b) => (b.missing + b.stale) - (a.missing + a.stale));
  }, [drillDownContext, processedData, orgMappings, selectedSources]);

  // Command palette search results
  const commandPaletteResults = useMemo(() => {
    if (!showCommandPalette) return [];
    const q = commandQuery.toLowerCase().trim();
    const results = [];

    // Actions (always shown when no query, or matching query)
    const actions = [
      { id: 'action-sync', label: 'Sync All Sources', icon: '\u21BB', badge: 'Action', type: 'action', action: () => handleSync() },
      { id: 'action-export', label: 'Export CSV', icon: '\u21E9', badge: 'Action', type: 'action', action: () => handleExportCSV() },
      { id: 'action-settings', label: 'Open Settings', icon: '\u2699', badge: 'Action', type: 'action', action: () => navigateTo('settings') },
      { id: 'action-fleet', label: 'Go to Fleet Command', icon: '\u2302', badge: 'Action', type: 'action', action: () => navigateTo('fleet') },
    ];

    if (!q) {
      // Show actions + first few orgs when empty
      results.push(...actions);
      allOrganizations.slice(0, 5).forEach(name => {
        results.push({ id: `org-${name}`, label: name, icon: '\u{1F3E2}', subtitle: 'Organization', badge: 'Org', type: 'org', data: { name } });
      });
      return results;
    }

    // Filter actions
    actions.forEach(a => { if (a.label.toLowerCase().includes(q)) results.push(a); });

    // Search organizations
    allOrganizations.forEach(name => {
      if (name.toLowerCase().includes(q)) {
        results.push({ id: `org-${name}`, label: name, icon: '\u{1F3E2}', subtitle: 'Organization', badge: 'Org', type: 'org', data: { name } });
      }
    });

    // Search devices (limit to 10)
    if (processedData?.enriched) {
      let deviceCount = 0;
      for (const dev of processedData.enriched) {
        if (deviceCount >= 10) break;
        if (dev.is_duplicate || dev.isOffboarded) continue;
        const hostname = (dev.hostname || '').toLowerCase();
        const serial = (dev.serial || '').toLowerCase();
        const org = (dev.organization || '').toLowerCase();
        if (hostname.includes(q) || serial.includes(q) || org.includes(q)) {
          results.push({
            id: `device-${dev.hostname}-${dev.serial}`,
            label: dev.hostname || dev.serial || 'Unknown',
            icon: '\u{1F4BB}',
            subtitle: dev.organization || 'No org',
            badge: dev.computedStatus || 'Device',
            type: 'device',
            data: dev
          });
          deviceCount++;
        }
      }
    }

    return results;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCommandPalette, commandQuery, allOrganizations, processedData, navigateTo]);

  // Keyboard shortcuts - global handler
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      // Don't capture when typing in inputs/textareas (except command palette)
      const tag = e.target.tagName;
      const isInput = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
      const isCommandPaletteInput = e.target === commandInputRef.current;

      // Escape always works
      if (e.key === 'Escape') {
        if (showCommandPalette) { setShowCommandPalette(false); setCommandQuery(''); return; }
        if (confirmDrawer) { handleConfirmDrawerCancel(); return; }
        if (showShortcutsHelp) { setShowShortcutsHelp(false); return; }
        if (selectedDevice) { setSelectedDevice(null); return; }
        if (showTaskDrawer) { setShowTaskDrawer(false); return; }
        if (currentView !== 'fleet') { navigateTo('fleet'); return; }
        return;
      }

      // Command palette shortcut: Ctrl+K or / (when not in input)
      if ((e.ctrlKey && e.key === 'k') || (e.key === '/' && !isInput)) {
        e.preventDefault();
        setShowCommandPalette(true);
        setCommandQuery('');
        setCommandSelectedIdx(0);
        return;
      }

      // Skip remaining shortcuts if in an input
      if (isInput && !isCommandPaletteInput) return;

      // T = toggle task tray
      if (e.key === 't' || e.key === 'T') {
        if (!isInput) { setShowTaskDrawer(prev => !prev); return; }
      }

      // R = refresh/sync
      if (e.key === 'r' || e.key === 'R') {
        if (!isInput) { handleSync(); return; }
      }

      // ? = show shortcuts help
      if (e.key === '?' && !isInput) {
        setShowShortcutsHelp(true);
        return;
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCommandPalette, confirmDrawer, showShortcutsHelp, selectedDevice, showTaskDrawer, currentView, navigateTo, handleConfirmDrawerCancel]);

  // Focus command palette input when opened
  useEffect(() => {
    if (showCommandPalette && commandInputRef.current) {
      setTimeout(() => commandInputRef.current?.focus(), 50);
    }
  }, [showCommandPalette]);

  // Integration color map
  const INTEGRATION_COLORS = {
    'N-Central': '#00b4d8', 'ConnectWise': '#f59e0b', 'Huntress': '#10b981',
    'ImmyBot': '#6366f1', 'CloudRadial': '#ec4899', 'Datto RMM': '#8b5cf6', 'Microsoft': '#3b82f6'
  };

  // === RENDER HELPERS ===
  const renderDeviceStatusBadge = (dev) => {
    const severityClass = {
      healthy: 'badge-healthy', warning: 'badge-warning', error: 'badge-error',
      stale: 'badge-stale', offboarded: 'badge-muted', duplicate: 'badge-muted'
    }[dev.healthSeverity] || 'badge-warning';
    return (
      <span className={`device-status-badge ${severityClass}`}>
        {dev.healthPriority === 'critical' && <span style={{ marginRight: '4px' }}>!</span>}
        {dev.computedStatus}
      </span>
    );
  };

  const renderDeviceRow = (dev, idx) => {
    const deviceKey = `${dev.hostname}-${dev.serial}`;
    const isSelected = selectedDevices.has(deviceKey);
    const onlineStatus = getDeviceOnlineStatus(dev);
    return (
      <tr key={deviceKey + idx} className={`device-row ${isSelected ? 'selected' : ''}`}
        onClick={() => { setSelectedDevice(dev); fetchDeviceHistory(dev.hostname); fetchDuplicateGroup(dev); }}>
        <td className="device-checkbox" onClick={e => e.stopPropagation()}>
          <input type="checkbox" checked={isSelected}
            onChange={() => toggleDeviceSelection(deviceKey)} />
        </td>
        <td className="device-hostname">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span className={`online-dot ${onlineStatus}${immybotStatusLoading && dev.ids_map?.ImmyBot && immybotOnlineStatus[dev.ids_map.ImmyBot] === undefined ? ' pulsing' : ''}`}
              title={immybotStatusLoading && dev.ids_map?.ImmyBot && immybotOnlineStatus[dev.ids_map.ImmyBot] === undefined
                ? 'Checking online status...'
                : onlineStatus === 'connected' ? 'Online (ImmyBot agent connected)'
                : onlineStatus === 'recent' ? 'Recently active (within 24h)'
                : 'Offline'} />
            <span>{dev.hostname}</span>
            {dev.isSplit && <span className="badge-split" title="Split record">SPLIT</span>}
          </div>
        </td>
        <td className="device-org">{dev.organization || '—'}</td>
        <td>{renderDeviceStatusBadge(dev)}</td>
        <td className="device-sources">
          {dev.isOffboarded ? (
            // Offboarded devices: show a single muted label instead of noisy X icons
            <span className="integration-dot" title="Device has been offboarded — no integrations expected"
              style={{ color: 'var(--text-muted)', fontSize: '0.75rem', opacity: 0.6 }}>
              — offboarded —
            </span>
          ) : (
          ['N-Central', 'Datto RMM', 'ConnectWise', 'Huntress', 'ImmyBot', 'CloudRadial', 'Microsoft']
            .filter(src => selectedSources.includes(src))
            .map(src => {
              const ih = dev.healthIntegrations?.[src];
              if (!ih || !ih.selected || !ih.enabled) return null;
              const color = ih.health === 'healthy' ? 'var(--green)' :
                ih.health === 'missing' ? 'var(--text-muted)' :
                ih.health === 'inactive' ? 'var(--text-muted)' :
                ih.health === 'stale' ? 'var(--red)' :
                ih.health === 'warning' ? 'var(--yellow)' :
                ih.health === 'unhealthy' ? 'var(--red)' : 'var(--text-muted)';
              // ConnectWise uses dot (●) for healthy instead of checkmark (✓)
              // 'inactive' = grey dot (correctly offboarded, not an error)
              const icon = ih.health === 'healthy' ? (src === 'ConnectWise' ? '\u25CF' : '\u2713') :
                ih.health === 'missing' ? '\u2717' :
                ih.health === 'inactive' ? '\u25CF' :
                ih.health === 'stale' ? '\u25CF' :
                ih.health === 'unhealthy' ? '\u2717' : '\u25B2';
              // Enhanced tooltip for ConnectWise showing PSA status context
              const tooltip = src === 'ConnectWise'
                ? `${src}: ${ih.cwStatus || 'Unknown'}${ih.health === 'unhealthy' && ih.cwStatus === 'Active' ? ' (No Primary RMM — PSA Orphan)' : ih.health === 'inactive' ? ' (Offboarded)' : ''}`
                : `${src}: ${ih.health}${ih.days != null ? ` (${ih.days}d)` : ''}`;
              return (
                <span key={src} className="integration-dot" title={tooltip}
                  style={{ color, cursor: 'pointer' }}
                  onClick={(e) => handleIntegrationClick(e, dev, src)}>
                  {icon}
                </span>
              );
            })
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="app-shell">
      {/* === DEVICE SLIDE-OVER PANEL === */}
      {selectedDevice && (
        <div className="device-panel-overlay open" onClick={() => setSelectedDevice(null)}>
          <div className="device-panel open" onClick={e => e.stopPropagation()}>
            <div className="device-panel-header">
              <div>
                <h2 style={{ margin: 0 }}>{selectedDevice.hostname}</h2>
                <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{selectedDevice.organization || 'No Organization'}</span>
              </div>
              <button className="btn-icon" onClick={() => setSelectedDevice(null)} title="Close">✕</button>
            </div>
            <div className="device-panel-body">
              <div className="detail-section">
                <h3>Identity</h3>
                <div className="detail-row">
                  <span className="detail-label">Hostname:</span>
                  <span className="detail-value">{selectedDevice.hostname}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Serial Number:</span>
                  <span className="detail-value">{selectedDevice.serial}</span>
                </div>
                {selectedDevice.organization && (
                  <div className="detail-row">
                    <span className="detail-label">Organization:</span>
                    <span className="detail-value">{selectedDevice.organization}</span>
                  </div>
                )}
                {(selectedDevice.os_name || selectedDevice.os_version) && (
                  <div className="detail-row">
                    <span className="detail-label">Operating System:</span>
                    <span className="detail-value">
                      {selectedDevice.os_name || 'Unknown'}
                      {selectedDevice.os_version && ` (${selectedDevice.os_version})`}
                      {selectedDevice.os_type && selectedDevice.os_type !== 'windows' && (
                        <span className={`os-type-badge ${selectedDevice.os_type}`} style={{ marginLeft: '0.5rem', padding: '0.1rem 0.4rem', borderRadius: '3px', fontSize: '0.75rem', fontWeight: 'bold', backgroundColor: selectedDevice.os_type === 'macos' ? '#333' : '#666', color: '#fff' }}>
                          {selectedDevice.os_type === 'macos' ? 'macOS' : selectedDevice.os_type === 'linux' ? 'Linux' : ''}
                        </span>
                      )}
                    </span>
                  </div>
                )}
                <div className="detail-row">
                  <span className="detail-label">Status:</span>
                  <span className={`badge-reason ${selectedDevice.computedStatus === 'Healthy' ? 'healthy' : selectedDevice.computedStatus === 'Stale' ? 'stale' : 'warning'}`}>
                    {selectedDevice.computedStatus}
                  </span>
                </div>
              </div>

              <div className="detail-section">
                <h3>Source Integration</h3>
                <div className="detail-row">
                  <span className="detail-label">Present In:</span>
                  <span className="detail-value">{selectedDevice.sources ? selectedDevice.sources.join(', ') : 'None'}</span>
                </div>
                {selectedDevice.ids_map && Object.keys(selectedDevice.ids_map).length > 0 && (
                  <>
                    <h4 style={{ marginTop: '1rem', marginBottom: '0.5rem', color: '#888', fontSize: '0.85rem' }}>Source-Specific IDs:</h4>
                    {Object.entries(selectedDevice.ids_map).map(([source, id]) => (
                      <div className="detail-row" key={source}>
                        <span className="detail-label">{source}:</span>
                        <span className="detail-value" style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{id}</span>
                      </div>
                    ))}
                  </>
                )}
                {selectedDevice.organizations_by_source && Object.keys(selectedDevice.organizations_by_source).length > 0 && (
                  <>
                    <h4 style={{ marginTop: '1rem', marginBottom: '0.5rem', color: '#888', fontSize: '0.85rem' }}>Organizations by Source:</h4>
                    {Object.entries(selectedDevice.organizations_by_source).map(([source, orgData]) => (
                      <div className="detail-row" key={source}>
                        <span className="detail-label">{source}:</span>
                        <span className="detail-value" style={{ fontSize: '0.85rem' }}>
                          {orgData.name} <span style={{ color: '#666', fontFamily: 'monospace', fontSize: '0.75rem' }}>(ID: {orgData.id})</span>
                        </span>
                      </div>
                    ))}
                  </>
                )}
              </div>

              <div className="detail-section">
                <h3>Last Check-In Times</h3>
                {selectedDevice.last_sync_nable && (
                  <div className="detail-row">
                    <span className="detail-label">N-Central:</span>
                    <span className="detail-value">{selectedDevice.last_sync_nable} ({getTimeDiffText(getDaysAgo(selectedDevice.last_sync_nable))})</span>
                  </div>
                )}
                {selectedDevice.last_sync_huntress && (
                  <div className="detail-row">
                    <span className="detail-label">Huntress:</span>
                    <span className="detail-value">{selectedDevice.last_sync_huntress} ({getTimeDiffText(getDaysAgo(selectedDevice.last_sync_huntress))})</span>
                  </div>
                )}
                {selectedDevice.last_sync_immybot && (
                  <div className="detail-row">
                    <span className="detail-label">ImmyBot:</span>
                    <span className="detail-value">{selectedDevice.last_sync_immybot} ({getTimeDiffText(getDaysAgo(selectedDevice.last_sync_immybot))})</span>
                  </div>
                )}
                {selectedDevice.last_sync_cloudradial && (
                  <div className="detail-row">
                    <span className="detail-label">CloudRadial:</span>
                    <span className="detail-value">{selectedDevice.last_sync_cloudradial} ({getTimeDiffText(getDaysAgo(selectedDevice.last_sync_cloudradial))})</span>
                  </div>
                )}
                {selectedDevice.last_sync_connectwise && (
                  <div className="detail-row">
                    <span className="detail-label">ConnectWise:</span>
                    <span className="detail-value">{selectedDevice.last_sync_connectwise} ({getTimeDiffText(getDaysAgo(selectedDevice.last_sync_connectwise))})</span>
                  </div>
                )}
                {selectedDevice.last_sync_datto && (
                  <div className="detail-row">
                    <span className="detail-label">Datto RMM:</span>
                    <span className="detail-value">{selectedDevice.last_sync_datto} ({getTimeDiffText(getDaysAgo(selectedDevice.last_sync_datto))})</span>
                  </div>
                )}
                {selectedDevice.last_sync_microsoft && (
                  <div className="detail-row">
                    <span className="detail-label">Microsoft:</span>
                    <span className="detail-value">{selectedDevice.last_sync_microsoft} ({getTimeDiffText(getDaysAgo(selectedDevice.last_sync_microsoft))})</span>
                  </div>
                )}
              </div>

              {/* ConnectWise Details */}
              {selectedDevice.cw_status && (
                <div className="detail-section">
                  <h3>ConnectWise Details</h3>
                  <div className="detail-row">
                    <span className="detail-label">Status:</span>
                    <span className={`badge-reason ${selectedDevice.cw_status === 'Active' ? 'healthy' : 'warning'}`}>
                      {selectedDevice.cw_status}
                    </span>
                  </div>
                  {selectedDevice.cw_config_name && (
                    <div className="detail-row">
                      <span className="detail-label">Configuration:</span>
                      <span className="detail-value">{selectedDevice.cw_config_name}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Health Details */}
              {selectedDevice.healthIssues && selectedDevice.healthIssues.length > 0 && (
                <div className="detail-section" style={{ borderColor: selectedDevice.healthPriority === 'critical' ? '#e74c3c' : selectedDevice.healthPriority === 'high' ? '#ff9f43' : '#3498db' }}>
                  <h3 style={{ color: selectedDevice.healthPriority === 'critical' ? '#e74c3c' : selectedDevice.healthPriority === 'high' ? '#ff9f43' : '#3498db' }}>
                    {selectedDevice.healthPriority === 'critical' ? '🔓' : selectedDevice.healthPriority === 'high' ? '⚠️' : '📋'} Health Issues
                  </h3>
                  <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
                    {selectedDevice.healthIssues.map((issue, idx) => (
                      <li key={idx} style={{ color: 'var(--text-secondary)', marginBottom: '0.25rem', fontSize: '0.9rem' }}>{issue}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Ticket IDs */}
              {selectedDevice.ticket_ids && selectedDevice.ticket_ids.length > 0 && (
                <div className="detail-section">
                  <h3>Associated Tickets</h3>
                  <div className="detail-row">
                    <span className="detail-label">Ticket IDs:</span>
                    <span className="detail-value" style={{ fontFamily: 'monospace' }}>{selectedDevice.ticket_ids.join(', ')}</span>
                  </div>
                </div>
              )}

              {selectedDevice.isSplit && (
                <div className="detail-section" style={{ borderColor: '#ff9f43' }}>
                  <h3 style={{ color: '#ff9f43' }}>⚠️ Split Detection</h3>
                  <p style={{ color: '#ccc', fontSize: '0.9rem' }}>
                    This device appears as multiple separate records. This usually means different sources
                    have inconsistent serial numbers or MAC addresses. Use the MERGE button to combine them.
                  </p>
                </div>
              )}

              {/* Duplicate Detection */}
              {selectedDevice.is_duplicate && (
                <div className="detail-section" style={{ borderColor: '#f59e0b' }}>
                  <h3 style={{ color: '#f59e0b' }}>⚠️ Duplicate Detection</h3>
                  {duplicateGroupLoading ? (
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Loading duplicate records...</p>
                  ) : duplicateGroupData ? (
                    <div>
                      <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                        {duplicateGroupData.device_count} Microsoft records found for <strong>{duplicateGroupData.hostname}</strong> in {duplicateGroupData.organization_name}.
                        {duplicateGroupData.stale_count > 0 && <span style={{ color: 'var(--red)' }}> {duplicateGroupData.stale_count} stale.</span>}
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.75rem' }}>
                        {(duplicateGroupData.devices || []).map((dup, idx) => {
                          const isNewest = idx === 0;
                          const isSelected = selectedDuplicateIds.has(dup.unique_id);
                          return (
                            <label key={dup.unique_id} style={{
                              display: 'flex', alignItems: 'center', gap: '0.75rem',
                              padding: '0.5rem 0.75rem', borderRadius: '6px',
                              background: isNewest ? 'rgba(0, 255, 136, 0.05)' : dup.is_stale ? 'rgba(255, 71, 87, 0.08)' : 'transparent',
                              border: `1px solid ${isNewest ? 'rgba(0, 255, 136, 0.2)' : dup.is_stale ? 'rgba(255, 71, 87, 0.2)' : 'var(--border-color)'}`,
                              cursor: isNewest ? 'default' : 'pointer',
                              opacity: isNewest ? 0.7 : 1
                            }}>
                              <input type="checkbox"
                                checked={isSelected}
                                disabled={isNewest}
                                onChange={() => {
                                  setSelectedDuplicateIds(prev => {
                                    const next = new Set(prev);
                                    if (next.has(dup.unique_id)) next.delete(dup.unique_id);
                                    else next.add(dup.unique_id);
                                    return next;
                                  });
                                }}
                              />
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                    {dup.device_type || 'Microsoft'}
                                  </span>
                                  {isNewest && <span style={{ fontSize: '0.7rem', color: 'var(--green)', fontWeight: 'bold' }}>KEEP</span>}
                                  {dup.is_stale && <span style={{ fontSize: '0.7rem', color: 'var(--red)', fontWeight: 'bold' }}>STALE</span>}
                                  {dup.verification_status && dup.verification_status !== 'Unverified' && (
                                    <span style={{ fontSize: '0.7rem', color: 'var(--blue)', fontWeight: 'bold' }}>{dup.verification_status}</span>
                                  )}
                                </div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
                                  Last check-in: {dup.last_checkin || 'Never'}
                                  {dup.days_since_checkin != null && <span> ({dup.days_since_checkin}d ago)</span>}
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                      {/* Select all stale convenience button */}
                      {duplicateGroupData.stale_count > 0 && (
                        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                          <button
                            className="btn-action"
                            style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }}
                            onClick={() => {
                              const staleIds = (duplicateGroupData.devices || [])
                                .filter((d, i) => i !== 0 && d.is_stale)
                                .map(d => d.unique_id);
                              setSelectedDuplicateIds(new Set(staleIds));
                            }}
                          >
                            Select all stale ({duplicateGroupData.stale_count})
                          </button>
                        </div>
                      )}
                      {/* Cleanup button */}
                      <button
                        className="btn-action btn-action-danger"
                        disabled={selectedDuplicateIds.size === 0}
                        onClick={handleDuplicateCleanup}
                        style={{ width: '100%', borderColor: 'var(--red)', color: selectedDuplicateIds.size === 0 ? 'var(--text-muted)' : 'var(--red)' }}
                      >
                        {'\u2717'} Clean Up {selectedDuplicateIds.size} Duplicate{selectedDuplicateIds.size !== 1 ? 's' : ''}
                      </button>
                    </div>
                  ) : (
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                      This device is marked as a duplicate but no duplicate group data was found.
                      The duplicate may have already been resolved.
                    </p>
                  )}
                </div>
              )}

              {deviceHistory.length > 0 && (
                <div className="detail-section">
                  <h3>Device History</h3>
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {deviceHistory.map((event, idx) => (
                      <div key={idx} className="history-item" style={{
                        padding: '0.5rem 0',
                        borderBottom: idx < deviceHistory.length - 1 ? '1px solid var(--border-color)' : 'none',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div>
                          <span style={{
                            color: event.event === 'APPEARED' ? 'var(--green)' : 'var(--red)',
                            fontWeight: '700',
                            fontSize: '0.8rem'
                          }}>
                            {event.event === 'APPEARED' ? '✓' : '✗'} {event.event}
                          </span>
                          <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginLeft: '0.5rem' }}>
                            {event.source}
                          </span>
                        </div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', fontFamily: 'monospace' }}>
                          {event.timestamp}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Device Actions - Contextual based on health issues */}
              <div className="detail-section">
                <h3>Quick Actions</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {/* Issue-aware deploy buttons for each missing integration */}
                  {selectedDevice.healthIntegrations && Object.entries(selectedDevice.healthIntegrations).map(([src, ih]) => {
                    if (!ih || !ih.selected || !ih.enabled) return null;
                    if (ih.health === 'missing') {
                      return (
                        <button key={`deploy-${src}`} className="btn-action btn-action-deploy"
                          onClick={(e) => handleIntegrationClick(e, selectedDevice, src)}
                          title={`Deploy ${src} to this device`}>
                          + Deploy {src}
                        </button>
                      );
                    }
                    return null;
                  })}
                  {/* Redeploy buttons for stale/warning/unhealthy integrations that can be redeployed via ImmyBot */}
                  {selectedDevice.healthIntegrations && selectedDevice.ids_map?.ImmyBot && Object.entries(selectedDevice.healthIntegrations).map(([src, ih]) => {
                    if (!ih || !ih.selected || !ih.enabled) return null;
                    if ((ih.health === 'stale' || ih.health === 'warning' || ih.health === 'unhealthy') && ['CloudRadial', 'N-Central', 'Datto RMM', 'Huntress'].includes(src)) {
                      return (
                        <button key={`redeploy-${src}`} className="btn-action btn-action-deploy"
                          onClick={(e) => handleIntegrationClick(e, selectedDevice, src)}
                          title={`Remove and redeploy ${src} via ImmyBot`}
                          style={{ borderColor: '#f59e0b', color: '#f59e0b' }}>
                          {'\u21BB'} Redeploy {src}
                        </button>
                      );
                    }
                    return null;
                  })}
                  {/* Remove buttons for present integrations */}
                  {selectedDevice.healthIntegrations && Object.entries(selectedDevice.healthIntegrations).map(([src, ih]) => {
                    if (!ih || !ih.selected || !ih.enabled) return null;
                    if (ih.health === 'healthy' || ih.health === 'stale' || ih.health === 'warning') {
                      // Only show remove for integrations that support it
                      if (['ImmyBot', 'CloudRadial', 'N-Central', 'Datto RMM'].includes(src)) {
                        return (
                          <button key={`remove-${src}`} className="btn-action btn-action-danger"
                            onClick={(e) => handleIntegrationClick(e, selectedDevice, src)}
                            title={`Remove from ${src}`}
                            style={{ borderColor: 'var(--red)', color: 'var(--red)' }}>
                            {'\u2717'} Remove {src}
                          </button>
                        );
                      }
                    }
                    return null;
                  })}
                  {/* Microsoft Archive button */}
                  {selectedDevice.ids_map?.Microsoft && !selectedDevice.microsoft_archived && (
                    <button className="btn-action" onClick={(e) => handleIntegrationClick(e, selectedDevice, 'Microsoft')}
                      style={{ borderColor: 'var(--yellow)', color: 'var(--yellow)' }}
                      title="Archive device in Microsoft Entra ID">
                      {'\u{1F4E6}'} Archive Microsoft
                    </button>
                  )}
                  {/* Merge split records */}
                  {selectedDevice.isSplit && (
                    <button className="btn-action btn-action-merge" onClick={() => {
                      const candidates = processedData.enriched.filter(d => d.hostname === selectedDevice.hostname && !d.is_duplicate);
                      handleTrustMerge(selectedDevice.hostname, candidates);
                    }}>
                      Merge Records
                    </button>
                  )}
                  {/* Show message if no actions available */}
                  {!selectedDevice.isSplit && !selectedDevice.ids_map?.Microsoft &&
                   (!selectedDevice.healthIntegrations || Object.values(selectedDevice.healthIntegrations).every(ih => !ih?.selected || !ih?.enabled || (ih.health !== 'missing' && !['ImmyBot', 'CloudRadial', 'N-Central', 'Datto RMM'].includes('')))) && (
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem', fontStyle: 'italic' }}>No actions available for this device</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Microsoft Action Modal */}
      {bulkMicrosoftModal && (
        <div className="modal-overlay" onClick={() => setBulkMicrosoftModal(null)}>
          <div className="modal-content" style={{ maxWidth: '550px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{bulkMicrosoftModal.action === 'delete' ? 'Delete' : 'Archive'} Microsoft Devices</h2>
              <button className="close-btn" onClick={() => setBulkMicrosoftModal(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <h3 style={{ marginBottom: '0.5rem', color: bulkMicrosoftModal.action === 'delete' ? '#e74c3c' : '#f39c12' }}>
                  {bulkMicrosoftModal.action === 'delete' ? '⚠️ Delete' : '📦 Archive'} selected device(s)
                </h3>

                {/* Device list with checkboxes */}
                <div style={{ maxHeight: '350px', overflowY: 'auto', marginBottom: '1rem', padding: '0.5rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '6px' }}>
                  {/* Select all header */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '4px 0 8px 0',
                    borderBottom: '1px solid var(--border-color)',
                    marginBottom: '8px'
                  }}>
                    <input
                      type="checkbox"
                      checked={bulkMicrosoftModal.devices.every(d => bulkMicrosoftModal.selectedDevices?.has(d.ids_map?.Microsoft))}
                      onChange={(e) => {
                        const newSelected = new Set();
                        const newDupSelected = new Set();
                        const newExpanded = new Set(bulkMicrosoftModal.expandedDuplicates || []);
                        if (e.target.checked) {
                          bulkMicrosoftModal.devices.forEach(d => {
                            const devId = d.ids_map?.Microsoft;
                            if (devId) newSelected.add(devId);
                            // Also select all duplicates for each device
                            const dk = `${d.hostname}|${d.organization}`;
                            const dg = bulkMicrosoftModal.duplicateGroupMap?.[dk] || [];
                            const others = dg.filter(dup => dup.unique_id !== devId);
                            if (others.length > 0) {
                              newExpanded.add(devId);
                              others.forEach(dup => newDupSelected.add(dup.unique_id));
                            }
                          });
                        }
                        setBulkMicrosoftModal(prev => ({
                          ...prev,
                          selectedDevices: newSelected,
                          selectedDuplicateIds: newDupSelected,
                          expandedDuplicates: e.target.checked ? newExpanded : prev.expandedDuplicates
                        }));
                      }}
                      style={{ width: '16px', height: '16px' }}
                    />
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      Select all ({(bulkMicrosoftModal.selectedDevices?.size || 0) + (bulkMicrosoftModal.selectedDuplicateIds?.size || 0)} of {bulkMicrosoftModal.devices.length}{bulkMicrosoftModal.duplicateDeviceIds?.size > 0 ? ' + duplicates' : ''} selected)
                    </span>
                  </div>
                  {bulkMicrosoftModal.devices.map((d, i) => {
                    const deviceId = d.ids_map?.Microsoft;
                    const isDuplicate = bulkMicrosoftModal.duplicateDeviceIds?.has(deviceId);
                    const isSelected = bulkMicrosoftModal.selectedDevices?.has(deviceId);
                    const isExpanded = bulkMicrosoftModal.expandedDuplicates?.has(deviceId);
                    const dupKey = `${d.hostname}|${d.organization}`;
                    const dupGroup = isDuplicate ? (bulkMicrosoftModal.duplicateGroupMap?.[dupKey] || []) : [];
                    // Filter out the current device from duplicate group (match by unique_id)
                    const otherDuplicates = dupGroup.filter(dup => dup.unique_id !== deviceId);
                    return (
                      <div key={i}>
                        <div
                          style={{
                            fontSize: '0.85rem',
                            padding: '6px 4px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            background: isDuplicate ? 'rgba(245, 158, 11, 0.1)' : 'transparent',
                            borderRadius: '4px',
                            marginBottom: '2px'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              const newSelected = new Set(bulkMicrosoftModal.selectedDevices || []);
                              const newDupSelected = new Set(bulkMicrosoftModal.selectedDuplicateIds || []);
                              const newExpanded = new Set(bulkMicrosoftModal.expandedDuplicates || []);
                              if (e.target.checked) {
                                newSelected.add(deviceId);
                                // Auto-select and auto-expand all duplicates when checking a device
                                if (isDuplicate && otherDuplicates.length > 0) {
                                  newExpanded.add(deviceId);
                                  otherDuplicates.forEach(dup => newDupSelected.add(dup.unique_id));
                                }
                              } else {
                                newSelected.delete(deviceId);
                                // Deselect all duplicates when unchecking the device
                                if (isDuplicate && otherDuplicates.length > 0) {
                                  otherDuplicates.forEach(dup => newDupSelected.delete(dup.unique_id));
                                }
                              }
                              setBulkMicrosoftModal(prev => ({
                                ...prev,
                                selectedDevices: newSelected,
                                selectedDuplicateIds: newDupSelected,
                                expandedDuplicates: newExpanded
                              }));
                            }}
                            style={{ width: '16px', height: '16px', flexShrink: 0 }}
                          />
                          <span style={{ flex: 1 }}>{d.hostname}</span>
                          {isDuplicate && otherDuplicates.length > 0 && (
                            <button
                              onClick={() => {
                                const newExpanded = new Set(bulkMicrosoftModal.expandedDuplicates || []);
                                if (newExpanded.has(deviceId)) {
                                  newExpanded.delete(deviceId);
                                } else {
                                  newExpanded.add(deviceId);
                                }
                                setBulkMicrosoftModal(prev => ({ ...prev, expandedDuplicates: newExpanded }));
                              }}
                              style={{
                                background: 'rgba(245, 158, 11, 0.2)',
                                color: '#f59e0b',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontSize: '0.7rem',
                                fontWeight: '500',
                                border: '1px solid rgba(245, 158, 11, 0.4)',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                              title={isExpanded ? 'Hide duplicates' : 'Show duplicates'}
                            >
                              {otherDuplicates.length} DUPLICATE{otherDuplicates.length !== 1 ? 'S' : ''}
                              <span style={{ fontSize: '0.6rem' }}>{isExpanded ? '▲' : '▼'}</span>
                            </button>
                          )}
                        </div>
                        {/* Expandable duplicate records */}
                        {isDuplicate && isExpanded && otherDuplicates.length > 0 && (
                          <div style={{
                            marginLeft: '28px',
                            marginBottom: '6px',
                            padding: '6px 8px',
                            background: 'rgba(245, 158, 11, 0.05)',
                            border: '1px solid rgba(245, 158, 11, 0.2)',
                            borderRadius: '4px'
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                              <span style={{ fontSize: '0.7rem', color: '#f59e0b', fontWeight: '500' }}>
                                Duplicate records for {d.hostname}:
                              </span>
                              {(() => {
                                const allSelected = otherDuplicates.every(dup => bulkMicrosoftModal.selectedDuplicateIds?.has(dup.unique_id));
                                return (
                                  <button
                                    onClick={() => {
                                      const newDupSelected = new Set(bulkMicrosoftModal.selectedDuplicateIds || []);
                                      if (allSelected) {
                                        otherDuplicates.forEach(dup => newDupSelected.delete(dup.unique_id));
                                      } else {
                                        otherDuplicates.forEach(dup => newDupSelected.add(dup.unique_id));
                                      }
                                      setBulkMicrosoftModal(prev => ({ ...prev, selectedDuplicateIds: newDupSelected }));
                                    }}
                                    style={{
                                      background: allSelected ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)',
                                      color: '#ef4444',
                                      border: '1px solid rgba(239, 68, 68, 0.3)',
                                      borderRadius: '3px',
                                      padding: '1px 6px',
                                      fontSize: '0.65rem',
                                      cursor: 'pointer',
                                      fontWeight: '500'
                                    }}
                                  >
                                    {allSelected ? 'Deselect all' : 'Delete all duplicates'}
                                  </button>
                                );
                              })()}
                            </div>
                            {otherDuplicates.map((dup, j) => {
                              const dupSelected = bulkMicrosoftModal.selectedDuplicateIds?.has(dup.unique_id);
                              const checkinDate = dup.last_checkin && dup.last_checkin !== 'N/A'
                                ? new Date(dup.last_checkin).toLocaleDateString()
                                : null;
                              const daysAgo = dup.days_since_checkin;
                              const isStale = dup.is_stale;
                              const ageColor = isStale ? '#ef4444' : daysAgo != null && daysAgo > 7 ? '#f59e0b' : '#10b981';
                              const ageLabel = daysAgo != null
                                ? (daysAgo === 0 ? 'Today' : daysAgo === 1 ? '1 day ago' : `${daysAgo} days ago`)
                                : 'Never';
                              return (
                                <div
                                  key={j}
                                  style={{
                                    fontSize: '0.8rem',
                                    padding: '4px 4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    borderRadius: '3px',
                                    marginBottom: '2px',
                                    background: dupSelected ? 'rgba(239, 68, 68, 0.08)' : 'transparent'
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={dupSelected}
                                    onChange={(e) => {
                                      const newDupSelected = new Set(bulkMicrosoftModal.selectedDuplicateIds || []);
                                      if (e.target.checked) {
                                        newDupSelected.add(dup.unique_id);
                                      } else {
                                        newDupSelected.delete(dup.unique_id);
                                      }
                                      setBulkMicrosoftModal(prev => ({ ...prev, selectedDuplicateIds: newDupSelected }));
                                    }}
                                    style={{ width: '14px', height: '14px', flexShrink: 0 }}
                                  />
                                  <span style={{
                                    background: dup.device_type === 'Intune' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(139, 92, 246, 0.15)',
                                    color: dup.device_type === 'Intune' ? '#3b82f6' : '#8b5cf6',
                                    padding: '1px 5px',
                                    borderRadius: '3px',
                                    fontSize: '0.65rem',
                                    fontWeight: '500',
                                    flexShrink: 0
                                  }}>
                                    {dup.device_type}
                                  </span>
                                  <span style={{ flex: 1, fontSize: '0.75rem', color: ageColor, fontWeight: isStale ? '600' : '400' }}>
                                    {ageLabel}
                                    {checkinDate && <span style={{ color: 'var(--text-secondary)', fontWeight: '400' }}> ({checkinDate})</span>}
                                  </span>
                                  {isStale && (
                                    <span style={{
                                      background: 'rgba(239, 68, 68, 0.15)',
                                      color: '#ef4444',
                                      padding: '1px 5px',
                                      borderRadius: '3px',
                                      fontSize: '0.6rem',
                                      fontWeight: '600',
                                      flexShrink: 0
                                    }}>
                                      STALE
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Reason dropdown */}
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Reason <span style={{ color: '#e74c3c' }}>*</span>
                </label>
                <select
                  value={bulkMicrosoftModal.reason}
                  onChange={(e) => setBulkMicrosoftModal(prev => ({ ...prev, reason: e.target.value }))}
                  style={{
                    display: 'block',
                    width: '100%',
                    marginBottom: '1rem',
                    padding: '8px 10px',
                    borderRadius: '6px',
                    border: '1px solid var(--border-color)',
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    fontSize: '0.9rem'
                  }}
                >
                  <option value="">Select reason...</option>
                  <option value="Device Replaced">Device Replaced</option>
                  <option value="Employee Offboarding">Employee Offboarding</option>
                  <option value="Device Decommissioned">Device Decommissioned</option>
                  <option value="Lost/Stolen">Lost/Stolen</option>
                  <option value="End of Life">End of Life</option>
                  <option value="Other">Other</option>
                </select>

                {/* Ticket options */}
                {ticketingConfig.enabled ? (
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={bulkMicrosoftModal.createTicket}
                        onChange={(e) => setBulkMicrosoftModal(prev => ({
                          ...prev,
                          createTicket: e.target.checked,
                          ticket: e.target.checked ? '' : prev.ticket
                        }))}
                        style={{ width: '16px', height: '16px' }}
                      />
                      <span>Create ticket in {ticketingConfig.psaName}</span>
                    </label>
                    {!bulkMicrosoftModal.createTicket && (
                      <div style={{ marginTop: '8px' }}>
                        <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                          Or enter ticket reference (optional):
                        </label>
                        <input
                          type="text"
                          value={bulkMicrosoftModal.ticket}
                          onChange={(e) => {
                            const sanitized = e.target.value.replace(/[^a-zA-Z0-9\-_\s]/g, '').slice(0, 50);
                            setBulkMicrosoftModal(prev => ({ ...prev, ticket: sanitized }));
                          }}
                          placeholder="TKT-12345"
                          maxLength={50}
                          style={{
                            display: 'block',
                            width: '100%',
                            marginTop: '4px',
                            padding: '6px 10px',
                            borderRadius: '6px',
                            border: '1px solid var(--border-color)',
                            backgroundColor: 'var(--bg-secondary)',
                            color: 'var(--text-primary)',
                            fontSize: '0.9rem'
                          }}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                      Ticket reference (optional):
                    </label>
                    <input
                      type="text"
                      value={bulkMicrosoftModal.ticket}
                      onChange={(e) => {
                        const sanitized = e.target.value.replace(/[^a-zA-Z0-9\-_\s]/g, '').slice(0, 50);
                        setBulkMicrosoftModal(prev => ({ ...prev, ticket: sanitized }));
                      }}
                      placeholder="TKT-12345"
                      maxLength={50}
                      style={{
                        display: 'block',
                        width: '100%',
                        marginTop: '4px',
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: '1px solid var(--border-color)',
                        backgroundColor: 'var(--bg-secondary)',
                        color: 'var(--text-primary)',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button
                  onClick={() => setBulkMicrosoftModal(null)}
                  style={{
                    padding: '0.6rem 1.2rem',
                    border: '2px solid var(--border-color)',
                    borderRadius: '8px',
                    backgroundColor: 'transparent',
                    color: 'var(--text-primary)',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkMicrosoftAction}
                  disabled={!bulkMicrosoftModal.reason || ((bulkMicrosoftModal.selectedDevices?.size || 0) + (bulkMicrosoftModal.selectedDuplicateIds?.size || 0)) === 0}
                  style={{
                    padding: '0.6rem 1.2rem',
                    border: 'none',
                    borderRadius: '8px',
                    backgroundColor: bulkMicrosoftModal.action === 'delete' ? '#e74c3c' : '#f39c12',
                    color: 'white',
                    cursor: (bulkMicrosoftModal.reason && ((bulkMicrosoftModal.selectedDevices?.size || 0) + (bulkMicrosoftModal.selectedDuplicateIds?.size || 0)) > 0) ? 'pointer' : 'not-allowed',
                    opacity: (bulkMicrosoftModal.reason && ((bulkMicrosoftModal.selectedDevices?.size || 0) + (bulkMicrosoftModal.selectedDuplicateIds?.size || 0)) > 0) ? 1 : 0.6,
                    fontWeight: 500
                  }}
                >
                  {bulkMicrosoftModal.action === 'delete' ? 'Delete' : 'Archive'} {(bulkMicrosoftModal.selectedDevices?.size || 0) + (bulkMicrosoftModal.selectedDuplicateIds?.size || 0)} Device(s)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Modal (ImmyBot, CloudRadial, N-Central) */}
      {bulkDeleteModal && (
        <div className="modal-overlay" onClick={() => setBulkDeleteModal(null)}>
          <div className="modal-content" style={{ maxWidth: '550px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ color: '#e74c3c' }}>
                {bulkDeleteModal.blockCloudRadial ? '🚫 Block & Delete' : '🗑️ Delete'} Devices
              </h2>
              <button className="close-btn" onClick={() => setBulkDeleteModal(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <h3 style={{ marginBottom: '0.5rem', color: '#e74c3c' }}>
                  ⚠️ {bulkDeleteModal.blockCloudRadial ? 'Block & Delete' : 'Delete'} {bulkDeleteModal.devices.length} device(s)
                </h3>

                {/* Device list */}
                <div style={{ maxHeight: '150px', overflowY: 'auto', marginBottom: '1rem', padding: '0.5rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '6px' }}>
                  {bulkDeleteModal.devices.slice(0, 20).map((d, i) => {
                    const hasImmy = d.ids_map?.ImmyBot;
                    const hasCR = d.ids_map?.CloudRadial;
                    const hasNC = d.ids_map?.['N-Central'];
                    return (
                      <div key={i} style={{ fontSize: '0.85rem', padding: '4px 0', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border-color)' }}>
                        <span>{d.hostname}</span>
                        <span style={{ fontSize: '0.7rem', color: '#888' }}>
                          {[hasImmy && 'Immy', hasCR && 'CR', hasNC && 'NC'].filter(Boolean).join(', ') || 'No integrations'}
                        </span>
                      </div>
                    );
                  })}
                  {bulkDeleteModal.devices.length > 20 && (
                    <div style={{ fontSize: '0.8rem', color: '#888', padding: '4px 0', fontStyle: 'italic' }}>
                      ... and {bulkDeleteModal.devices.length - 20} more devices
                    </div>
                  )}
                </div>

                {/* Integration summary */}
                <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: 'rgba(231, 76, 60, 0.1)', borderRadius: '6px', fontSize: '0.85rem' }}>
                  <strong>Will delete from:</strong>
                  <ul style={{ margin: '0.5rem 0 0 1.5rem', padding: 0 }}>
                    {bulkDeleteModal.devices.filter(d => d.ids_map?.ImmyBot).length > 0 && (
                      <li>ImmyBot: {bulkDeleteModal.devices.filter(d => d.ids_map?.ImmyBot).length} devices</li>
                    )}
                    {bulkDeleteModal.devices.filter(d => d.ids_map?.CloudRadial).length > 0 && (
                      <li>CloudRadial: {bulkDeleteModal.devices.filter(d => d.ids_map?.CloudRadial).length} devices {bulkDeleteModal.blockCloudRadial && '(will block first)'}</li>
                    )}
                    {bulkDeleteModal.devices.filter(d => d.ids_map?.['N-Central']).length > 0 && (
                      <li>N-Central: {bulkDeleteModal.devices.filter(d => d.ids_map?.['N-Central']).length} devices</li>
                    )}
                  </ul>
                </div>

                {/* PSA Ticket Options */}
                {ticketingConfig.enabled ? (
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={bulkDeleteModal.createTicket}
                        onChange={(e) => setBulkDeleteModal(prev => ({
                          ...prev,
                          createTicket: e.target.checked,
                          ticket: e.target.checked ? '' : prev.ticket
                        }))}
                        style={{ width: '18px', height: '18px' }}
                      />
                      <span>Create ticket in <strong style={{ color: '#60a5fa' }}>{ticketingConfig.psaName}</strong></span>
                    </label>
                    {!bulkDeleteModal.createTicket && (
                      <div style={{ marginTop: '8px' }}>
                        <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                          Or enter ticket reference (optional):
                        </label>
                        <input
                          type="text"
                          value={bulkDeleteModal.ticket}
                          onChange={(e) => {
                            const sanitized = e.target.value.replace(/[^a-zA-Z0-9\-_\s]/g, '').slice(0, 50);
                            setBulkDeleteModal(prev => ({ ...prev, ticket: sanitized }));
                          }}
                          placeholder="TKT-12345"
                          maxLength={50}
                          style={{
                            display: 'block',
                            width: '100%',
                            marginTop: '4px',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            border: '1px solid var(--border-color)',
                            backgroundColor: 'var(--bg-secondary)',
                            color: 'var(--text-primary)',
                            fontSize: '0.9rem'
                          }}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                      Ticket reference (optional):
                    </label>
                    <input
                      type="text"
                      value={bulkDeleteModal.ticket}
                      onChange={(e) => {
                        const sanitized = e.target.value.replace(/[^a-zA-Z0-9\-_\s]/g, '').slice(0, 50);
                        setBulkDeleteModal(prev => ({ ...prev, ticket: sanitized }));
                      }}
                      placeholder="TKT-12345"
                      maxLength={50}
                      style={{
                        display: 'block',
                        width: '100%',
                        marginTop: '4px',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid var(--border-color)',
                        backgroundColor: 'var(--bg-secondary)',
                        color: 'var(--text-primary)',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                )}

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                  <button
                    onClick={() => setBulkDeleteModal(null)}
                    style={{
                      padding: '0.6rem 1.2rem',
                      border: '2px solid var(--border-color)',
                      borderRadius: '8px',
                      backgroundColor: 'transparent',
                      color: 'var(--text-primary)',
                      cursor: 'pointer',
                      fontWeight: 500
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={executeBulkDelete}
                    style={{
                      padding: '0.6rem 1.2rem',
                      border: 'none',
                      borderRadius: '8px',
                      backgroundColor: '#e74c3c',
                      color: 'white',
                      cursor: 'pointer',
                      fontWeight: 500
                    }}
                  >
                    {bulkDeleteModal.blockCloudRadial ? 'Block & Delete' : 'Delete'} {bulkDeleteModal.devices.length} Device(s)
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Integration Action Modal */}
      {integrationModal && (
        <div className="modal-overlay" onClick={() => setIntegrationModal(null)}>
          <div className="modal-content" style={{ maxWidth: '450px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Integration Actions</h2>
              <button className="close-btn" onClick={() => setIntegrationModal(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <h3 style={{ marginBottom: '0.5rem' }}>{integrationModal.device.hostname}</h3>

                {/* DELETE Section */}
                {integrationModal.availableIntegrations.length > 0 && (
                  <>
                    <p style={{ color: '#e74c3c', fontSize: '0.85rem', marginBottom: '0.75rem', fontWeight: '600' }}>
                      🗑️ Delete from:
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                      {integrationModal.availableIntegrations.map(integration => (
                        <div key={integration} style={{
                          padding: '0.6rem 1rem',
                          backgroundColor: integrationModal.deleteOptions[integration] ? 'rgba(231, 76, 60, 0.1)' : 'var(--bg-tertiary)',
                          border: integrationModal.deleteOptions[integration] ? '2px solid #e74c3c' : '2px solid var(--border-color)',
                          borderRadius: '8px',
                          transition: 'all 0.2s ease'
                        }}>
                          <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: (integration === 'Microsoft' && integrationModal.archiveSelected) ? 'not-allowed' : 'pointer',
                            gap: '0.75rem',
                            opacity: (integration === 'Microsoft' && integrationModal.archiveSelected) ? 0.5 : 1
                          }}>
                            <input
                              type="checkbox"
                              checked={integrationModal.deleteOptions[integration] || false}
                              disabled={integration === 'Microsoft' && integrationModal.archiveSelected}
                              onChange={(e) => {
                                setIntegrationModal(prev => ({
                                  ...prev,
                                  deleteOptions: { ...prev.deleteOptions, [integration]: e.target.checked },
                                  // Clear archive when Microsoft delete is selected (mutually exclusive)
                                  archiveSelected: integration === 'Microsoft' && e.target.checked ? false : prev.archiveSelected
                                }));
                              }}
                              style={{ width: '18px', height: '18px', cursor: (integration === 'Microsoft' && integrationModal.archiveSelected) ? 'not-allowed' : 'pointer' }}
                            />
                            <span style={{ fontWeight: '600', fontSize: '0.95rem' }}>
                              {integration === 'ConnectWise' ? 'Deactivate in' : 'Remove from'} {integration}
                            </span>
                            {integration === 'Microsoft' && integrationModal.archiveSelected && (
                              <span style={{ fontSize: '0.7rem', color: '#888', fontStyle: 'italic' }}>
                                (uncheck Archive to enable)
                              </span>
                            )}
                            <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#888', fontFamily: 'monospace' }}>
                              ID: {integrationModal.device.ids_map[integration === 'N-Central' ? 'N-Central' : integration]}
                            </span>
                          </label>

                          {/* Microsoft Delete Options - Reason and Ticket */}
                          {integration === 'Microsoft' && integrationModal.deleteOptions[integration] && (
                            <div style={{ marginTop: '0.75rem', marginLeft: '2rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid var(--border-color)' }}>
                              {/* Duplicate cleanup link */}
                              <div style={{
                                background: 'rgba(59, 130, 246, 0.1)',
                                border: '1px solid rgba(59, 130, 246, 0.3)',
                                borderRadius: '6px',
                                padding: '8px 12px',
                                fontSize: '0.8rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                <span style={{ color: '#60a5fa' }}>💡</span>
                                <span style={{ color: '#9ca3af' }}>
                                  This device may have duplicate records.{' '}
                                  <button
                                    onClick={() => {
                                      setIntegrationModal(null);
                                      setSettingsInitialTab('duplicates');
                                      setCurrentView('settings');
                                    }}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: '#60a5fa',
                                      cursor: 'pointer',
                                      padding: 0,
                                      textDecoration: 'underline',
                                      fontSize: 'inherit'
                                    }}
                                  >
                                    Check Duplicates
                                  </button>
                                </span>
                              </div>
                              <label style={{ fontSize: '0.8rem', color: '#9ca3af' }}>
                                Reason:
                                <select
                                  value={integrationModal.archiveReason}
                                  onChange={(e) => setIntegrationModal(prev => ({ ...prev, archiveReason: e.target.value }))}
                                  style={{
                                    display: 'block',
                                    width: '100%',
                                    marginTop: '4px',
                                    padding: '6px 10px',
                                    borderRadius: '6px',
                                    border: '1px solid var(--border-color)',
                                    background: 'var(--bg-secondary)',
                                    color: 'var(--text-primary)',
                                    fontSize: '0.85rem'
                                  }}
                                >
                                  <option value="End of Employment">End of Employment</option>
                                  <option value="Device Replacement">Device Replacement</option>
                                  <option value="Lost/Stolen">Lost/Stolen</option>
                                  <option value="End of Life">End of Life</option>
                                  <option value="Decommissioned">Decommissioned</option>
                                  <option value="Other">Other</option>
                                </select>
                              </label>
                              {/* PSA Ticket Creation */}
                              {ticketingConfig.enabled ? (
                                <>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', color: '#9ca3af', marginTop: '0.5rem' }}>
                                    <input
                                      type="checkbox"
                                      checked={integrationModal.createTicket}
                                      onChange={(e) => setIntegrationModal(prev => ({
                                        ...prev,
                                        createTicket: e.target.checked,
                                        archiveTicket: e.target.checked ? '' : prev.archiveTicket
                                      }))}
                                      style={{ width: '16px', height: '16px' }}
                                    />
                                    <span>Create ticket in <strong style={{ color: '#60a5fa' }}>{ticketingConfig.psaName}</strong></span>
                                  </label>
                                  {!integrationModal.createTicket && (
                                    <label style={{ fontSize: '0.8rem', color: '#9ca3af', marginTop: '0.5rem' }}>
                                      Ticket reference (optional, max 50 chars):
                                      <input
                                        type="text"
                                        value={integrationModal.archiveTicket || ''}
                                        onChange={(e) => {
                                          const sanitized = e.target.value.replace(/[^a-zA-Z0-9\-_ ]/g, '').slice(0, 50);
                                          setIntegrationModal(prev => ({ ...prev, archiveTicket: sanitized }));
                                        }}
                                        placeholder="TKT-12345"
                                        maxLength={50}
                                        style={{
                                          display: 'block',
                                          width: '100%',
                                          marginTop: '4px',
                                          padding: '6px 10px',
                                          borderRadius: '6px',
                                          border: '1px solid var(--border-color)',
                                          background: 'var(--bg-secondary)',
                                          color: 'var(--text-primary)',
                                          fontSize: '0.85rem'
                                        }}
                                      />
                                      <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                                        {(integrationModal.archiveTicket || '').length}/50 - logged for audit purposes
                                      </span>
                                    </label>
                                  )}
                                </>
                              ) : (
                                <label style={{ fontSize: '0.8rem', color: '#9ca3af', marginTop: '0.5rem' }}>
                                  Ticket reference (optional, max 50 chars):
                                  <input
                                    type="text"
                                    value={integrationModal.archiveTicket || ''}
                                    onChange={(e) => {
                                      const sanitized = e.target.value.replace(/[^a-zA-Z0-9\-_ ]/g, '').slice(0, 50);
                                      setIntegrationModal(prev => ({ ...prev, archiveTicket: sanitized }));
                                    }}
                                    placeholder="TKT-12345"
                                    maxLength={50}
                                    style={{
                                      display: 'block',
                                      width: '100%',
                                      marginTop: '4px',
                                      padding: '6px 10px',
                                      borderRadius: '6px',
                                      border: '1px solid var(--border-color)',
                                      background: 'var(--bg-secondary)',
                                      color: 'var(--text-primary)',
                                      fontSize: '0.85rem'
                                    }}
                                  />
                                  <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                                    {(integrationModal.archiveTicket || '').length}/50 - logged for audit purposes
                                  </span>
                                </label>
                              )}
                            </div>
                          )}

                          {/* CloudRadial Block Option */}
                          {integration === 'CloudRadial' && integrationModal.deleteOptions[integration] && (
                            <div style={{ marginTop: '0.5rem', marginLeft: '2rem', paddingTop: '0.5rem', borderTop: '1px solid var(--border-color)' }}>
                              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '0.5rem', color: '#e74c3c' }}>
                                <input
                                  type="checkbox"
                                  checked={integrationModal.blockCloudRadial || false}
                                  onChange={(e) => {
                                    setIntegrationModal(prev => ({
                                      ...prev,
                                      blockCloudRadial: e.target.checked
                                    }));
                                  }}
                                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                />
                                <span style={{ fontWeight: '500', fontSize: '0.85rem' }}>Block device before deleting</span>
                              </label>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* REMEDIATION Section */}
                {integrationModal.remediationOptions && integrationModal.remediationOptions.length > 0 && (
                  <>
                    <p style={{ color: '#2ecc71', fontSize: '0.85rem', marginBottom: '0.75rem', fontWeight: '600' }}>
                      🚀 Deploy missing agents (via ImmyBot):
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                      {integrationModal.remediationOptions.map(opt => (
                        <div key={opt.target} style={{
                          padding: '0.6rem 1rem',
                          backgroundColor: integrationModal.deployOptions?.[opt.target] ? 'rgba(46, 204, 113, 0.1)' : 'var(--bg-tertiary)',
                          border: integrationModal.deployOptions?.[opt.target] ? '2px solid #2ecc71' : '2px solid var(--border-color)',
                          borderRadius: '8px',
                          transition: 'all 0.2s ease'
                        }}>
                          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '0.75rem' }}>
                            <input
                              type="checkbox"
                              checked={integrationModal.deployOptions?.[opt.target] || false}
                              onChange={(e) => {
                                setIntegrationModal(prev => ({
                                  ...prev,
                                  deployOptions: { ...prev.deployOptions, [opt.target]: e.target.checked }
                                }));
                              }}
                              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                            <span style={{ fontWeight: '600', fontSize: '0.95rem', color: '#2ecc71' }}>{opt.label}</span>
                            <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#888' }}>
                              via {opt.via}
                            </span>
                          </label>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* ARCHIVE Section - for Microsoft devices */}
                {integrationModal.showArchive && (
                  <>
                    <p style={{ color: '#8b5cf6', fontSize: '0.85rem', marginBottom: '0.75rem', fontWeight: '600' }}>
                      📦 Archive Device (Microsoft):
                    </p>
                    <div style={{
                      padding: '0.6rem 1rem',
                      backgroundColor: integrationModal.archiveSelected ? 'rgba(139, 92, 246, 0.1)' : 'var(--bg-tertiary)',
                      border: integrationModal.archiveSelected ? '2px solid #8b5cf6' : '2px solid var(--border-color)',
                      borderRadius: '8px',
                      marginBottom: '1rem',
                      transition: 'all 0.2s ease'
                    }}>
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: integrationModal.deleteOptions?.Microsoft ? 'not-allowed' : 'pointer',
                        gap: '0.75rem',
                        opacity: integrationModal.deleteOptions?.Microsoft ? 0.5 : 1
                      }}>
                        <input
                          type="checkbox"
                          checked={integrationModal.archiveSelected || false}
                          disabled={integrationModal.deleteOptions?.Microsoft || false}
                          onChange={(e) => {
                            setIntegrationModal(prev => ({
                              ...prev,
                              archiveSelected: e.target.checked,
                              // Clear delete option for Microsoft when archive is selected
                              deleteOptions: e.target.checked
                                ? { ...prev.deleteOptions, Microsoft: false }
                                : prev.deleteOptions
                            }));
                          }}
                          style={{ width: '18px', height: '18px', cursor: integrationModal.deleteOptions?.Microsoft ? 'not-allowed' : 'pointer' }}
                        />
                        <span style={{ fontWeight: '600', fontSize: '0.95rem', color: '#8b5cf6' }}>
                          Archive in Entra (disable, don't delete)
                        </span>
                        {integrationModal.deleteOptions?.Microsoft && (
                          <span style={{ fontSize: '0.7rem', color: '#888', fontStyle: 'italic' }}>
                            (uncheck Delete to enable)
                          </span>
                        )}
                      </label>

                      {integrationModal.archiveSelected && (
                        <div style={{ marginTop: '0.75rem', marginLeft: '2rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          <label style={{ fontSize: '0.8rem', color: '#9ca3af' }}>
                            Reason:
                            <select
                              value={integrationModal.archiveReason}
                              onChange={(e) => setIntegrationModal(prev => ({ ...prev, archiveReason: e.target.value }))}
                              style={{
                                display: 'block',
                                width: '100%',
                                marginTop: '4px',
                                padding: '6px 10px',
                                borderRadius: '6px',
                                border: '1px solid var(--border-color)',
                                background: 'var(--bg-secondary)',
                                color: 'var(--text-primary)',
                                fontSize: '0.85rem'
                              }}
                            >
                              <option value="Leave of Absence">Leave of Absence</option>
                              <option value="Seasonal Staff">Seasonal Staff</option>
                              <option value="Equipment Repair">Equipment Repair</option>
                              <option value="Temporary Replacement">Temporary Replacement</option>
                              <option value="Compliance Hold">Compliance Hold</option>
                              <option value="Other">Other</option>
                            </select>
                          </label>
                          {/* PSA Ticket Creation */}
                          {ticketingConfig.enabled ? (
                            <>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', color: '#9ca3af', marginTop: '0.5rem' }}>
                                <input
                                  type="checkbox"
                                  checked={integrationModal.createTicket}
                                  onChange={(e) => setIntegrationModal(prev => ({
                                    ...prev,
                                    createTicket: e.target.checked,
                                    archiveTicket: e.target.checked ? '' : prev.archiveTicket
                                  }))}
                                  style={{ width: '16px', height: '16px' }}
                                />
                                <span>Create ticket in <strong style={{ color: '#60a5fa' }}>{ticketingConfig.psaName}</strong></span>
                              </label>
                              {!integrationModal.createTicket && (
                                <label style={{ fontSize: '0.8rem', color: '#9ca3af', marginTop: '0.5rem' }}>
                                  Ticket reference (optional, max 50 chars):
                                  <input
                                    type="text"
                                    value={integrationModal.archiveTicket || ''}
                                    onChange={(e) => {
                                      // Sanitize: only allow alphanumeric, dash, underscore, space
                                      const sanitized = e.target.value.replace(/[^a-zA-Z0-9\-_ ]/g, '').slice(0, 50);
                                      setIntegrationModal(prev => ({ ...prev, archiveTicket: sanitized }));
                                    }}
                                    placeholder="TKT-12345"
                                    maxLength={50}
                                    style={{
                                      display: 'block',
                                      width: '100%',
                                      marginTop: '4px',
                                      padding: '6px 10px',
                                      borderRadius: '6px',
                                      border: '1px solid var(--border-color)',
                                      background: 'var(--bg-secondary)',
                                      color: 'var(--text-primary)',
                                      fontSize: '0.85rem'
                                    }}
                                  />
                                  <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                                    {(integrationModal.archiveTicket || '').length}/50 - logged for audit purposes
                                  </span>
                                </label>
                              )}
                            </>
                          ) : (
                            <label style={{ fontSize: '0.8rem', color: '#9ca3af', marginTop: '0.5rem' }}>
                              Ticket reference (optional, max 50 chars):
                              <input
                                type="text"
                                value={integrationModal.archiveTicket || ''}
                                onChange={(e) => {
                                  // Sanitize: only allow alphanumeric, dash, underscore, space
                                  const sanitized = e.target.value.replace(/[^a-zA-Z0-9\-_ ]/g, '').slice(0, 50);
                                  setIntegrationModal(prev => ({ ...prev, archiveTicket: sanitized }));
                                }}
                                placeholder="TKT-12345"
                                maxLength={50}
                                style={{
                                  display: 'block',
                                  width: '100%',
                                  marginTop: '4px',
                                  padding: '6px 10px',
                                  borderRadius: '6px',
                                  border: '1px solid var(--border-color)',
                                  background: 'var(--bg-secondary)',
                                  color: 'var(--text-primary)',
                                  fontSize: '0.85rem'
                                }}
                              />
                              <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                                {(integrationModal.archiveTicket || '').length}/50 - logged for audit purposes
                              </span>
                            </label>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* UNARCHIVE Section - for archived Microsoft devices */}
                {integrationModal.showUnarchive && (
                  <>
                    <p style={{ color: '#22c55e', fontSize: '0.85rem', marginBottom: '0.75rem', fontWeight: '600' }}>
                      📦 Restore Device (Microsoft):
                    </p>
                    <div style={{
                      padding: '0.6rem 1rem',
                      backgroundColor: integrationModal.unarchiveSelected ? 'rgba(34, 197, 94, 0.1)' : 'var(--bg-tertiary)',
                      border: integrationModal.unarchiveSelected ? '2px solid #22c55e' : '2px solid var(--border-color)',
                      borderRadius: '8px',
                      marginBottom: '1rem',
                      transition: 'all 0.2s ease'
                    }}>
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '0.75rem' }}>
                        <input
                          type="checkbox"
                          checked={integrationModal.unarchiveSelected || false}
                          onChange={(e) => {
                            setIntegrationModal(prev => ({
                              ...prev,
                              unarchiveSelected: e.target.checked
                            }));
                          }}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <span style={{ fontWeight: '600', fontSize: '0.95rem', color: '#22c55e' }}>
                          Unarchive - Re-enable in Entra
                        </span>
                      </label>
                      {integrationModal.device.microsoft_archive_reason && (
                        <div style={{ marginTop: '0.5rem', marginLeft: '2rem', fontSize: '0.75rem', color: '#9ca3af' }}>
                          Originally archived: {integrationModal.device.microsoft_archive_reason}
                          {integrationModal.device.microsoft_archive_date && ` on ${integrationModal.device.microsoft_archive_date.split('T')[0]}`}
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setIntegrationModal(null)}
                    style={{
                      padding: '0.6rem 1.2rem',
                      border: '2px solid var(--border-color)',
                      borderRadius: '8px',
                      backgroundColor: 'transparent',
                      color: 'var(--text-primary)',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    Cancel
                  </button>
                  {(() => {
                    const hasDeletes = Object.values(integrationModal.deleteOptions).some(v => v);
                    const hasDeploys = Object.values(integrationModal.deployOptions || {}).some(v => v);
                    const hasArchive = integrationModal.archiveSelected;
                    const hasUnarchive = integrationModal.unarchiveSelected;
                    const hasAnyAction = hasDeletes || hasDeploys || hasArchive || hasUnarchive;

                    // Determine button color: red for deletes, green for deploys/unarchive, purple for archive
                    let buttonColor = '#e74c3c'; // default red
                    if (!hasDeletes) {
                      if (hasArchive) buttonColor = '#8b5cf6'; // purple
                      else if (hasUnarchive || hasDeploys) buttonColor = '#22c55e'; // green
                    }

                    // Determine button text
                    const actionCount = [hasDeletes, hasDeploys, hasArchive, hasUnarchive].filter(Boolean).length;
                    const buttonText = actionCount > 1 ? 'Execute Actions' :
                                       hasDeletes ? 'Delete Selected' :
                                       hasDeploys ? 'Deploy Selected' :
                                       hasArchive ? 'Archive Device' :
                                       hasUnarchive ? 'Unarchive Device' : 'Select Action';
                    return (
                      <button
                        onClick={handleIntegrationModalAction}
                        disabled={!hasAnyAction}
                        style={{
                          padding: '0.6rem 1.2rem',
                          border: `2px solid ${buttonColor}`,
                          borderRadius: '8px',
                          backgroundColor: `${buttonColor}22`,
                          color: buttonColor,
                          cursor: hasAnyAction ? 'pointer' : 'not-allowed',
                          fontWeight: '600',
                          opacity: hasAnyAction ? 1 : 0.5
                        }}
                      >
                        {buttonText}
                      </button>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deployment Approval Modal */}
      {deploymentApprovalModal && (
        <div className="modal-overlay" onClick={() => setDeploymentApprovalModal(null)}>
          <div className="modal-content" style={{ maxWidth: '500px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Deployment Confirmation</h2>
              <button className="close-btn" onClick={() => setDeploymentApprovalModal(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <p style={{ marginBottom: '1rem' }}>
                  <strong>{deploymentApprovalModal.software_name}</strong> deployment for <strong>{deploymentApprovalModal.org_name}</strong>
                </p>

                {/* needs_approval: Show discovered deployment for confirmation */}
                {deploymentApprovalModal.status === 'needs_approval' && (
                  <>
                    <div style={{
                      padding: '1rem',
                      backgroundColor: 'rgba(46, 204, 113, 0.1)',
                      border: '2px solid #2ecc71',
                      borderRadius: '8px',
                      marginBottom: '1rem'
                    }}>
                      <p style={{ fontSize: '0.85rem', color: '#888', marginBottom: '0.5rem' }}>
                        Discovered deployment:
                      </p>
                      <p style={{ fontWeight: '600', fontSize: '1rem' }}>
                        {deploymentApprovalModal.deployment_name}
                      </p>
                      <p style={{ fontSize: '0.8rem', color: '#888', fontFamily: 'monospace' }}>
                        ID: {deploymentApprovalModal.deployment_id}
                      </p>
                    </div>
                    <p style={{ fontSize: '0.85rem', color: '#888', marginBottom: '1rem' }}>
                      Approve this deployment to use for all future {deploymentApprovalModal.software_name} deployments to {deploymentApprovalModal.org_name}?
                    </p>
                    <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => handleDeploymentApproval('cancel')}
                        style={{
                          padding: '0.6rem 1.2rem',
                          border: '2px solid var(--border-color)',
                          borderRadius: '8px',
                          backgroundColor: 'transparent',
                          color: 'var(--text-primary)',
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleDeploymentApproval('approve')}
                        style={{
                          padding: '0.6rem 1.2rem',
                          border: '2px solid #2ecc71',
                          borderRadius: '8px',
                          backgroundColor: 'rgba(46, 204, 113, 0.2)',
                          color: '#2ecc71',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                      >
                        Approve & Deploy
                      </button>
                    </div>
                  </>
                )}

                {/* needs_selection: Show dropdown to pick from multiple deployments */}
                {deploymentApprovalModal.status === 'needs_selection' && (
                  <>
                    <p style={{ fontSize: '0.85rem', color: '#f39c12', marginBottom: '0.5rem' }}>
                      {deploymentApprovalModal.searched_tenant
                        ? `No exact match for "${deploymentApprovalModal.searched_tenant}".`
                        : 'Multiple deployments found.'}
                    </p>
                    <p style={{ fontSize: '0.85rem', color: '#888', marginBottom: '1rem' }}>
                      Please select the correct deployment:
                    </p>
                    <select
                      id="deployment-select"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid var(--border-color)',
                        borderRadius: '8px',
                        backgroundColor: 'var(--bg-tertiary)',
                        color: 'var(--text-primary)',
                        fontSize: '0.95rem',
                        marginBottom: '1rem'
                      }}
                      defaultValue=""
                    >
                      <option value="" disabled>Select a deployment...</option>
                      {deploymentApprovalModal.deployments && deploymentApprovalModal.deployments.map(dep => (
                        <option key={dep.deployment_id} value={dep.deployment_id}>
                          {dep.tenant_name} - {dep.name} (ID: {dep.deployment_id})
                        </option>
                      ))}
                    </select>
                    <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => handleDeploymentApproval('cancel')}
                        style={{
                          padding: '0.6rem 1.2rem',
                          border: '2px solid var(--border-color)',
                          borderRadius: '8px',
                          backgroundColor: 'transparent',
                          color: 'var(--text-primary)',
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => {
                          const select = document.getElementById('deployment-select');
                          const selectedId = select ? parseInt(select.value, 10) : null;
                          if (selectedId) {
                            handleDeploymentApproval('select', selectedId);
                          } else {
                            showToast('Please select a deployment', { type: 'warning' });
                          }
                        }}
                        style={{
                          padding: '0.6rem 1.2rem',
                          border: '2px solid #f39c12',
                          borderRadius: '8px',
                          backgroundColor: 'rgba(243, 156, 18, 0.2)',
                          color: '#f39c12',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                      >
                        Select & Deploy
                      </button>
                    </div>
                  </>
                )}

                {/* needs_manual: Show input field with link to ImmyBot */}
                {deploymentApprovalModal.status === 'needs_manual' && (
                  <>
                    <div style={{
                      padding: '1rem',
                      backgroundColor: 'rgba(231, 76, 60, 0.1)',
                      border: '2px solid #e74c3c',
                      borderRadius: '8px',
                      marginBottom: '1rem'
                    }}>
                      <p style={{ fontSize: '0.85rem', color: '#e74c3c', marginBottom: '0.5rem' }}>
                        No deployment found for this organization.
                      </p>
                      <p style={{ fontSize: '0.85rem', color: '#888' }}>
                        Please create the deployment in ImmyBot first, then enter the deployment ID below.
                      </p>
                    </div>
                    {(deploymentApprovalModal.software_url || deploymentApprovalModal.immy_url) && (
                      <a
                        href={deploymentApprovalModal.software_url || deploymentApprovalModal.immy_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'block',
                          marginBottom: '1rem',
                          color: '#3498db',
                          fontSize: '0.85rem'
                        }}
                      >
                        Open ImmyBot Deployments →
                      </a>
                    )}
                    <input
                      id="manual-deployment-id"
                      type="number"
                      placeholder="Enter deployment ID"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid var(--border-color)',
                        borderRadius: '8px',
                        backgroundColor: 'var(--bg-tertiary)',
                        color: 'var(--text-primary)',
                        fontSize: '0.95rem',
                        marginBottom: '1rem'
                      }}
                    />
                    <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => handleDeploymentApproval('cancel')}
                        style={{
                          padding: '0.6rem 1.2rem',
                          border: '2px solid var(--border-color)',
                          borderRadius: '8px',
                          backgroundColor: 'transparent',
                          color: 'var(--text-primary)',
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => {
                          const input = document.getElementById('manual-deployment-id');
                          const manualId = input ? parseInt(input.value, 10) : null;
                          if (manualId) {
                            handleDeploymentApproval('manual', null, manualId);
                          } else {
                            showToast('Please enter a deployment ID', { type: 'warning' });
                          }
                        }}
                        style={{
                          padding: '0.6rem 1.2rem',
                          border: '2px solid #e74c3c',
                          borderRadius: '8px',
                          backgroundColor: 'rgba(231, 76, 60, 0.2)',
                          color: '#e74c3c',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                      >
                        Save & Deploy
                      </button>
                    </div>
                  </>
                )}

                {/* Fallback for unknown status or error */}
                {!['needs_approval', 'needs_selection', 'needs_manual'].includes(deploymentApprovalModal.status) && (
                  <>
                    <div style={{
                      padding: '1rem',
                      backgroundColor: 'rgba(231, 76, 60, 0.1)',
                      border: '2px solid #e74c3c',
                      borderRadius: '8px',
                      marginBottom: '1rem'
                    }}>
                      <p style={{ fontSize: '0.85rem', color: '#e74c3c' }}>
                        {deploymentApprovalModal.message || 'An unexpected error occurred'}
                      </p>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => handleDeploymentApproval('cancel')}
                        style={{
                          padding: '0.6rem 1.2rem',
                          border: '2px solid var(--border-color)',
                          borderRadius: '8px',
                          backgroundColor: 'transparent',
                          color: 'var(--text-primary)',
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}
                      >
                        Close
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* === HEADER === */}
      <header className="header">
        <div className="header-left">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }} onClick={() => navigateTo('fleet')}>
            {(theme === 'dark' ? branding.logo_dark : branding.logo_light) ? (
              <img src={theme === 'dark' ? branding.logo_dark : branding.logo_light} alt="Logo" className="logo-icon" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
            ) : (
              <AssetMeshLogo size={32} className="logo-icon" />
            )}
            <h1 className="logo-title" style={{ fontSize: '1.3rem' }}>{branding.app_name || 'ASSET MESH'}</h1>
          </div>
        </div>

        <div className="header-center">
          {/* Breadcrumbs */}
          <nav className="breadcrumbs">
            <button className={`breadcrumb ${currentView === 'fleet' ? 'breadcrumb-current' : ''}`} onClick={() => navigateTo('fleet')}>Fleet</button>
            {(currentView === 'integration' || currentView === 'org') && drillDownContext && (
              <>
                <span className="breadcrumb-separator">/</span>
                <span className="breadcrumb-current">{drillDownContext.name}</span>
              </>
            )}
            {currentView === 'settings' && (
              <>
                <span className="breadcrumb-separator">/</span>
                <span className="breadcrumb-current">Settings</span>
              </>
            )}
          </nav>
        </div>

        <div className="header-right">
          {/* Search */}
          <div className="header-search">
            <input type="text" className="search-input" placeholder="Search devices..." value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)} />
            {searchTerm && <button className="clear-search-btn" onClick={() => setSearchTerm('')}>✕</button>}
          </div>

          {/* Sync Button */}
          <div className="sync-dropdown">
            <button className="btn-icon" title="Sync" onClick={() => setShowSyncMenu(!showSyncMenu)}
              disabled={syncing || syncingSource} style={{ position: 'relative' }}>
              <span style={{ fontSize: '1.1rem', display: 'inline-block', animation: (syncing || syncingSource) ? 'spin 1s linear infinite' : 'none' }}>&#x21bb;</span>
              {(syncing || syncingSource) && <span className="sync-pulse" />}
            </button>
            {showSyncMenu && (
              <div className="sync-menu">
                <button onClick={() => { handleSync(); setShowSyncMenu(false); }} disabled={syncing}>
                  {syncing ? 'Syncing...' : 'Full Sync (All)'}
                </button>
                {['N-Central', 'ConnectWise', 'Huntress', 'ImmyBot', 'CloudRadial', 'Datto RMM', 'Microsoft'].map(src => (
                  <button key={src} onClick={() => handleSyncSource(src)}
                    disabled={syncStatus[src] === 'syncing'}
                    className={syncStatus[src] === 'syncing' ? 'syncing' : ''}>
                    {syncStatus[src] === 'syncing' ? `Syncing ${src}...` : src}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Task Tray Toggle */}
          <button className="btn-icon" title="Task Queue" onClick={() => setShowTaskDrawer(!showTaskDrawer)}
            style={{ position: 'relative' }}>
            <span style={{ fontSize: '1.1rem' }}>&#x26A1;</span>
            {taskQueue.in_progress.length > 0 && (
              <span className="badge-count">{taskQueue.in_progress.length}</span>
            )}
          </button>

          {/* Theme Toggle */}
          <button className="btn-icon" title={theme === 'dark' ? 'Light mode' : 'Dark mode'} onClick={toggleTheme}>
            {theme === 'dark' ? '\u2600' : '\u263D'}
          </button>

          {/* Overflow Menu */}
          <div style={{ position: 'relative' }}>
            <button className="btn-icon" title="More" onClick={() => setShowOverflowMenu(!showOverflowMenu)}>
              &#x2630;
            </button>
            {showOverflowMenu && (
              <div className="overflow-menu" onClick={() => setShowOverflowMenu(false)}>
                <button onClick={() => { navigateTo('settings'); setSettingsInitialTab(null); }}>Settings</button>
                <button onClick={() => { navigateTo('settings'); setSettingsInitialTab('eventlog'); }}>Event Log</button>
                <button onClick={() => { navigateTo('settings'); setSettingsInitialTab('duplicates'); }}>Microsoft Duplicates</button>
                <button onClick={handleExportCSV}>Export CSV</button>
                <div style={{ borderTop: '1px solid var(--border-color)', margin: '4px 0' }} />
                <button onClick={() => window.open('/docs/index.html', '_blank')}>
                  {'\u{1F4D6}'} Documentation
                </button>
                <button onClick={() => setShowChangelogModal(true)}>
                  v{APP_VERSION} Changelog
                </button>
                <button onClick={handleRefresh}>Refresh Data</button>
                <button onClick={() => setShowShortcutsHelp(true)}>{'\u2328'} Keyboard Shortcuts</button>
                <div style={{ borderTop: '1px solid var(--border-color)', margin: '4px 0' }} />
                <button onClick={logout} style={{ color: 'var(--red)' }}>Sign Out ({user?.name?.split(' ')[0] || 'User'})</button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* === MAIN CONTENT AREA === */}
      <main className={`main-content ${transitionDir !== 'none' ? `page-transition-${transitionDir}` : ''}`}>
        {/* Loading Overlay */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: '2rem', animation: 'spin 1s linear infinite', display: 'inline-block' }}>&#x21bb;</div>
            <p>Loading devices...</p>
          </div>
        )}


        {/* === SETTINGS VIEW === */}
        {currentView === 'settings' && !loading && (
          <Settings
            onOrgSettingsChange={fetchOrgSettings}
            initialTab={settingsInitialTab}
            showToast={showToast}
            onVerificationStatusChange={setVerificationStatus}
          />
        )}

        {/* === FLEET COMMAND (Landing) === */}
        {currentView === 'fleet' && !loading && (
          <div className="fleet-command">
            {/* Fleet Health Summary */}
            <div className="fleet-health-summary">
              <div className={`stat-card ${activeFilter === 'All' ? 'active' : ''}`} onClick={() => handleFilterClick('All')}>
                <div className="stat-label">{'\u25CF'} TOTAL DEVICES</div>
                <div className="stat-value font-mono">{processedData.stats.total}</div>
              </div>
              <div className={`stat-card green ${activeFilter === 'Healthy' ? 'active' : ''}`} onClick={() => handleFilterClick('Healthy')}>
                <div className="stat-label">{'\u25CF'} HEALTHY</div>
                <div className="stat-value font-mono">{processedData.stats.healthy}</div>
                <div className="stat-pct">{processedData.stats.total > 0 ? Math.round((processedData.stats.healthy / processedData.stats.total) * 100) : 0}%</div>
              </div>
              <div className={`stat-card yellow ${activeFilter === 'Warning' ? 'active' : ''}`} onClick={() => handleFilterClick('Warning')}>
                <div className="stat-label">{'\u25B2'} WARNING</div>
                <div className="stat-value font-mono">{processedData.stats.warning}</div>
              </div>
              <div className={`stat-card ${activeFilter === 'Stale' ? 'active' : ''}`} onClick={() => handleFilterClick('Stale')}
                style={{ borderLeftColor: '#6b7280' }}>
                <div className="stat-label">{'\u25C6'} STALE</div>
                <div className="stat-value font-mono">{processedData.stats.stale}</div>
              </div>
              <div className={`stat-card red ${activeFilter === 'PSA Orphans' ? 'active' : ''}`} onClick={() => handleFilterClick('PSA Orphans')}>
                <div className="stat-label">{'\u25A0'} CRITICAL</div>
                <div className="stat-value font-mono">{processedData.stats.psaOrphans}</div>
              </div>
            </div>

            {/* Fleet overview — collapses when a filter is active to show device table */}
            {activeFilter === 'All' && processedData.stats.total > 0 && (() => {
              const total = processedData.stats.total;
              const healthy = processedData.stats.healthy;
              const warning = processedData.stats.warning;
              const stale = processedData.stats.stale;
              const critical = processedData.stats.psaOrphans;
              const healthPct = Math.round((healthy / total) * 100);
              const segments = [
                { label: 'Healthy', count: healthy, color: 'var(--green)', filter: 'Healthy' },
                { label: 'Warning', count: warning, color: 'var(--yellow)', filter: 'Warning' },
                { label: 'Stale', count: stale, color: '#6b7280', filter: 'Stale' },
                { label: 'Critical', count: critical, color: 'var(--red)', filter: 'PSA Orphans' },
              ].filter(s => s.count > 0);
              const radius = 46;
              const circumference = 2 * Math.PI * radius;
              let offset = 0;
              return (
                <div className="health-ring-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.5rem', margin: '0 0 1rem 0' }}>
                  <div className="health-ring" style={{ position: 'relative', width: '120px', height: '120px', flexShrink: 0 }}>
                    <svg width="120" height="120" viewBox="0 0 120 120" style={{ transform: 'rotate(-90deg)' }}>
                      {/* Background circle */}
                      <circle cx="60" cy="60" r={radius} fill="none" stroke="var(--border-color)" strokeWidth="14" opacity="0.3" />
                      {/* Segments */}
                      {segments.map((seg) => {
                        const pct = seg.count / total;
                        const dash = pct * circumference;
                        const gap = circumference - dash;
                        const currentOffset = offset;
                        offset += dash;
                        return (
                          <circle key={seg.label} cx="60" cy="60" r={radius} fill="none"
                            stroke={seg.color} strokeWidth="14" strokeLinecap="butt"
                            strokeDasharray={`${dash} ${gap}`}
                            strokeDashoffset={-currentOffset}
                            style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                            onClick={() => handleFilterClick(seg.filter)}
                            onMouseOver={(e) => e.target.style.opacity = '0.7'}
                            onMouseOut={(e) => e.target.style.opacity = '1'}
                          />
                        );
                      })}
                    </svg>
                    {/* Center text */}
                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                      <div style={{ fontSize: '1.2rem', fontWeight: '700', fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>{healthPct}%</div>
                      <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Health</div>
                    </div>
                  </div>
                  {/* Legend */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    {segments.map(seg => (
                      <div key={seg.label} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', padding: '0.15rem 0.4rem', borderRadius: '4px' }}
                        onClick={() => handleFilterClick(seg.filter)}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '2px', backgroundColor: seg.color, flexShrink: 0 }} />
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-primary)', minWidth: '48px' }}>{seg.label}</span>
                        <span style={{ fontSize: '0.75rem', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>{seg.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Two-Panel Layout — hidden when filter active to show device table */}
            {activeFilter === 'All' && <div className="fleet-panels">
              {/* LEFT: Integration Cards */}
              <div className="fleet-panel">
                <div className="fleet-panel-header">
                  <h3>Integrations</h3>
                  <span className="text-muted" style={{ fontSize: '0.8rem' }}>{selectedSources.length} active</span>
                </div>
                <div className="fleet-panel-content">
                  {['N-Central', 'Datto RMM', 'ConnectWise', 'Huntress', 'ImmyBot', 'CloudRadial', 'Microsoft']
                    .filter(src => selectedSources.includes(src))
                    .map(src => {
                      const data = integrationBreakdown[src] || { total: 0, healthy: 0, issues: 0, missing: 0, stale: 0 };
                      const syncTime = sourceSyncTimes[src];
                      const isSyncing = syncStatus[src] === 'syncing';
                      return (
                        <div key={src} className="integration-card" onClick={() => navigateTo('integration', { type: 'integration', name: src })}>
                          <div className="integration-card-icon" style={{ backgroundColor: `${INTEGRATION_COLORS[src]}20`, color: INTEGRATION_COLORS[src] }}>
                            {src.charAt(0)}
                          </div>
                          <div className="integration-card-body">
                            <div className="integration-card-name">{src}</div>
                            <div className="integration-card-meta">
                              {isSyncing ? (
                                <span style={{ color: 'var(--blue)', fontSize: '0.75rem' }}>Syncing...</span>
                              ) : syncTime ? (
                                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                  Synced {getTimeDiffText(getDaysAgo(syncTime))}
                                </span>
                              ) : null}
                            </div>
                          </div>
                          <div className="integration-card-stats">
                            <span className="text-green font-mono" style={{ fontSize: '0.9rem' }}>{data.healthy}</span>
                            {data.issues > 0 && (
                              <span className="integration-card-issue">{data.issues}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>

              {/* RIGHT: Organization Cards */}
              <div className="fleet-panel">
                <div className="fleet-panel-header">
                  <h3>Organizations</h3>
                  <span className="text-muted" style={{ fontSize: '0.8rem' }}>{orgHealthBreakdown.length} orgs</span>
                </div>
                <div className="fleet-panel-content" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  {/* Org search */}
                  <div style={{ position: 'relative', marginBottom: '0.75rem' }}>
                    <input type="text" className="search-input" placeholder="Filter organizations..." value={orgSearchTerm}
                      onChange={e => setOrgSearchTerm(e.target.value)} style={{ width: '100%', padding: '0.5rem 2rem 0.5rem 0.75rem', fontSize: '0.85rem' }} />
                    {orgSearchTerm && <button className="clear-search-btn" onClick={() => setOrgSearchTerm('')}>{'\u2715'}</button>}
                  </div>
                  {orgHealthBreakdown
                    .filter(org => !orgSearchTerm || org.name.toLowerCase().includes(orgSearchTerm.toLowerCase()))
                    .map(org => (
                      <div key={org.name} className="org-row" onClick={() => navigateTo('org', { type: 'org', name: org.name })}>
                        <span className={`org-row-pct font-mono ${org.healthPct >= 90 ? 'good' : org.healthPct >= 70 ? 'warn' : 'bad'}`}>
                          {org.healthPct}%
                        </span>
                        <span className="org-row-name" title={org.name}>{org.name}</span>
                        <span className="org-row-count text-muted">{org.total}</span>
                        <span className="org-row-badges">
                          {org.critical > 0 && <span className="badge-error">{org.critical}</span>}
                          {org.high > 0 && <span className="badge-warning">{org.high}</span>}
                          {org.medium > 0 && <span className="badge-info">{org.medium}</span>}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            </div>}

            {/* Quick Filters Row */}
            {(processedData.stats.allIssues > 0 || processedData.stats.duplicate > 0) && (
              <div className="quick-filters" style={{ marginTop: '0.75rem' }}>
                <h4 style={{ color: 'var(--text-muted)', marginBottom: '0.5rem', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Quick Filters</h4>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {processedData.stats.allIssues > 0 && (
                    <button className={`quick-filter-btn ${activeFilter === 'All Issues' ? 'active' : ''}`} onClick={() => handleFilterClick('All Issues')}>
                      All Issues <span className="font-mono">{processedData.stats.allIssues}</span>
                    </button>
                  )}
                  {processedData.stats.missingSecurity > 0 && (
                    <button className={`quick-filter-btn critical ${activeFilter === 'Security Issues' ? 'active' : ''}`} onClick={() => handleFilterClick('Security Issues')}>
                      Security Issues <span className="font-mono">{processedData.stats.missingSecurity}</span>
                    </button>
                  )}
                  {processedData.stats.psaIssues > 0 && (
                    <button className={`quick-filter-btn ${activeFilter === 'PSA Issues' ? 'active' : ''}`} onClick={() => handleFilterClick('PSA Issues')}>
                      PSA Issues <span className="font-mono">{processedData.stats.psaIssues}</span>
                    </button>
                  )}
                  {processedData.stats.serviceDelivery > 0 && (
                    <button className={`quick-filter-btn ${activeFilter === 'Service Delivery' ? 'active' : ''}`} onClick={() => handleFilterClick('Service Delivery')}>
                      Service Delivery <span className="font-mono">{processedData.stats.serviceDelivery}</span>
                    </button>
                  )}
                  {processedData.stats.rmmIssues > 0 && (
                    <button className={`quick-filter-btn ${activeFilter === 'RMM Issues' ? 'active' : ''}`} onClick={() => handleFilterClick('RMM Issues')}>
                      RMM Issues <span className="font-mono">{processedData.stats.rmmIssues}</span>
                    </button>
                  )}
                  {processedData.stats.microsoftIssues > 0 && (
                    <button className={`quick-filter-btn ${activeFilter === 'Microsoft Issues' ? 'active' : ''}`} onClick={() => handleFilterClick('Microsoft Issues')}>
                      Microsoft Issues <span className="font-mono">{processedData.stats.microsoftIssues}</span>
                    </button>
                  )}
                  {processedData.stats.duplicate > 0 && (
                    <button className={`quick-filter-btn muted ${activeFilter === 'Duplicate' ? 'active' : ''}`} onClick={() => handleFilterClick('Duplicate')}>
                      Duplicates <span className="font-mono">{processedData.stats.duplicate}</span>
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Device Table (when a filter is active) */}
            {activeFilter !== 'All' && (
              <div ref={deviceTableRef} style={{ marginTop: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ margin: 0 }}>{activeFilter} — {finalDisplayData.length} devices</h3>
                  <button className="btn-action" onClick={() => setActiveFilter('All')} style={{ fontSize: '0.8rem' }}>Clear Filter</button>
                </div>

                {/* Bulk Actions */}
                {selectedDevices.size > 0 && (
                  <div className="bulk-actions-bar">
                    <span>{selectedDevices.size} selected</span>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button className="btn-action btn-action-deploy" onClick={() => handleBulkDeployNCentral(false)}>Deploy N-Central</button>
                      <button className="btn-action btn-action-deploy" onClick={handleBulkDeployCloudRadial}>Deploy CloudRadial</button>
                      <button className="btn-action btn-action-delete" onClick={() => handleBulkDelete(false)}>Delete</button>
                      <button className="btn-action btn-action-delete" onClick={() => handleBulkDelete(true)}>Block &amp; Delete CR</button>
                      <button className="btn-action" onClick={() => {
                        const msDevices = resolveSelectedDevices(selectedDevices)
                          .filter(d => d.ids_map?.Microsoft);
                        if (msDevices.length === 0) { showToast('No Microsoft devices selected', { type: 'warning' }); return; }
                        checkBulkMicrosoftDuplicates(msDevices).then(dupResult => {
                          setBulkMicrosoftModal({
                            devices: msDevices, action: 'archive', reason: '', ticket: '',
                            createTicket: ticketingConfig.enabled,
                            selectedDevices: new Set(msDevices.map(d => d.ids_map.Microsoft)),
                            ...(dupResult.hasDuplicates ? {
                              duplicateDeviceIds: new Set(dupResult.devicesWithDuplicates.map(d => d.ids_map.Microsoft)),
                              duplicateGroupMap: dupResult.duplicateGroupMap,
                              selectedDuplicateIds: new Set(), expandedDuplicates: new Set()
                            } : {})
                          });
                        });
                      }}>MS Archive</button>
                      <button className="btn-action" onClick={() => setSelectedDevices(new Set())} style={{ color: 'var(--text-muted)' }}>Clear</button>
                    </div>
                  </div>
                )}

                {/* Device Table */}
                <table className="device-table">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>
                        <input type="checkbox" onChange={() => toggleSelectAll(paginatedDevices)}
                          checked={paginatedDevices.length > 0 && paginatedDevices.filter(d => d.ids_map?.ImmyBot || d.ids_map?.CloudRadial || d.ids_map?.['N-Central'] || d.ids_map?.Microsoft).every(d => selectedDevices.has(`${d.hostname}-${d.serial}`))} />
                      </th>
                      <th onClick={() => requestSort('hostname')}>Hostname {getSortIcon('hostname')}</th>
                      <th onClick={() => requestSort('organization')}>Organization {getSortIcon('organization')}</th>
                      <th onClick={() => requestSort('status')}>Status {getSortIcon('status')}</th>
                      <th>Integrations</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedDevices.map((dev, idx) => renderDeviceRow(dev, idx))}
                  </tbody>
                </table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="pagination">
                    <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>&laquo; Prev</button>
                    <span className="font-mono">{currentPage} / {totalPages}</span>
                    <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next &raquo;</button>
                    <select value={itemsPerPage} onChange={e => { setItemsPerPage(e.target.value === 'All' ? 'All' : parseInt(e.target.value)); setCurrentPage(1); }}>
                      <option value={10}>10</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value="All">All</option>
                    </select>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* === INTEGRATION DRILL-DOWN === */}
        {currentView === 'integration' && drillDownContext && !loading && (
          <div className="drill-down-view">
            <div className="drill-down-summary-bar">
              <div className="integration-card-icon" style={{ backgroundColor: `${INTEGRATION_COLORS[drillDownContext.name]}20`, color: INTEGRATION_COLORS[drillDownContext.name], width: '48px', height: '48px', fontSize: '1.5rem' }}>
                {drillDownContext.name.charAt(0)}
              </div>
              <div>
                <h2 style={{ margin: 0 }}>{drillDownContext.name}</h2>
                <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                  {integrationBreakdown[drillDownContext.name]?.healthy || 0} healthy
                  {' \u2022 '}{integrationBreakdown[drillDownContext.name]?.issues || 0} issues
                  {' \u2022 '}Last sync: {sourceSyncTimes[drillDownContext.name] ? getTimeDiffText(getDaysAgo(sourceSyncTimes[drillDownContext.name])) : 'N/A'}
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="drill-down-tabs">
              <button className={`drill-down-tab ${drillDownTab === 'issues' ? 'active' : ''}`} onClick={() => setDrillDownTab('issues')}>
                Issues ({integrationBreakdown[drillDownContext.name]?.issues || 0})
              </button>
              <button className={`drill-down-tab ${drillDownTab === 'stale' ? 'active' : ''}`} onClick={() => setDrillDownTab('stale')}>
                Stale ({integrationBreakdown[drillDownContext.name]?.stale || 0})
              </button>
              <button className={`drill-down-tab ${drillDownTab === 'all' ? 'active' : ''}`} onClick={() => setDrillDownTab('all')}>
                All Devices ({integrationBreakdown[drillDownContext.name]?.total || 0})
              </button>
            </div>

            {/* Bulk Actions */}
            {selectedDevices.size > 0 && (
              <div className="bulk-actions-bar">
                <span>{selectedDevices.size} selected</span>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {drillDownContext.name !== 'ConnectWise' && (
                    <button className="btn-action btn-action-deploy" onClick={() => handleBulkDeployNCentral(false)}>Deploy N-Central</button>
                  )}
                  <button className="btn-action btn-action-deploy" onClick={handleBulkDeployCloudRadial}>Deploy CloudRadial</button>
                  <button className="btn-action btn-action-delete" onClick={() => handleBulkDelete(false)}>Delete Selected</button>
                  <button className="btn-action" onClick={() => setSelectedDevices(new Set())} style={{ color: 'var(--text-muted)' }}>Clear</button>
                </div>
              </div>
            )}

            {/* Integration Legend */}
            <div className="integration-legend">
              <span className="integration-legend-item"><span style={{ color: 'var(--green)' }}>{'\u2713'}</span> Healthy</span>
              <span className="integration-legend-item"><span style={{ color: 'var(--text-muted)' }}>{'\u2717'}</span> Missing</span>
              <span className="integration-legend-item"><span style={{ color: 'var(--red)' }}>{'\u25CF'}</span> Stale</span>
              <span className="integration-legend-item"><span style={{ color: 'var(--yellow)' }}>{'\u25B2'}</span> Warning</span>
            </div>

            {/* Device Table */}
            <table className="device-table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input type="checkbox" onChange={() => toggleSelectAll(drillDownDevices)}
                      checked={drillDownDevices.length > 0 && drillDownDevices.filter(d => d.ids_map?.ImmyBot || d.ids_map?.CloudRadial || d.ids_map?.['N-Central'] || d.ids_map?.Microsoft).every(d => selectedDevices.has(`${d.hostname}-${d.serial}`))} />
                  </th>
                  <th onClick={() => requestSort('hostname')}>Hostname {getSortIcon('hostname')}</th>
                  <th onClick={() => requestSort('organization')}>Organization {getSortIcon('organization')}</th>
                  <th onClick={() => requestSort('status')}>Status {getSortIcon('status')}</th>
                  <th>Integrations</th>
                </tr>
              </thead>
              <tbody>
                {drillDownDevices.map((dev, idx) => renderDeviceRow(dev, idx))}
              </tbody>
            </table>
            {drillDownDevices.length === 0 && (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                No devices match this filter.
              </div>
            )}
          </div>
        )}

        {/* === ORGANIZATION DRILL-DOWN === */}
        {currentView === 'org' && drillDownContext && !loading && (
          <div className="drill-down-view">
            <div className="drill-down-summary-bar">
              <div>
                <h2 style={{ margin: 0 }}>{drillDownContext.name}</h2>
                {(() => {
                  const orgData = orgHealthBreakdown.find(o => o.name === drillDownContext.name);
                  return orgData ? (
                    <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                      {orgData.healthPct}% healthy {' \u2022 '} {orgData.total} devices {' \u2022 '} {orgData.issues} issues
                    </div>
                  ) : null;
                })()}
              </div>
              <button className="btn-action" onClick={() => openQuickOrgSettings(drillDownContext.name)} title="Organization Settings">
                &#x2699; Settings
              </button>
            </div>

            {/* Integration Health Cards for this Org */}
            <div className="org-integration-grid">
              {['N-Central', 'Datto RMM', 'ConnectWise', 'Huntress', 'ImmyBot', 'CloudRadial', 'Microsoft']
                .filter(src => selectedSources.includes(src))
                .map(src => {
                  const issue = orgIntegrationIssues.find(i => i.source === src);
                  const total = issue?.total || 0;
                  const healthy = issue ? issue.healthy : total;
                  const missing = issue?.missing || 0;
                  const stale = issue?.stale || 0;
                  return (
                    <div key={src} className={`org-integration-card ${(missing + stale) > 0 ? 'has-issues' : ''}`}>
                      <div style={{ color: INTEGRATION_COLORS[src], fontWeight: '600', fontSize: '0.85rem' }}>{src}</div>
                      <div className="font-mono" style={{ fontSize: '1.5rem', fontWeight: '700' }}>{healthy}</div>
                      <div className="text-muted" style={{ fontSize: '0.75rem' }}>OK</div>
                      {missing > 0 && <div style={{ color: 'var(--red)', fontSize: '0.75rem', fontWeight: '600' }}>{missing} missing</div>}
                      {stale > 0 && <div style={{ color: 'var(--yellow)', fontSize: '0.75rem', fontWeight: '600' }}>{stale} stale</div>}
                    </div>
                  );
                })}
            </div>

            {/* Issue Queue */}
            {orgIntegrationIssues.length > 0 && (
              <div className="issue-queue">
                <h3 style={{ marginBottom: '0.75rem' }}>Issue Queue</h3>
                <div className="issue-queue-grid">
                  {orgIntegrationIssues.map(issue => (
                    <div key={issue.source} className="issue-row">
                      <button className="issue-row-source"
                        style={{ color: INTEGRATION_COLORS[issue.source], borderColor: INTEGRATION_COLORS[issue.source] }}
                        onClick={() => {
                          const deviceKeys = issue.devices.map(d => `${d.hostname}-${d.serial}`);
                          setSelectedDevices(new Set(deviceKeys));
                        }}>
                        {issue.source}
                        <span className="text-muted font-mono" style={{ fontSize: '0.7rem', fontWeight: '400' }}>
                          {issue.missing > 0 && `${issue.missing} missing`}
                          {issue.missing > 0 && issue.stale > 0 && ' \u00B7 '}
                          {issue.stale > 0 && `${issue.stale} stale`}
                        </span>
                      </button>
                      <div className="issue-row-actions">
                        {/* Inline deploy button for missing integrations */}
                        {issue.missing > 0 && ['N-Central', 'CloudRadial'].includes(issue.source) && (
                          <button className="btn-action btn-action-deploy" onClick={async () => {
                            const missingDevices = issue.devices.filter(d => d.healthIntegrations?.[issue.source]?.health === 'missing');
                            const deviceKeys = missingDevices.map(d => `${d.hostname}-${d.serial}`);
                            setSelectedDevices(new Set(deviceKeys));
                            const confirmed = await showConfirmation({
                              title: `Deploy ${issue.source}`,
                              message: `Deploy ${issue.source} to ${missingDevices.length} device(s) in this organization?`,
                              confirmLabel: `Deploy ${missingDevices.length}`,
                              variant: 'primary'
                            });
                            if (confirmed) {
                              if (issue.source === 'N-Central') handleBulkDeployNCentral(false);
                              else if (issue.source === 'CloudRadial') handleBulkDeployCloudRadial();
                            }
                          }}>
                            + Deploy {issue.missing}
                          </button>
                        )}
                        {/* Investigate stale devices */}
                        {issue.stale > 0 && (
                          <button className="btn-action" style={{ borderColor: 'var(--yellow)', color: 'var(--yellow)' }} onClick={() => {
                            const staleDevices = issue.devices.filter(d => d.healthIntegrations?.[issue.source]?.health === 'stale');
                            const deviceKeys = staleDevices.map(d => `${d.hostname}-${d.serial}`);
                            setSelectedDevices(new Set(deviceKeys));
                            setDrillDownTab('issues');
                          }}>
                            {'\u26A0'} {issue.stale} Stale
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tabs */}
            <div className="drill-down-tabs" style={{ marginTop: '1.5rem' }}>
              <button className={`drill-down-tab ${drillDownTab === 'all' ? 'active' : ''}`} onClick={() => setDrillDownTab('all')}>
                All Devices
              </button>
              <button className={`drill-down-tab ${drillDownTab === 'issues' ? 'active' : ''}`} onClick={() => setDrillDownTab('issues')}>
                Issues
              </button>
              <button className={`drill-down-tab ${drillDownTab === 'duplicates' ? 'active' : ''}`} onClick={() => setDrillDownTab('duplicates')}>
                Duplicates
              </button>
              <button className={`drill-down-tab ${drillDownTab === 'archived' ? 'active' : ''}`} onClick={() => setDrillDownTab('archived')}>
                Archived
              </button>
              <button className={`drill-down-tab ${drillDownTab === 'offboarded' ? 'active' : ''}`} onClick={() => setDrillDownTab('offboarded')}>
                Offboarded
              </button>
            </div>

            {/* Bulk Actions */}
            {selectedDevices.size > 0 && (
              <div className="bulk-actions-bar">
                <span>{selectedDevices.size} selected</span>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="btn-action btn-action-deploy" onClick={() => handleBulkDeployNCentral(false)}>Deploy N-Central</button>
                  <button className="btn-action btn-action-deploy" onClick={handleBulkDeployCloudRadial}>Deploy CloudRadial</button>
                  <button className="btn-action btn-action-delete" onClick={() => handleBulkDelete(false)}>Delete</button>
                  <button className="btn-action" onClick={() => setSelectedDevices(new Set())} style={{ color: 'var(--text-muted)' }}>Clear</button>
                </div>
              </div>
            )}

            {/* Integration Legend */}
            <div className="integration-legend">
              <span className="integration-legend-item"><span style={{ color: 'var(--green)' }}>{'\u2713'}</span> Healthy</span>
              <span className="integration-legend-item"><span style={{ color: 'var(--text-muted)' }}>{'\u2717'}</span> Missing</span>
              <span className="integration-legend-item"><span style={{ color: 'var(--red)' }}>{'\u25CF'}</span> Stale</span>
              <span className="integration-legend-item"><span style={{ color: 'var(--yellow)' }}>{'\u25B2'}</span> Warning</span>
            </div>

            {/* Device Table */}
            <table className="device-table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input type="checkbox" onChange={() => toggleSelectAll(drillDownDevices)}
                      checked={drillDownDevices.length > 0 && drillDownDevices.filter(d => d.ids_map?.ImmyBot || d.ids_map?.CloudRadial || d.ids_map?.['N-Central'] || d.ids_map?.Microsoft).every(d => selectedDevices.has(`${d.hostname}-${d.serial}`))} />
                  </th>
                  <th onClick={() => requestSort('hostname')}>Hostname {getSortIcon('hostname')}</th>
                  <th onClick={() => requestSort('organization')}>Organization {getSortIcon('organization')}</th>
                  <th onClick={() => requestSort('status')}>Status {getSortIcon('status')}</th>
                  <th>Integrations</th>
                </tr>
              </thead>
              <tbody>
                {drillDownDevices.map((dev, idx) => renderDeviceRow(dev, idx))}
              </tbody>
            </table>
            {drillDownDevices.length === 0 && (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                No devices match this filter.
              </div>
            )}
          </div>
        )}
      </main>

      {/* === QUICK ORG SETTINGS MODAL === */}
      {quickOrgSettingsModal && (
        <div className="modal-overlay" onClick={() => setQuickOrgSettingsModal(null)}>
          <div className="modal-content" style={{ maxWidth: '450px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{quickOrgSettingsModal.orgName} Settings</h2>
              <button className="modal-close" onClick={() => setQuickOrgSettingsModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={quickOrgSettingsForm.is_hidden}
                    onChange={e => setQuickOrgSettingsForm(p => ({ ...p, is_hidden: e.target.checked }))} />
                  <span>Hide this organization</span>
                </label>
                <h4 style={{ marginBottom: '0.5rem' }}>Required Integrations</h4>
                {['ncentral', 'connectwise', 'huntress', 'immybot', 'cloudradial', 'datto_rmm', 'microsoft'].map(key => {
                  const label = { ncentral: 'N-Central', connectwise: 'ConnectWise', huntress: 'Huntress', immybot: 'ImmyBot', cloudradial: 'CloudRadial', datto_rmm: 'Datto RMM', microsoft: 'Microsoft' }[key];
                  return (
                    <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.5rem', cursor: 'pointer' }}>
                      <input type="checkbox" checked={quickOrgSettingsForm[`${key}_enabled`]}
                        onChange={e => setQuickOrgSettingsForm(p => ({ ...p, [`${key}_enabled`]: e.target.checked }))} />
                      <span>{label}</span>
                    </label>
                  );
                })}
                <h4 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>Primary RMM</h4>
                <select value={quickOrgSettingsForm.primary_rmm}
                  onChange={e => setQuickOrgSettingsForm(p => ({ ...p, primary_rmm: e.target.value }))}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-card)', color: 'var(--text-primary)' }}>
                  <option value="global">Global Default (N-Central OR Datto)</option>
                  <option value="ncentral">N-Central Only</option>
                  <option value="datto">Datto RMM Only</option>
                  <option value="microsoft">Microsoft Intune Only</option>
                </select>
                <h4 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>macOS Settings</h4>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.5rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={quickOrgSettingsForm.mac_health_checks_enabled}
                    onChange={e => setQuickOrgSettingsForm(p => ({ ...p, mac_health_checks_enabled: e.target.checked }))} />
                  <span>Enable macOS health checks</span>
                </label>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
                <button className="btn-action" onClick={() => setQuickOrgSettingsModal(null)}>Cancel</button>
                <button className="btn-action btn-action-deploy" onClick={saveQuickOrgSettings}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* === CHANGELOG MODAL === */}
      {showChangelogModal && (
        <div className="modal-overlay" onClick={() => setShowChangelogModal(false)}>
          <div className="modal-content" style={{ maxWidth: '600px', maxHeight: '80vh' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Changelog</h2>
              <button className="modal-close" onClick={() => setShowChangelogModal(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ overflowY: 'auto', maxHeight: '60vh' }}>
              {/* Version 7.1.0 */}
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ color: '#3b82f6', marginBottom: '0.5rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem' }}>
                  v7.1.0 <span style={{ fontSize: '0.75rem', color: '#888', fontWeight: 'normal' }}>— February 22, 2026</span>
                </h3>
                <div style={{ marginLeft: '1rem' }}>
                  <h4 style={{ color: '#22c55e', marginBottom: '0.25rem', fontSize: '0.9rem' }}>&#x2728; Added</h4>
                  <ul style={{ marginLeft: '1rem', marginBottom: '0.75rem', fontSize: '0.85rem', lineHeight: '1.6' }}>
                    <li><strong>Restructured quick filters</strong> — All Issues, Security Issues, PSA Issues, Service Delivery, RMM Issues, Microsoft Issues, Duplicates</li>
                    <li><strong>Organization drill-down tabs</strong> — All Devices, Issues, Duplicates, Archived, Offboarded</li>
                    <li><strong>Redeploy action</strong> — Stale/erroring integrations show "Redeploy" button in Quick Actions</li>
                    <li><strong>Toast progress notifications</strong> — Bulk operations show determinate progress bar in toast tray</li>
                  </ul>
                  <h4 style={{ color: '#f59e0b', marginBottom: '0.25rem', fontSize: '0.9rem' }}>&#x1F504; Changed</h4>
                  <ul style={{ marginLeft: '1rem', marginBottom: '0.75rem', fontSize: '0.85rem', lineHeight: '1.6' }}>
                    <li><strong>Redeploy order</strong> — Delete now runs before deploy for correct agent removal then reinstall</li>
                    <li><strong>Offboarded/Archived</strong> — Moved from main quick filters to organization drill-down tabs</li>
                  </ul>
                  <h4 style={{ color: '#10b981', marginBottom: '0.25rem', fontSize: '0.9rem' }}>&#x1F527; Fixed</h4>
                  <ul style={{ marginLeft: '1rem', marginBottom: '0.75rem', fontSize: '0.85rem', lineHeight: '1.6' }}>
                    <li><strong>Bulk actions in drill-down views</strong> — Fixed "No valid devices" errors in org/integration drill-downs</li>
                    <li><strong>ConnectWise deactivate route</strong> — Restored missing /api/deactivate-connectwise endpoint</li>
                    <li><strong>Rate limiter crash</strong> — Fixed Flask-Limiter 4.x startup crash</li>
                    <li><strong>Module registration</strong> — Asset inventory Blueprint routes now actually registered</li>
                  </ul>
                </div>
              </div>

              {/* Version 7.0.0 */}
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ color: '#3b82f6', marginBottom: '0.5rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem' }}>
                  v7.0.0 "Nas" <span style={{ fontSize: '0.75rem', color: '#888', fontWeight: 'normal' }}>— February 21, 2026</span>
                </h3>
                <div style={{ marginLeft: '1rem' }}>
                  <h4 style={{ color: '#22c55e', marginBottom: '0.25rem', fontSize: '0.9rem' }}>&#x2728; Added</h4>
                  <ul style={{ marginLeft: '1rem', marginBottom: '0.75rem', fontSize: '0.85rem', lineHeight: '1.6' }}>
                    <li><strong>Modular Blueprint architecture</strong> — Extracted all routes from monolithic app.py into 13 Flask Blueprint modules under core/ and modules/</li>
                    <li><strong>Core auth package</strong> — Authentication decorators and middleware extracted to core/auth/</li>
                    <li><strong>Shared services</strong> — Event log, task queue, diagnostics, SSRF protection extracted to core/shared/</li>
                    <li><strong>App factory</strong> — Flask app factory with automatic module registration</li>
                    <li><strong>ImmyBot online status</strong> — Real-time 3-tier online detection via ephemeral agent API</li>
                    <li><strong>Duplicate cleanup from device panel</strong> — Inline duplicate detection and cleanup in slide-over panel</li>
                  </ul>
                  <h4 style={{ color: '#10b981', marginBottom: '0.25rem', fontSize: '0.9rem' }}>&#x1F527; Fixed</h4>
                  <ul style={{ marginLeft: '1rem', marginBottom: '0.75rem', fontSize: '0.85rem', lineHeight: '1.6' }}>
                    <li><strong>Security hardening</strong> — Auth bypass fixes, SSRF protection, hidden org filtering, credential cleanup</li>
                    <li><strong>Responsive stat cards</strong> — Fixed CSS breakpoints for proper tablet/mobile reflow</li>
                    <li><strong>Filter UX</strong> — Auto-scroll on filter click, panels collapse when filter active</li>
                    <li><strong>Microsoft sync tracking</strong> — Proper status tracking, timeout detection, event logging</li>
                    <li><strong>Offboarded device display</strong> — Muted indicators for correctly decommissioned devices</li>
                  </ul>
                  <h4 style={{ color: '#ef4444', marginBottom: '0.25rem', fontSize: '0.9rem' }}>&#x1F5D1; Removed</h4>
                  <ul style={{ marginLeft: '1rem', marginBottom: '0.75rem', fontSize: '0.85rem', lineHeight: '1.6' }}>
                    <li><strong>SQLite completely removed</strong> — PostgreSQL is now the only supported database</li>
                  </ul>
                </div>
              </div>

              {/* Version 6.0.0 */}
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ color: '#3b82f6', marginBottom: '0.5rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem' }}>
                  v6.0.0 "Eminem" <span style={{ fontSize: '0.75rem', color: '#888', fontWeight: 'normal' }}>— February 16, 2026</span>
                </h3>
                <div style={{ marginLeft: '1rem' }}>
                  <h4 style={{ color: '#22c55e', marginBottom: '0.25rem', fontSize: '0.9rem' }}>&#x2728; Added</h4>
                  <ul style={{ marginLeft: '1rem', marginBottom: '0.75rem', fontSize: '0.85rem', lineHeight: '1.6' }}>
                    <li><strong>Hub-and-spoke navigation</strong> — Fleet Command landing page replaces monolithic dashboard</li>
                    <li><strong>Integration drill-down views</strong> — Click any integration card to see filtered device list</li>
                    <li><strong>Organization drill-down views</strong> — Click any org to see health breakdown with inline actions</li>
                    <li><strong>3-click rule</strong> — All regular tasks achievable in 3 clicks or fewer</li>
                    <li><strong>Device slide-over panel</strong> — Device details open as side panel instead of modal</li>
                    <li><strong>Redesigned header</strong> — Compact header with breadcrumbs, search, and overflow menu</li>
                  </ul>
                  <h4 style={{ color: '#f59e0b', marginBottom: '0.25rem', fontSize: '0.9rem' }}>&#x1F504; Changed</h4>
                  <ul style={{ marginLeft: '1rem', marginBottom: '0.75rem', fontSize: '0.85rem', lineHeight: '1.6' }}>
                    <li><strong>Information architecture</strong> — Dual-panel layout (integrations + organizations) replaces flat filter-based view</li>
                    <li><strong>Device table</strong> — Simplified 5-column layout with integration health dots</li>
                    <li><strong>Navigation model</strong> — Context-driven surfaces show only relevant data</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* === TASK TRAY === */}
      <div className={`task-tray ${showTaskDrawer ? 'expanded' : ''}`}>
        <div className="task-tray-tab" onClick={() => setShowTaskDrawer(!showTaskDrawer)}>
          <span>
            &#x26A1; {taskQueue.in_progress.length > 0
              ? `${taskQueue.in_progress.length} task${taskQueue.in_progress.length !== 1 ? 's' : ''} running`
              : 'Task Queue'}
            {taskQueue.completed.length > 0 && ` \u2022 ${taskQueue.completed.length} completed`}
          </span>
          <span style={{ fontSize: '0.8rem' }}>{showTaskDrawer ? '\u25BC' : '\u25B2'}</span>
        </div>
        {showTaskDrawer && (
          <div className="task-tray-content">
            {taskQueue.in_progress.length === 0 && taskQueue.completed.length === 0 && scheduledTasks.jobs.length === 0 && (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>No active tasks</div>
            )}
            {taskQueue.in_progress.map(task => (
              <div key={task.id} className="task-item in-progress">
                <span className="task-icon" style={{ color: 'var(--blue)' }}>&#x25CF;</span>
                <div className="task-info">
                  <div className="task-desc">{task.description}</div>
                  {task.hostname && <div className="task-meta">Device: {task.hostname}</div>}
                </div>
                {task.source && <span className="task-source">{task.source}</span>}
              </div>
            ))}
            {taskQueue.completed.map(task => (
              <div key={task.id} className="task-item completed">
                <span className="task-icon" style={{ color: task.status === 'completed' ? 'var(--green)' : 'var(--red)' }}>
                  {task.status === 'completed' ? '\u2713' : '\u2717'}
                </span>
                <div className="task-info">
                  <div className="task-desc">{task.description}</div>
                  {task.hostname && <div className="task-meta">Device: {task.hostname}</div>}
                  {task.result && <div className="task-meta">{task.result}</div>}
                </div>
                {task.source && <span className="task-source">{task.source}</span>}
              </div>
            ))}
            {scheduledTasks.jobs.map(job => {
              const nextRun = job.next_run ? new Date(job.next_run) : null;
              const now = new Date();
              const diffMs = nextRun ? nextRun - now : 0;
              const diffMin = Math.round(diffMs / 60000);
              const diffHrs = Math.floor(diffMin / 60);
              const remainMin = diffMin % 60;
              let timeLabel = !nextRun ? 'Not scheduled' : diffMs < 0 ? 'Running now' : diffHrs > 0 ? `in ${diffHrs}h ${remainMin}m` : `in ${diffMin}m`;
              return (
                <div key={job.id} className="task-item" style={{ opacity: 0.7 }}>
                  <span className="task-icon" style={{ color: 'var(--purple)' }}>&#x23F0;</span>
                  <div className="task-info">
                    <div className="task-desc">{job.id === 'AutoSync' ? 'Full Sync' : job.id === 'VerificationCheck' ? 'Verification Check' : job.name}</div>
                    <div className="task-meta">Next: {timeLabel}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* === CONFIRMATION DRAWER === */}
      {confirmDrawer && (
        <>
          <div className="device-panel-overlay open" onClick={handleConfirmDrawerCancel} />
          <div className={`confirmation-drawer open`}>
            <div className="confirmation-drawer-header">
              <h3>{confirmDrawer.title}</h3>
            </div>
            <div className="confirmation-drawer-body">
              {confirmDrawer.message.split('\n').map((line, i) => (
                <div key={i}>{line || <br />}</div>
              ))}
            </div>
            <div className="confirmation-drawer-actions">
              <button className="btn-action" onClick={handleConfirmDrawerCancel}>Cancel</button>
              <button
                className={`btn-action ${confirmDrawer.variant === 'danger' ? 'btn-action-delete' : confirmDrawer.variant === 'deploy' ? 'btn-action-deploy' : ''}`}
                onClick={handleConfirmDrawerConfirm}>
                {confirmDrawer.confirmLabel}
              </button>
            </div>
          </div>
        </>
      )}

      {/* === COMMAND PALETTE === */}
      {showCommandPalette && (
        <div className="command-palette-overlay" onClick={() => setShowCommandPalette(false)}>
          <div className="command-palette" onClick={e => e.stopPropagation()}>
            <input
              ref={commandInputRef}
              className="command-palette-input"
              placeholder="Search devices, organizations, or actions..."
              value={commandQuery}
              onChange={e => { setCommandQuery(e.target.value); setCommandSelectedIdx(0); }}
              onKeyDown={e => {
                const results = commandPaletteResults;
                if (e.key === 'ArrowDown') { e.preventDefault(); setCommandSelectedIdx(i => Math.min(i + 1, results.length - 1)); }
                else if (e.key === 'ArrowUp') { e.preventDefault(); setCommandSelectedIdx(i => Math.max(i - 1, 0)); }
                else if (e.key === 'Enter' && results[commandSelectedIdx]) {
                  e.preventDefault();
                  const item = results[commandSelectedIdx];
                  if (item.type === 'device') { setSelectedDevice(item.data); fetchDeviceHistory(item.data.hostname); fetchDuplicateGroup(item.data); }
                  else if (item.type === 'org') { navigateTo('org', { type: 'org', name: item.data.name }); }
                  else if (item.action) { item.action(); }
                  setShowCommandPalette(false); setCommandQuery('');
                }
                else if (e.key === 'Escape') { setShowCommandPalette(false); setCommandQuery(''); }
              }}
              autoFocus
            />
            <div className="command-palette-results">
              {commandPaletteResults.length === 0 && commandQuery && (
                <div className="command-palette-empty">No results for "{commandQuery}"</div>
              )}
              {commandPaletteResults.map((item, idx) => (
                <div
                  key={item.id}
                  className={`command-palette-item ${idx === commandSelectedIdx ? 'selected' : ''}`}
                  onClick={() => {
                    if (item.type === 'device') { setSelectedDevice(item.data); fetchDeviceHistory(item.data.hostname); fetchDuplicateGroup(item.data); }
                    else if (item.type === 'org') { navigateTo('org', { type: 'org', name: item.data.name }); }
                    else if (item.action) { item.action(); }
                    setShowCommandPalette(false); setCommandQuery('');
                  }}
                  onMouseEnter={() => setCommandSelectedIdx(idx)}>
                  <span className="command-palette-icon">{item.icon}</span>
                  <div className="command-palette-item-body">
                    <div className="command-palette-item-title">{item.label}</div>
                    {item.subtitle && <div className="command-palette-item-subtitle">{item.subtitle}</div>}
                  </div>
                  {item.badge && <span className="command-palette-badge">{item.badge}</span>}
                </div>
              ))}
            </div>
            <div className="command-palette-footer">
              <span>{'\u21B5'} Open</span>
              <span>{'\u2191\u2193'} Navigate</span>
              <span>Esc Close</span>
            </div>
          </div>
        </div>
      )}

      {/* === KEYBOARD SHORTCUTS HELP === */}
      {showShortcutsHelp && (
        <div className="modal-overlay" onClick={() => setShowShortcutsHelp(false)}>
          <div className="modal-content" style={{ maxWidth: '420px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Keyboard Shortcuts</h2>
              <button className="modal-close" onClick={() => setShowShortcutsHelp(false)}>{'\u2715'}</button>
            </div>
            <div className="modal-body" style={{ fontSize: '0.85rem' }}>
              {[
                ['/', 'Open search / command palette'],
                ['Ctrl+K', 'Open command palette'],
                ['Esc', 'Close panel / Go back'],
                ['T', 'Toggle task tray'],
                ['R', 'Refresh data'],
                ['A', 'Select all visible devices'],
                ['?', 'Show this help'],
              ].map(([key, desc]) => (
                <div key={key} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', borderBottom: '1px solid var(--border-color)' }}>
                  <kbd style={{ background: 'var(--bg-secondary)', padding: '0.15rem 0.5rem', borderRadius: '4px', fontFamily: 'var(--font-mono)', fontSize: '0.8rem', fontWeight: 600 }}>{key}</kbd>
                  <span style={{ color: 'var(--text-muted)' }}>{desc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* === TOAST NOTIFICATIONS === */}
      <div className="toast-container">
        {toasts.slice(0, 5).map(toast => (
          <div key={toast.toastId}
            className={`toast ${toast.status === 'completed' ? 'toast-success' : toast.status === 'progress' ? 'toast-progress' : toast.status === 'info' ? 'toast-info' : toast.status === 'warning' ? 'toast-warning' : 'toast-failure'}${toast.exiting ? ' toast-exit' : ''}`}
            onClick={() => dismissToast(toast.toastId)} title="Click to dismiss">
            <span className="toast-icon">
              {toast.status === 'completed' ? '\u2713' : toast.status === 'progress' ? '\u21BB' : toast.status === 'info' ? '\u2139' : toast.status === 'warning' ? '\u26A0' : '\u2717'}
            </span>
            <div className="toast-body">
              <div className="toast-title">{toast.description}</div>
              {toast.hostname && <div className="toast-subtitle" style={{ whiteSpace: 'pre-line' }}>{toast.hostname}{toast.source ? ` \u2022 ${toast.source}` : ''}</div>}
              {!toast.hostname && toast.source && <div className="toast-subtitle" style={{ whiteSpace: 'pre-line' }}>{toast.source}</div>}
              {toast.isProgress && toast.progressTotal > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.4rem' }}>
                  <div className="toast-progress-bar" style={{ flex: 1 }}>
                    <div className="toast-progress-bar-fill toast-progress-determinate" style={{ width: `${(toast.progressCurrent / toast.progressTotal) * 100}%` }} />
                  </div>
                  <span style={{ fontSize: '0.75rem', color: '#888', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{toast.progressCurrent}/{toast.progressTotal}</span>
                </div>
              )}
              {toast.isProgress && !toast.progressTotal && (
                <div className="toast-progress-bar">
                  <div className="toast-progress-bar-fill" />
                </div>
              )}
            </div>
            {(toast.status === 'failed' || toast.isProgress) && (
              <span className="toast-dismiss" onClick={(e) => { e.stopPropagation(); dismissToast(toast.toastId); }}
                style={{ marginLeft: '0.5rem', cursor: 'pointer', opacity: 0.5, fontSize: '1.1rem' }}>{'\u2715'}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default App;
